import { TaskDateShiftError } from './task-date-shift-error'
import { TaskCopyError } from './task-copy-error'
import { getTaskCopyBatches, type TaskCopyContext } from '../../functions/get-task-copy-batches'
import { getAccountTimezone } from '@awork/_shared/functions/account-timezone'
import { MAX_BATCH_OPERATION_ITEMS } from '@awork/_shared/constants/batch-operation.constants'
import type { ChecklistItemTemplates } from '@awork/features/task/models/checklist-item-template'
import { ChecklistItemTemplate } from '@awork/features/task/models/checklist-item-template'
import type { Observable } from 'rxjs'
import {
  bufferCount,
  catchError,
  concatMap,
  debounceTime,
  defer,
  filter,
  finalize,
  forkJoin,
  from,
  map,
  merge,
  mergeMap,
  of,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  toArray
} from 'rxjs'
import { TaskStatus } from '@awork/features/task/models/task-status.model'
import { TrackingService } from '@awork/_shared/services/tracking-service/tracking.service'
import { TrackingEvent } from '@awork/_shared/services/tracking-service/events'
import type { Injector, Signal } from '@angular/core'
import { Injectable, signal, inject } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import type { HttpResponse } from '@angular/common/http'
import { HttpErrorResponse, HttpParams } from '@angular/common/http'
import type { ErrorResponse, MultiStatusResponse, QueryParams } from '@awork/_shared/services/api-client/ApiClient'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import { apiEndpoint } from '@awork/environments/environment'
import type { ValidTaskBaseTypes, ValidTaskEntityTypes } from '@awork/features/task/models/task.model'
import { Task } from '@awork/features/task/models/task.model'
import { TaskList } from '@awork/features/task/models/task-list.model'
import { TaskBundle } from '@awork/features/task/models/task-bundle.model'
import { TaskListTemplate } from '@awork/features/task/models/task-list-template.model'
import { TaskTemplate } from '@awork/features/task/models/task-template.model'
import { TaskBundleAssignment } from '@awork/features/task/models/task-bundle-assignment.model'
import { TaskViewList } from '@awork/features/task/models/task-view-list.model'
import type { User } from '@awork/features/user/models/user.model'
import type { FileUpload } from '@awork/_shared/models/file-upload.model'
import { UserSettingsService } from '@awork/_shared/services/user-settings-service/user-settings.service'
import { ChecklistItemStore } from '@awork/features/task/state/checklist-item.store'
import { ChecklistItemQuery } from '@awork/features/task/state/checklist-item.query'
import { TaskListStore } from '@awork/features/task/state/task-list.store'
import { TaskListQuery } from '@awork/features/task/state/task-list.query'
import { TaskStore } from '@awork/features/task/state/task.store'
import { TaskQuery } from '@awork/features/task/state/task.query'
import { TaskStatusStore } from '@awork/features/task/state/task-status.store'
import { TaskStatusQuery } from '@awork/features/task/state/task-status.query'
import { TaskViewStore } from '@awork/features/task/state/task-view.store'
import { TaskViewQuery } from '@awork/features/task/state/task-view.query'
import { WorkTypeStore } from '@awork/features/task/state/work-type.store'
import { WorkTypeQuery } from '@awork/features/task/state/work-type.query'
import { arrayAdd, arrayRemove, arrayUpsert } from '@awork/core/state/signal-store/helpers'
import { formatDateForFilter } from '@awork/_shared/functions/query-params-helper'
import { addDays, isAfter, startOfToday } from '@awork/_shared/functions/date-fns-wrappers'
import { SignalService } from '@awork/_shared/services/signal-service/signal.service'
import type { ChecklistItems } from '@awork/features/task/models/checklist-item'
import { ChecklistItem } from '@awork/features/task/models/checklist-item'
import { ProjectQuery } from '@awork/features/project/state/project.query'
import { ProjectStore } from '@awork/features/project/state/project.store'
import { SubscriptionQuery } from '@awork/_shared/state/subscription.query'
import { cloneDeep, cloneShallow, deepEqual, sortByAsc } from '@awork/_shared/functions/lodash'
import { PermissionsService } from '@awork/features/workspace/services/permission-service/permissions.service'
import { AccessLevels, Features } from '@awork/features/workspace/models/permissions.model'
import { trimRichText } from '@awork/_shared/functions/string-operations'
import type { TaskDependencyApi } from '@awork/features/task/models/task-dependency.model'
import { generateGUID } from '@awork/_shared/functions/guid-generator'
import type { EntityReorderResponse } from '@awork/_shared/functions/entity-reorder'
import type { MapTaskImport } from '@awork/features/task/models/map-task-import'
import type { TaskRecurrency } from '@awork/features/task/models/task-recurrency.model'
import { TimeTrackingStore } from '@awork/features/time-tracking/state/time-tracking.store'
import type { Tag, TagOwner, TagsSet } from '@awork/_shared/models/tag.model'
import { LAST_USED_LIMIT, MOST_USED_IN_PROJECTS_LIMIT } from '@awork/_shared/models/tag.model'
import { LogService } from '@awork/_shared/services/log-service/log.service'
import { Level } from '@awork/_shared/services/log-service/types'
import type { WebSocketSignal, WebSocketSignalOperation } from '@awork/_shared/services/signal-service/signal.model'
import type { ExcludedSignal } from '@awork/_shared/services/signal-service/signal.model'
import { UserQuery } from '@awork/features/user/state/user.query'
import { TaskListUI } from '@awork/features/task/models/task-list-ui.model'
import { TaskSettingsQuery } from '@awork/features/task/state/task-settings.query'
import { TaskSchedule } from '@awork/features/planner/models/task-schedule.model'
import type { ChangeTaskLists } from '@awork/features/task/models/change-task-list.model'
import type { TaskTemplateDependency } from '@awork/features/task/models/task-template-dependency.model'
import type { AutomationTask } from '@awork/features/project/models/automation-task.model'
import type { DateRange } from '@awork/_shared/functions/date-operations'
import { dateToISO, getRangeSegments, convertToTimezone, convertToUTC } from '@awork/_shared/functions/date-operations'
import { RecurringTask } from '@awork/features/planner/models/recurring-task-model'
import type { TaskAssignee } from '@awork/features/task/models/task-assignee.model'
import { getFlattenItemsByGrouping } from '@awork/_shared/functions/flatten-tasks'
import { uniqueBy } from '@awork/_shared/functions/array-operations'
import { SettingsStore } from '@awork/framework/state/settings.store'
import type {
  BatchAddTags,
  BatchAssignUsers,
  BatchDeleteTask,
  BatchMoveToList,
  BatchMoveToProject,
  BatchRequestBody,
  BatchSetDueDate,
  BatchSetPrio,
  BatchSetStatus,
  BatchSetWorkType,
  ChangeSubtaskItem,
  ChangeSubtaskTemplateItem,
  IDeleteTaskBody,
  IDeleteTaskStatusBody,
  PromoteSubtaskItem,
  TaskStatusMappingEntry,
  TaskBundleAssignmentResponse,
  TaskListMigrationOperation,
  TaskStatistics,
  TaskStatisticsBillability,
  ValidEntityTypes,
  ValidTaskTypes
} from '@awork/features/task/services/task-service/types'
import { BatchOperation, batchURLMap } from '@awork/features/task/services/task-service/types'
import { FeatureFlagQuery } from '@awork/_shared/state/feature-flag.query'
import { FeatureFlag } from '@awork/_shared/state/feature-flag.store'
import { areArraysEqual } from '@awork/_shared/functions/array-operations'
import type { SyncResponse, TrackedEntitiesRemovedEvent } from '@awork/core/state/signal-store/types'
import { getProjectTasksFilter } from '@awork/_shared/helpers/filter-expression-helper'
import { PlannedEffortUpdateLoadingService } from '../planned-effort-update-loading-service/planned-effort-update-loading.service'
import type { ProjectStatusType } from '@awork/features/project/models/project-status.model'
import { ApiCacheService } from '@awork/_shared/services/api-cache-service/api-cache.service'
import type { ApiCacheInvalidationOptions } from '@awork/_shared/services/api-cache-service/types'
import type { ApiCacheOptions } from '@awork/_shared/services/api-cache-service/types'

export enum PlannerMoveType {
  DateOnly = 'DateOnly',
  LaneOnly = 'LaneOnly',
  DateAndLane = 'DateAndLane',
  AssigneeChange = 'AssigneeChange',
  AssigneeAndLane = 'AssigneeAndLane',
  AssigneeAndDate = 'AssigneeAndDate'
}

const TASK_STATUS_SIGNAL_EXCLUSION_MS = 5000
const PLANNER_TASK_CHANGE_TTL_MS = 30000

interface TaskListPutForm {
  name: string
  plannedDuration?: number
  isHiddenForConnectUsers?: boolean
}

interface TaskListTemplatePut {
  name: string
  order: number
  isHiddenForConnectUsers?: boolean
}

export interface QueryParamsForAssigned extends QueryParams {
  assignedOnFrom?: string
  assignedOnTo?: string
}

export interface ParentTaskDerivedDatesPreview {
  earliestStartOn: Date
  latestDueOn: Date
}

export interface TaskPropertiesToUpdate {
  laneOrder?: number
  startOn?: Date
  dueOn?: Date
  assignees?: User[]
}

export interface UpdateTaskPropertiesOptions {
  timezone?: string
  wasTaskUnplanned?: boolean
  adjustSuccessors?: boolean
  batchTaskIds?: string[]
}

@Injectable({ providedIn: 'root' })
export class TaskService {
  /**
   * Returns the query params used for the default "new tasks" view.
   * @returns {QueryParamsForAssigned}
   */
  static getFetchParamsForNewTasks(): QueryParamsForAssigned {
    return {
      filterBy: "project/projectStatus/type ne 'closed'",
      assignedOnFrom: dateToISO(addDays(new Date(), -7)),
      assignedOnTo: dateToISO(addDays(new Date(), 1))
    }
  }

  /**
   * Returns the query params used for the default "my tasks" view.
   * @returns {QueryParamsForAssigned}
   */
  static getFetchParamsForMyTasks(): QueryParamsForAssigned {
    return {
      filterBy: "project/projectStatus/type ne 'closed'"
    }
  }

  private apiClient = inject(ApiClient)
  private trackingService = inject(TrackingService)
  private userSettingsService = inject(UserSettingsService)
  private taskStore = inject(TaskStore)
  private taskQuery = inject(TaskQuery)
  private taskListStore = inject(TaskListStore)
  private taskListQuery = inject(TaskListQuery)
  private workTypeStore = inject(WorkTypeStore)
  private workTypeQuery = inject(WorkTypeQuery)
  private taskStatusStore = inject(TaskStatusStore)
  private taskStatusQuery = inject(TaskStatusQuery)
  private userQuery = inject(UserQuery)
  private projectQuery = inject(ProjectQuery)
  private subscriptionQuery = inject(SubscriptionQuery)
  private signalService = inject(SignalService)
  private taskViewStore = inject(TaskViewStore)
  private taskViewQuery = inject(TaskViewQuery)
  private projectStore = inject(ProjectStore)
  private permissionsService = inject(PermissionsService)
  private timeTrackingStore = inject(TimeTrackingStore)
  private logService = inject(LogService)
  private taskSettingsQuery = inject(TaskSettingsQuery)
  private checklistItemStore = inject(ChecklistItemStore)
  private checklistItemQuery = inject(ChecklistItemQuery)
  private settingsStore = inject(SettingsStore)
  private featureFlagQuery = inject(FeatureFlagQuery)
  private plannedEffortUpdateLoadingService = inject(PlannedEffortUpdateLoadingService)
  private apiCacheService = inject(ApiCacheService)

  private readonly url: string
  private readonly bundleUrl: string
  private readonly viewTaskListUrl: string
  private readonly privateTaskViewListUrl: string
  private readonly meUrl: string
  private readonly tasksTemplatesUrl: string
  private readonly privateTaskUrl: string
  private readonly projectTaskUrl: string
  private readonly dependenciesUrl: string
  totalItems = 0

  // Used to know when a task has been created/updated/deleted
  taskCreated = new Subject<{ task: Task; regenerateLists: boolean }>()
  taskUpdated = new Subject<Task>()
  taskStatusUpdated = new Subject<{ task: Task; previousStatus?: TaskStatus }>()
  taskStatusDeleted = new Subject<TaskStatus>()
  taskDeleted = new Subject<string>()
  recurrencyChanged = new Subject<string>()
  taskChanged = merge(this.taskCreated, this.taskUpdated, this.taskDeleted)
  plannerTasksChanged = new Subject<string[]>()
  private readonly localPlannerTaskChanges = new Map<string, { version: number; changedAt: number }>()
  private plannerTaskChangeVersion = 0
  private readonly plannerTaskFetches = {
    activeCount: 0,
    sequence: 0,
    entities: new Map<string, { fetchId: number; task?: Task }>()
  }
  private importNavigationProjectId: string | null = null
  private importNavigationProjectName: string | null = null
  private navigateToProjectListAfterImport = false
  taskClosed = new Subject<Task>()
  attachmentUploaded = new Subject<FileUpload>()
  attachmentDeleted = new Subject<FileUpload>()
  bundleImported = new Subject<{ taskBundle: TaskBundle; entityType: ValidEntityTypes; entityId: string }>()
  projectMembersToBeChecked = new Subject<Task>()
  taskDependenciesUpdated = new Subject<{ task: Task; taskDependencies: Task[] }>()

  tagsByProjectCache = new Map<string, Tag[]>()
  privateTagsCache: Tag[]
  projectTasksTagsCache: Tag[]
  lastUsedByUserCache: Tag[]

  fetchCanceled = new Subject<boolean>()
  private pendingTaskViewTaskRequests = new Map<string, Observable<Task[]>>()

  taskQueue = new Subject<Observable<Task>>()
  taskOrderQueue = new Subject<Observable<EntityReorderResponse[]>>()
  checklistItemOrderQueue = new Subject<Observable<void>>()

  allowMultiUserAssignment: boolean

  private newListIdTracker = {
    optimisticId: null,
    taskListId: null
  }

  hoveredTask: {
    taskId: string
    infoHeight: number
  }
  readonly isTaskDragging = signal<boolean>(false)

  protected readonly isPEHierarchyFFEnabled = this.featureFlagQuery.queryFlag(FeatureFlag.plannedEffortHierarchy)

  // Flag to track if we should check for tasks with duplicated identifiers when navigating to a task detail page
  checkDuplicateIdentifier = false

  isCreatingTaskList = false

  constructor() {
    const taskStore = this.taskStore
    const taskListStore = this.taskListStore
    const taskStatusStore = this.taskStatusStore
    const checklistItemStore = this.checklistItemStore

    this.url = `${apiEndpoint}/tasks`
    this.meUrl = `${apiEndpoint}/me/tasks`
    this.bundleUrl = `${apiEndpoint}/taskbundles`
    this.viewTaskListUrl = `${apiEndpoint}/taskviews`
    this.privateTaskViewListUrl = `${apiEndpoint}/me/taskviews`
    this.tasksTemplatesUrl = `${apiEndpoint}/tasktemplates`
    this.privateTaskUrl = `${apiEndpoint}/me/privatetasks`
    this.projectTaskUrl = `${apiEndpoint}/projects/projecttasks`
    this.dependenciesUrl = `${apiEndpoint}/taskdependencies`

    // Subscribe to signal service
    this.signalService.getSignal('task', taskStore, this.taskDeleted)
    this.signalService.getSignal('taskstatus').subscribe(signal => this.handleTaskStatusSignal(signal, taskStatusStore))
    this.signalService.getSignal('tasklist', taskListStore)
    this.signalService.getSignal('checklistitem', checklistItemStore)

    // Telemetry to find the code paths that remove tasks a sync filter still tracks
    this.taskStore.trackedEntitiesRemoved.subscribe(event => this.logTrackedTasksRemoved(event))

    // Queues used to make api one call at a time
    this.taskQueue.pipe(mergeMap(call => call, 1)).subscribe()
    this.taskOrderQueue.pipe(mergeMap(call => call, 1)).subscribe()
    this.checklistItemOrderQueue.pipe(mergeMap(call => call, 1)).subscribe()

    this.allowMultiUserAssignment = this.taskSettingsQuery.getTaskSettings().allowMultiUserAssignment
  }

  /**
   * Handles websocket updates for task statuses.
   * @param {WebSocketSignal | void} signal
   * @param {TaskStatusStore} taskStatusStore
   */
  private handleTaskStatusSignal(signal: WebSocketSignal | void, taskStatusStore: TaskStatusStore): void {
    if (!signal) {
      return
    }

    const entity = signal.entity as TaskStatus | undefined
    const taskStatus = entity ?? this.taskStatusQuery.getEntity(signal.entityId)

    this.apiCacheService.invalidate(
      taskStatus ? this.getTaskStatusCacheInvalidation(taskStatus) : { entity: TaskStatus }
    )

    if (taskStatus?.workflowId) {
      return
    }

    if (signal.operationType === 'deleted') {
      taskStatusStore.remove(signal.entityId)
    } else if (entity) {
      taskStatusStore.syncEntity(entity)
    }
  }

  /**
   * Emits when a re-sync for tasks is necessary
   * This happens when the connection is re-established or when an entity needs has changed (optional)
   * @param {boolean} checkForFilteredEntities Necessary in case of using SyncFilter store and we need to force the resync on entity changes.
   * @returns {Observable<void>}
   */
  forceTasksReSync(checkForFilteredEntities?: boolean): Observable<void> {
    const signalSubjects$: Observable<WebSocketSignal | void>[] = [this.signalService.reconnected]

    if (checkForFilteredEntities) {
      signalSubjects$.push(this.signalService.getSignal('task'))
    }

    return merge(...signalSubjects$).pipe(
      debounceTime(200),
      filter(() => !this.taskQuery.queryLoading()),
      map(() => undefined)
    )
  }

  /**
   * Makes an API call to get the sync information of filtered project tasks and updates the store
   * @param {string} projectId
   * @param {boolean} filterOldDoneTasks
   * @returns {Observable<SyncResponse<Task>>}
   */
  syncProjectTasks(projectId: string, filterOldDoneTasks?: boolean): Observable<SyncResponse<Task>> {
    const filterBy = getProjectTasksFilter(projectId, filterOldDoneTasks)

    // A delta sync only returns changes since the cursor. Without a cursor, or when the store lost
    // tasks the filter still tracks, only a full fetch can restore the missing tasks.
    if (this.taskQuery.requiresFilteredFullSync(filterBy)) {
      this.logPartialProjectTaskCache(projectId, filterBy)

      return this.fetchProjectTasksWithFilter(projectId, filterBy).pipe(
        tap(tasks => {
          const syncQuerySignal = this.taskQuery.queryByProject(projectId, { filterOldDoneTasks })
          this.taskStore.sync(tasks, syncQuerySignal())
        }),
        map(tasks => {
          const fetchedTaskIds = tasks.map(task => task.id)
          const lastUpdatedOn = this.taskQuery.queryLastUpdatedOn(fetchedTaskIds)

          return { updated: tasks, deleted: [], lastSync: lastUpdatedOn() }
        }),
        tap(response => {
          this.taskStore.setSyncFilter(filterBy, response)
        })
      )
    }

    const syncFilter = this.taskQuery.querySyncFilter(filterBy)
    const queryParams = {
      since: syncFilter().lastSync.toISOString(),
      filterBy,
      supportsTaskReconciliation: true
    }

    const currentFilterEntityIds = [...syncFilter().entityIds]
    const issuedAtGeneration = this.taskStore.getResetGeneration()

    return this.apiClient.get<SyncResponse<Task>>(`${apiEndpoint}/sync/task`, { params: queryParams }).pipe(
      switchMap(response => {
        if (response.reconciliationRequired) {
          this.resetTaskStoreFromFullSync(filterBy, response)

          return of(response)
        }

        const trackedEntityIdSet = new Set(currentFilterEntityIds)
        const entitiesToRefresh = (response.unfilteredUpdated || []).filter(entityId =>
          trackedEntityIdSet.has(entityId)
        )
        const refreshEntities$ = entitiesToRefresh.length ? this.fetchTasksById(entitiesToRefresh) : of([])

        return refreshEntities$.pipe(
          tap(() => this.taskStore.apiSyncFiltered(filterBy, response, { issuedAtGeneration })),
          map(() => response)
        )
      }),
      tap(() => this.projectStore.update(projectId, { tasksLoaded: true }))
    )
  }

  /**
   * Logs a project task cache that tracks tasks missing from the store.
   * A first load has no cursor and is not logged.
   * @param {string} projectId
   * @param {string} filterBy
   */
  private logPartialProjectTaskCache(projectId: string, filterBy: string): void {
    const syncFilter = this.taskQuery.querySyncFilters().find(entry => entry.filterExpression === filterBy)

    if (!syncFilter?.lastSync) {
      return
    }

    const missingTaskIds = syncFilter.entityIds.filter(taskId => !this.taskQuery.getTask(taskId))
    const storedProjectTasks = this.taskQuery.queryByProject(projectId)

    this.logService.logMessage(Level.warn, 'Partial project task cache detected, forcing a full fetch', {
      projectId,
      filterBy,
      lastSync: syncFilter.lastSync,
      trackedCount: syncFilter.entityIds.length,
      missingCount: missingTaskIds.length,
      missingTaskIds: missingTaskIds.slice(0, 50),
      storedProjectTaskCount: storedProjectTasks().length
    })
  }

  /**
   * Logs which code path removed tasks that a sync filter still tracks.
   * Together with the partial cache log this shows how a partial cache formed.
   * @param {TrackedEntitiesRemovedEvent} event
   */
  private logTrackedTasksRemoved(event: TrackedEntitiesRemovedEvent): void {
    this.logService.logMessage(Level.debug, 'Tasks removed while tracked by a sync filter', {
      action: event.action,
      removedTaskIds: event.entityIds,
      syncFilters: event.syncFilters
    })
  }

  /**
   * Clears cached tasks after an access change and stores the authoritative current-filter snapshot.
   * @param {string} filterBy Filter represented by the full sync response.
   * @param {SyncResponse<Task>} response Authoritative tasks and cursor for the current filter.
   */
  private resetTaskStoreFromFullSync(filterBy: string, response: SyncResponse<Task>): void {
    this.taskStore.reset('task: reset after permission-triggered full sync')
    this.projectStore.resetTasksLoaded()
    // The reset is part of this flow and the response is the authoritative snapshot for the
    // filter, so the stale delta sync guard has to be skipped here
    this.taskStore.apiSyncFiltered(filterBy, response, { skipStaleCheck: true })
  }

  /**
   * Sets the active task in the store
   * @param {string} taskId
   */
  setActiveTask(taskId: string): void {
    this.taskStore.setActive(taskId)
  }

  // ########### TASKS ##############

  /**
   * Updates task properties in the planner including lane order, dates, and assignees
   * @param {string} referenceTaskId - The ID of the task to update
   * @param {TaskPropertiesToUpdate} [propertiesToUpdate={}] - Properties to update on the task
   * @param {UpdateTaskPropertiesOptions} options - Options for the update operation
   * @returns {Observable<MultiStatusResponse>} Response indicating success/failure of the operation
   */
  updateTaskProperties(
    referenceTaskId: string,
    propertiesToUpdate: TaskPropertiesToUpdate = {},
    options: UpdateTaskPropertiesOptions
  ): Observable<MultiStatusResponse> {
    const originalTask = this.taskQuery.getTask(referenceTaskId)

    if (!originalTask) {
      return of()
    }

    const { wasTaskUnplanned, adjustSuccessors, batchTaskIds } = options
    const actionType = this.getActionType(propertiesToUpdate)
    const originalTasks = new Map(
      (batchTaskIds?.length ? batchTaskIds : [referenceTaskId])
        .map(taskId => [taskId, this.taskQuery.getTask(taskId)] as const)
        .filter((entry): entry is readonly [string, Task] => !!entry[1])
    )

    this.taskStore.updateTask(referenceTaskId, propertiesToUpdate)

    const changeVersion = this.markLocalPlannerTaskChange(referenceTaskId)

    return this.apiClient
      .post<MultiStatusResponse>(`${this.url}/batch/plannerbatch`, {
        taskIds: batchTaskIds?.length ? batchTaskIds : [referenceTaskId],
        plannerData: {
          referenceTaskId: referenceTaskId,
          targetStartDate: propertiesToUpdate.startOn,
          targetEndDate: propertiesToUpdate.dueOn,
          targetLaneOrder: propertiesToUpdate.laneOrder,
          moveType: actionType,
          setDates: wasTaskUnplanned,
          timezone: options.timezone
        },
        userIds: propertiesToUpdate.assignees?.map(user => user.id) || [],
        adjustSuccessors
      })
      .pipe(
        tap(response => {
          if (!response.ok?.ids.includes(referenceTaskId)) {
            this.taskStore.updateTask(referenceTaskId, originalTask)
            this.clearLocalPlannerTaskChange(referenceTaskId, changeVersion)
          }

          const affectedUsers = new Set<string>()

          response.ok?.ids.forEach(taskId => {
            originalTasks.get(taskId)?.assignees?.forEach(user => affectedUsers.add(user.id))
            this.taskQuery.getTask(taskId)?.assignees?.forEach(user => affectedUsers.add(user.id))
          })

          if (response.ok?.ids.length) {
            propertiesToUpdate.assignees?.forEach(user => affectedUsers.add(user.id))
          }

          if (affectedUsers.size) {
            this.plannerTasksChanged.next([...affectedUsers])
          }
        }),
        catchError(error => {
          this.taskStore.updateTask(referenceTaskId, originalTask)
          this.clearLocalPlannerTaskChange(referenceTaskId, changeVersion)

          return of()
        })
      )
  }

  /** Tracks an optimistic planner change until a task fetch confirms it.
   * @param {string} taskId - The changed task ID.
   * @returns {number} Revision that identifies this local change.
   */
  private markLocalPlannerTaskChange(taskId: string): number {
    for (const [id, change] of this.localPlannerTaskChanges) {
      if (Date.now() - change.changedAt > PLANNER_TASK_CHANGE_TTL_MS) {
        this.localPlannerTaskChanges.delete(id)
      }
    }

    const version = ++this.plannerTaskChangeVersion

    this.localPlannerTaskChanges.set(taskId, { version, changedAt: Date.now() })

    return version
  }

  /** Clears a planner change only if no newer change replaced it.
   * @param {string} taskId - The changed task ID.
   * @param {number} version - The change version.
   */
  private clearLocalPlannerTaskChange(taskId: string, version: number): void {
    if (this.localPlannerTaskChanges.get(taskId)?.version === version) {
      this.localPlannerTaskChanges.delete(taskId)
    }
  }

  /** Checks whether fetched planner fields match the current task.
   * @param {Task} current - The task in the store.
   * @param {Task} fetched - The task returned by the API.
   * @returns {boolean} Whether the fetched planner fields confirm the stored task.
   */
  private plannerTaskFieldsMatch(current: Task, fetched: Task): boolean {
    return (
      current.startOn?.getTime() === fetched.startOn?.getTime() &&
      current.dueOn?.getTime() === fetched.dueOn?.getTime() &&
      current.laneOrder === fetched.laneOrder &&
      deepEqual(current.assignees?.map(user => user.id).sort(), fetched.assignees?.map(user => user.id).sort())
    )
  }

  /**
   * Determines the type of planner move action based on which properties are being updated
   * @param {TaskPropertiesToUpdate} [propertiesToUpdate={}]
   * @returns {PlannerMoveType}
   * @param {number} propertiesToUpdate.laneOrder
   * @param {Date} propertiesToUpdate.startOn
   * @param {Date} propertiesToUpdate.dueOn
   * @param {User[]} propertiesToUpdate.assignees
   */
  getActionType(propertiesToUpdate: TaskPropertiesToUpdate = {}): PlannerMoveType {
    const hasLaneChange = propertiesToUpdate.laneOrder >= 0
    const hasDateChange = !!propertiesToUpdate.startOn || !!propertiesToUpdate.dueOn
    const hasAssigneeChange = !!propertiesToUpdate.assignees

    if (hasAssigneeChange && hasLaneChange) {
      return PlannerMoveType.AssigneeAndLane
    }

    if (hasAssigneeChange && hasDateChange) {
      return PlannerMoveType.AssigneeAndDate
    }

    if (hasAssigneeChange) {
      return PlannerMoveType.AssigneeChange
    }

    if (hasDateChange && hasLaneChange) {
      return PlannerMoveType.DateAndLane
    }

    if (hasLaneChange) {
      return PlannerMoveType.LaneOnly
    }

    if (hasDateChange) {
      return PlannerMoveType.DateOnly
    }

    return PlannerMoveType.DateOnly
  }

  /**
   * Moves task dates once, including related tasks. Large selections are resolved before saving.
   * Partial failures provide a TaskDateShiftError with a retry for unsaved tasks.
   * @param {string[]} taskIds - Tasks whose dates move.
   * @param {number} daysOffset - Number of days by which dates move.
   * @param {boolean} adjustSuccessors - Whether successor dates also move.
   * @returns {Observable<void>} Completion of the batch date move.
   */
  batchMoveTasksDates(taskIds: string[], daysOffset: number, adjustSuccessors?: boolean): Observable<void> {
    // Normalize offsets from callers that still pass input strings.
    daysOffset = Number(daysOffset)

    const selectedIds = [...new Set(taskIds)]

    if (selectedIds.length <= MAX_BATCH_OPERATION_ITEMS) {
      return this.moveTaskDates(selectedIds, daysOffset, true, adjustSuccessors ?? false)
    }

    // Resolve the full graph before any writes. Each resolved task is then moved exactly once.
    return from(selectedIds).pipe(
      bufferCount(MAX_BATCH_OPERATION_ITEMS),
      concatMap(batchTaskIds =>
        this.apiClient.post<MultiStatusResponse>(`${this.url}/batch/previewadjustdates`, {
          daysOffset,
          taskIds: batchTaskIds,
          adjustSuccessors: adjustSuccessors ?? false,
          adjustSubtasks: true
        })
      ),
      tap(response => {
        if (response.notFound?.ids?.length || response.unauthorized?.ids?.length || response.badRequest?.ids?.length) {
          throw new HttpErrorResponse({ status: 400, error: response })
        }
      }),
      toArray(),
      switchMap(responses => {
        const affectedIds = [...new Set(responses.flatMap(response => response.ok?.ids ?? []))]

        return this.moveTaskDates(affectedIds, daysOffset, false, false)
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    )
  }

  /**
   * Moves dates and keeps completed tasks out of rollback and retry.
   * @param {string[]} taskIds
   * @param {number} daysOffset
   * @param {boolean} adjustSubtasks
   * @param {boolean} adjustSuccessors
   * @returns {Observable<void>}
   */
  private moveTaskDates(
    taskIds: string[],
    daysOffset: number,
    adjustSubtasks: boolean,
    adjustSuccessors: boolean
  ): Observable<void> {
    return defer(() => {
      const originalTasks = new Map<string, Task>()
      const changeVersions = new Map<string, number>()
      const remainingIds = new Set(taskIds)
      let hasSavedTasks = false

      taskIds.forEach(taskId => {
        const task = this.taskQuery.getTask(taskId)

        const hasDerivedDates = task?.deriveDatesFromSubtasks && !task.parentId && task.numberOfSubtasks > 0

        // Explicit batches let the server derive parent dates from the saved subtasks.
        if (task && (adjustSubtasks || !hasDerivedDates)) {
          originalTasks.set(taskId, task)
          this.taskStore.updateTask(taskId, {
            startOn: task.startOn ? addDays(task.startOn, daysOffset) : task.startOn,
            dueOn: task.dueOn ? addDays(task.dueOn, daysOffset) : task.dueOn
          })
          changeVersions.set(taskId, this.markLocalPlannerTaskChange(taskId))
        }
      })

      return from(taskIds).pipe(
        bufferCount(MAX_BATCH_OPERATION_ITEMS),
        concatMap(batchTaskIds =>
          this.apiClient.post<MultiStatusResponse>(`${this.url}/batch/adjustdates`, {
            daysOffset,
            taskIds: batchTaskIds,
            adjustDatesOfDoneTasks: true,
            adjustSuccessors,
            adjustSubtasks
          })
        ),
        tap(response => {
          const affectedUsers = new Set<string>()

          for (const taskId of response.ok?.ids ?? []) {
            originalTasks.get(taskId)?.assignees?.forEach(user => affectedUsers.add(user.id))
            hasSavedTasks = true
            remainingIds.delete(taskId)
            originalTasks.delete(taskId)
          }

          const failedIds = [
            ...(response.notFound?.ids ?? []),
            ...(response.unauthorized?.ids ?? []),
            ...(response.badRequest?.ids ?? [])
          ]

          for (const taskId of failedIds) {
            const originalTask = originalTasks.get(taskId)

            if (originalTask) {
              this.taskStore.updateTask(taskId, originalTask)
              this.clearLocalPlannerTaskChange(taskId, changeVersions.get(taskId)!)
            }
          }

          if (affectedUsers.size) {
            this.plannerTasksChanged.next([...affectedUsers])
          }

          if (failedIds.length) {
            throw new HttpErrorResponse({ status: 400, error: response })
          }
        }),
        toArray(),
        map(() => void 0),
        catchError(error => {
          originalTasks.forEach((originalTask, taskId) => {
            this.taskStore.updateTask(taskId, originalTask)
            this.clearLocalPlannerTaskChange(taskId, changeVersions.get(taskId)!)
          })

          if (hasSavedTasks) {
            return throwError(
              () => new TaskDateShiftError(error, this.moveTaskDates([...remainingIds], daysOffset, false, false))
            )
          }

          return throwError(() => error)
        })
      )
    }).pipe(
      // Finish store reconciliation if the move dialog closes during the request.
      shareReplay({ bufferSize: 1, refCount: false })
    )
  }

  /**
   * Add tags to group of tasks
   * @param {string[]} taskIds
   * @param {Tag[]} tags
   * @returns {Observable<void>}
   */
  batchAddTags(taskIds: string[], tags: Tag[]): Observable<void> {
    return this.batchEdit(taskIds, BatchOperation.addTags, { tags })
  }

  /**
   * Sends task edits in sequential batches and updates successful tasks after each response.
   * Submitted batches continue if the caller closes its view.
   * @param {string[]} taskIds
   * @param {BatchOperation} operationName
   * @param {BatchRequestBody} requestData
   * @returns {Observable<void>} Completes once all batches succeed; propagates a failed request.
   */
  batchEdit(taskIds: string[], operationName: BatchOperation, requestData: BatchRequestBody): Observable<void> {
    if (operationName === BatchOperation.copyTask) {
      const selectedIds = [...new Set(taskIds)]
      const batches$ =
        selectedIds.length > MAX_BATCH_OPERATION_ITEMS
          ? from(selectedIds).pipe(
              bufferCount(MAX_BATCH_OPERATION_ITEMS),
              concatMap(taskIds =>
                this.apiClient.post<TaskCopyContext[]>(`${this.url}/batch/copycontext`, { taskIds })
              ),
              toArray(),
              map(contexts => getTaskCopyBatches(selectedIds, contexts.flat()))
            )
          : of(selectedIds.length ? [selectedIds] : [])

      return batches$.pipe(
        switchMap(batches => this.copyTaskBatches(batches)),
        shareReplay({ bufferSize: 1, refCount: false })
      )
    }

    const batchURL = `${this.url}/batch/${batchURLMap[operationName]}`
    const independentTaskIds = this.getIndependentTaskIds(taskIds, operationName)

    return from(independentTaskIds).pipe(
      bufferCount(MAX_BATCH_OPERATION_ITEMS),
      concatMap(batchTaskIds =>
        this.apiClient
          .post<HttpResponse<void | MultiStatusResponse>>(
            batchURL,
            { taskIds: batchTaskIds, ...requestData },
            { observe: 'response' }
          )
          .pipe(
            tap(response => {
              const ok = (response.body as MultiStatusResponse)?.ok
              const updatedTaskIds =
                response.status === 207 ? batchTaskIds.filter(taskId => ok?.ids?.includes(taskId)) : batchTaskIds

              this.batchTaskStoreUpdate(updatedTaskIds, operationName, requestData)
            })
          )
      ),
      toArray(),
      map(() => void 0),
      shareReplay({ bufferSize: 1, refCount: false })
    )
  }

  /**
   * Copies planned batches and retains only unconfirmed IDs after a failure.
   * @param {string[][]} batches Complete related groups, each within the request limit.
   * @returns {Observable<void>}
   */
  private copyTaskBatches(batches: string[][]): Observable<void> {
    return defer(() => {
      const remainingIds = new Set(batches.flat())

      return from(batches).pipe(
        concatMap(taskIds =>
          this.apiClient
            .post<HttpResponse<MultiStatusResponse>>(`${this.url}/batch/copy`, { taskIds }, { observe: 'response' })
            .pipe(
              tap(response => {
                const copiedIds = response.status === 207 ? (response.body?.ok?.ids ?? []) : taskIds

                copiedIds.forEach(id => remainingIds.delete(id))

                if (taskIds.some(id => remainingIds.has(id))) {
                  throw new HttpErrorResponse({ status: 207, statusText: 'MultiStatus', error: response.body })
                }
              })
            )
        ),
        toArray(),
        map(() => void 0),
        catchError(error => {
          const remainingBatches = batches.map(ids => ids.filter(id => remainingIds.has(id))).filter(ids => ids.length)

          return throwError(() => new TaskCopyError(error, this.copyTaskBatches(remainingBatches)))
        })
      )
    }).pipe(shareReplay({ bufferSize: 1, refCount: false }))
  }

  /**
   * Removes selected descendants that a selected parent move already covers.
   * @param {string[]} taskIds
   * @param {BatchOperation} operationName
   * @returns {string[]}
   */
  private getIndependentTaskIds(taskIds: string[], operationName: BatchOperation): string[] {
    if (
      operationName !== BatchOperation.moveToList &&
      operationName !== BatchOperation.moveToProject &&
      operationName !== BatchOperation.deleteTask
    ) {
      return taskIds
    }

    const selectedIds = new Set(taskIds)
    const selectedTasks = new Map(this.taskQuery.getTasks(taskIds).map(task => [task.id, task]))

    return taskIds.filter(taskId => {
      let parentId = selectedTasks.get(taskId)?.parentId

      while (parentId) {
        if (selectedIds.has(parentId)) {
          return false
        }

        parentId = selectedTasks.get(parentId)?.parentId
      }

      return true
    })
  }

  /**
   * Applies a batch operation to the affected tasks in the local store.
   * @param {string[]} taskIds
   * @param {BatchOperation} operationName
   * @param {BatchRequestBody} requestData
   * @returns {void}
   */
  batchTaskStoreUpdate(taskIds: string[], operationName: BatchOperation, requestData: BatchRequestBody): void {
    switch (operationName) {
      case 'addTags':
        return this.batchAddTagsInStore(taskIds, requestData as BatchAddTags)
      case 'deleteTask':
        return this.batchDeleteTask(taskIds, requestData as BatchDeleteTask)
      case 'moveToList':
        return this.batchMoveToList(taskIds, requestData as BatchMoveToList)
      case 'moveToProject':
        return this.batchMoveToProject(taskIds, requestData as BatchMoveToProject)
      case 'setDueDate':
        return this.batchSetDueDate(taskIds, requestData as BatchSetDueDate)
      case 'setPrio':
        return this.batchSetPrio(taskIds, requestData as BatchSetPrio)
      case 'setStatus':
        return this.batchSetStatus(taskIds, requestData as BatchSetStatus)
      case 'unassignUsers':
        return this.batchUnassignUsers(taskIds)
      case 'assignUsers':
        return this.batchSetUser(taskIds, requestData as BatchAssignUsers)
      case 'setWorkType':
        return this.batchSetWorkType(taskIds, requestData as BatchSetWorkType)
    }
  }

  /**
   * sets tasks work type in the store
   * @param {string[]} taskIds
   * @param {BatchSetWorkType} requestData
   * @private
   */
  private batchSetWorkType(taskIds: string[], requestData: BatchSetWorkType): void {
    const { newTypeOfWorkId } = requestData

    this.taskStore.updateTask(taskIds, {
      typeOfWorkId: newTypeOfWorkId,
      typeOfWork: this.workTypeQuery.getEntity(newTypeOfWorkId)
    })
  }

  /**
   * sets tasks user in the store
   * @param {string[]} taskIds
   * @param {BatchAssignUsers} requestData
   * @private
   */
  private batchSetUser(taskIds: string[], requestData: BatchAssignUsers): void {
    const { userIds = [], removeOldAssignments } = requestData

    this.taskStore.update(taskIds, ({ storeVersion, assignees, projectId }) => {
      const newAssignees = userIds.map(userId => this.getAssignableUser(userId, projectId)).filter(user => !!user)

      return {
        assignees: removeOldAssignments ? newAssignees : uniqueBy(assignees.concat(newAssignees), 'id'),
        storeVersion: (storeVersion || 0) + 1
      }
    })
  }

  /**
   * Gets an assignable user from the user store or from the project members.
   * The user store holds only the users that the current user can see, for example only users of the same teams,
   * but the current user can assign all members of the project.
   * @param {string} userId
   * @param {string} [projectId]
   * @returns {User | undefined}
   */
  private getAssignableUser(userId: string, projectId?: string): User | undefined {
    const user = this.userQuery.getUser(userId)

    if (user) {
      return user
    }

    return this.projectQuery.getProject(projectId)?.members?.find(member => member.userId === userId)?.user
  }

  /**
   * sets tasks user in the store
   * @param {string[]} taskIds
   * @private
   */
  private batchUnassignUsers(taskIds: string[]): void {
    this.taskStore.update(taskIds, { assignees: [] })
  }

  /**
   * sets tasks status in the store
   * @param {string[]} taskIds
   * @param {BatchSetStatus} requestData
   * @private
   */
  private batchSetStatus(taskIds: string[], requestData: BatchSetStatus): void {
    const { newTaskStatusId } = requestData

    this.taskStore.updateTask(taskIds, {
      taskStatusId: newTaskStatusId,
      taskStatus: this.taskStatusQuery.getEntity(newTaskStatusId)
    })
  }

  /**
   * sets tasks prio in the store
   * @param {string[]} taskIds
   * @param {BatchSetPrio} requestData
   * @private
   */
  private batchSetPrio(taskIds: string[], requestData: BatchSetPrio): void {
    const { isPrio } = requestData

    this.taskStore.updateTask(taskIds, { isPrio })
  }

  /**
   * sets tasks dates in the the store
   * @param {string[]} taskIds
   * @param {BatchSetDueDate} requestData
   * @private
   */
  private batchSetDueDate(taskIds: string[], requestData: BatchSetDueDate): void {
    const { newDueDate } = requestData

    this.taskStore.updateTask(taskIds, { dueOn: newDueDate })
  }

  /**
   * moves tasks to project in the store
   * @param {string[]} taskIds
   * @param {BatchMoveToProject} requestData
   * @private
   */
  private batchMoveToProject(taskIds: string[], requestData: BatchMoveToProject): void {
    const { newProjectId, newTaskStatusId, newTaskListId, taskStatusMapping } = requestData

    this.batchMoveInOrder(taskIds, {
      newProjectId,
      newTaskStatusId,
      newTaskListId,
      removeFromCurrentList: true,
      taskStatusMapping
    })
  }
  /**
   * moves tasks to list in the store
   * @param {string[]} taskIds
   * @param {BatchMoveToList} requestData
   * @private
   */
  private batchMoveToList(taskIds: string[], requestData: BatchMoveToList): void {
    const { newTaskListId, removeFromCurrentList } = requestData

    this.batchMoveInOrder(taskIds, { newTaskListId, removeFromCurrentList })
  }

  /**
   * Moves tasks from projects and/or lists in batch in the store
   * @param {string[]} taskIds
   * @param {{ newProjectId?: string; newTaskStatusId?: string; newTaskListId?: string | null; removeFromCurrentList?: boolean; taskStatusMapping?: TaskStatusMappingEntry[]; }} requestData
   * @param requestData.newProjectId
   * @param requestData.newTaskStatusId
   * @param requestData.newTaskListId
   * @param requestData.removeFromCurrentList
   * @param requestData.taskStatusMapping
   */
  private batchMoveInOrder(
    taskIds: string[],
    requestData: {
      newProjectId?: string
      newTaskStatusId?: string
      newTaskListId?: string | null
      removeFromCurrentList?: boolean
      taskStatusMapping?: TaskStatusMappingEntry[]
    }
  ): void {
    const { newProjectId, newTaskListId, removeFromCurrentList } = requestData
    const taskStatusMappingByOldStatusId = this.getTaskStatusMappingByOldStatusId(requestData.taskStatusMapping)

    let targetTaskList: TaskList
    let lastTaskOrder: number
    let tasksNotInList: Task[]

    if (newTaskListId) {
      targetTaskList = this.taskListQuery.getEntity(newTaskListId)

      this.fetchProjectTasksByList(targetTaskList.projectId, newTaskListId).subscribe(currentTasksInList => {
        const newTaskListUI = new TaskListUI({ tasks: currentTasksInList, entity: targetTaskList })

        lastTaskOrder = newTaskListUI.getLastTaskOrder()

        // Select Many
        const storedTasks = this.taskQuery.getTasks(taskIds)
        const updatedTasks = taskIds.map(id => storedTasks.find(task => task.id === id))

        tasksNotInList = updatedTasks.filter(updatedTask => {
          const isWithoutList = !updatedTask.lists?.length
          const isNotInList = updatedTask.lists?.every(({ id }) => id !== newTaskListId)

          return isWithoutList || isNotInList
        })

        const tasksToUpdate = this.taskQuery.getTasks(taskIds)

        tasksToUpdate.forEach(storedTask => {
          const indexInArray = tasksNotInList.findIndex(task => task.id === storedTask.id)
          const addedList = new TaskList({ ...targetTaskList })

          addedList.orderOfTask = lastTaskOrder + indexInArray

          let lists = storedTask.lists || []

          if (indexInArray > -1 && addedList) {
            if (removeFromCurrentList) {
              lists = [addedList]
            } else {
              lists.push(addedList)
            }
          }

          const baseType = newProjectId || storedTask.projectId ? 'projecttask' : 'private'
          const projectProps = this.getBatchMoveProjectProps(storedTask, requestData, taskStatusMappingByOldStatusId)

          this.syncSubtasksByParents(
            new Task({ ...storedTask, lists, baseType, ...projectProps }),
            requestData,
            taskStatusMappingByOldStatusId
          )

          this.taskStore.update(storedTask.id, {
            lists,
            storeVersion: (storedTask.storeVersion || 0) + 1,
            baseType,
            ...projectProps
          })
        })
      })
    } else if (newTaskListId === null) {
      const tasksToUpdate = this.taskQuery.getTasks(taskIds)

      tasksToUpdate.forEach(storedTask => {
        const baseType = newProjectId || storedTask.projectId ? 'projecttask' : 'private'
        const projectProps = this.getBatchMoveProjectProps(storedTask, requestData, taskStatusMappingByOldStatusId)

        this.syncSubtasksByParents(
          new Task({ ...storedTask, lists: [], baseType, ...projectProps }),
          requestData,
          taskStatusMappingByOldStatusId
        )

        this.taskStore.update(storedTask.id, {
          lists: [],
          storeVersion: (storedTask.storeVersion || 0) + 1,
          baseType,
          ...projectProps
        })
      })
    } else {
      const tasksToUpdate = this.taskQuery.getTasks(taskIds)

      tasksToUpdate.forEach(storedTask => {
        const baseType = newProjectId ? 'projecttask' : 'private'
        const projectProps = this.getBatchMoveProjectProps(storedTask, requestData, taskStatusMappingByOldStatusId)

        this.syncSubtasksByParents(
          new Task({ ...storedTask, baseType, ...projectProps }),
          requestData,
          taskStatusMappingByOldStatusId
        )

        this.taskStore.update(storedTask.id, {
          baseType,
          ...projectProps
        })
      })
    }
  }

  /**
   * Returns the project and status properties for a batch move-to-project update.
   * @param {Task} task
   * @param {{ newProjectId?: string; newTaskStatusId?: string; taskStatusMapping?: TaskStatusMappingEntry[]; }} requestData
   * @param requestData.newProjectId
   * @param {Map<string, string>} taskStatusMappingByOldStatusId
   * @param requestData.newTaskStatusId
   * @param requestData.taskStatusMapping
   * @returns {Partial<Task>}
   */
  private getBatchMoveProjectProps(
    task: Task,
    requestData: {
      newProjectId?: string
      newTaskStatusId?: string
      taskStatusMapping?: TaskStatusMappingEntry[]
    },
    taskStatusMappingByOldStatusId?: Map<string, string>
  ): Partial<Task> {
    const { newProjectId, newTaskStatusId } = requestData

    if (!newProjectId) {
      return {}
    }

    const mappedStatusId = taskStatusMappingByOldStatusId?.get(task.taskStatusId) ?? newTaskStatusId

    if (!mappedStatusId) {
      return {}
    }

    return {
      project: this.projectQuery.getProject(newProjectId),
      taskStatus: this.taskStatusQuery.getEntity(mappedStatusId),
      projectId: newProjectId,
      taskStatusId: mappedStatusId
    }
  }

  /**
   * Returns a lookup for old-to-new task status mappings.
   * @param {TaskStatusMappingEntry[]} taskStatusMapping
   * @returns {Map<string, string> | undefined}
   */
  private getTaskStatusMappingByOldStatusId(
    taskStatusMapping?: TaskStatusMappingEntry[]
  ): Map<string, string> | undefined {
    if (!taskStatusMapping?.length) {
      return undefined
    }

    return new Map(taskStatusMapping.map(statusMapping => [statusMapping.oldStatusId, statusMapping.newStatusId]))
  }

  /**
   * Syncs a toplevel task's subtasks with its parent task
   * @param {Task} parentTask
   * @param {{ newProjectId?: string; newTaskStatusId?: string; taskStatusMapping?: TaskStatusMappingEntry[]; }} requestData
   * @param requestData.newProjectId
   * @param {Map<string, string>} taskStatusMappingByOldStatusId
   * @param requestData.newTaskStatusId
   * @param requestData.taskStatusMapping
   * @private
   */
  private syncSubtasksByParents(
    parentTask: Task,
    requestData: {
      newProjectId?: string
      newTaskStatusId?: string
      taskStatusMapping?: TaskStatusMappingEntry[]
    },
    taskStatusMappingByOldStatusId?: Map<string, string>
  ): void {
    const subtasks = this.taskQuery.getByParentId(parentTask.id)

    subtasks?.forEach(subtask => {
      const subtaskProjectProps = requestData.newProjectId
        ? this.getBatchMoveProjectProps(subtask, requestData, taskStatusMappingByOldStatusId)
        : {}
      const taskStatus = subtaskProjectProps.taskStatus ?? subtask.taskStatus
      const taskStatusId = subtaskProjectProps.taskStatusId ?? subtask.taskStatusId

      return this.taskStore.update(subtask.id, {
        project: parentTask.project,
        projectId: parentTask.projectId,
        taskStatus,
        taskStatusId,
        lists: parentTask.lists,
        resourceVersion: (subtask.storeVersion || 0) + 1
      })
    })
  }

  /**
   * deletes tasks in the store
   * @param {string[]} taskIds
   * @param {BatchDeleteTask} requestData
   * @private
   */
  private batchDeleteTask(taskIds: string[], requestData: BatchDeleteTask): void {
    if (requestData.deleteTimeTrackings) {
      taskIds.forEach(id => this.updateTaskTimeTrackings(id, requestData as IDeleteTaskBody))
    }

    // Remove all subtasks for given parent
    taskIds.forEach(id => this.taskQuery.getByParentId(id).forEach(task => this.taskStore.remove(task.id)))

    this.taskStore.remove(taskIds)
  }

  /**
   * adds tags to tasks in the store
   * @param {string[]} taskIds
   * @param {BatchAddTags} requestData
   * @private
   */
  private batchAddTagsInStore(taskIds: string[], requestData: BatchAddTags): void {
    const { tags } = requestData

    taskIds.map(taskId => {
      const storedTags = this.taskQuery.getTask(taskId)?.tags || []

      this.taskStore.updateTask(taskId, { tags: storedTags.concat(tags) })
    })
  }

  /**
   * Makes an API call to request the task information
   * @param {Injector} injector
   * @param {string} id The id of the task
   * @returns {Observable<Task>} The task information, is null if there is some error (ex.: Task not found)
   * @deprecated
   */
  getTask(injector: Injector, id: string): Observable<Task> {
    let fetched = false
    return toObservable(this.taskQuery.queryTask(id), { injector }).pipe(
      mergeMap(task => {
        // If the task has been found, return it and make an API call to fetch it fresh (just once)
        if (task) {
          if (!fetched) {
            fetched = true
            this.fetchTask(id).pipe(takeUntil(this.fetchCanceled)).subscribe()
          }
          // else, fetch it from the API (just once)
        } else if (!fetched) {
          fetched = true

          return this.fetchTask(id)
        }

        return of(task)
      })
    )
  }

  /**
   * Makes an API call to request the task information
   * @param {string} taskId
   * @param {boolean} [redirect=true]
   * @param {boolean} [skipStoreSync=false]
   * @returns {Observable<Task>}
   */
  fetchTask(taskId: string, redirect = true, skipStoreSync = false): Observable<Task> {
    return this.apiClient.get<Task>(`${this.url}/${taskId}`).pipe(
      map(task => {
        task = Task.mapTask(task)

        // prevent to be stuck with a old version if there is a problem with the resource version
        // by skipping the resource version check when updating the store
        if (!skipStoreSync) {
          this.taskStore.syncEntity(task)
        }

        return task
      }),
      catchError(error => {
        if (error.status === 401) {
          const task = this.taskQuery.getEntity(taskId)

          if (
            task?.project?.isExternal ||
            !this.permissionsService.isAllowed(Features.ProjectPlanning, AccessLevels.Read, redirect)
          ) {
            try {
              this.taskStore.remove(taskId)
            } catch (_) {}
          }
        }

        return throwError(error)
      })
    )
  }

  /**
   * Restores confirmed assignees and their efforts after a rejected detail save.
   * Keeps newer local edits and does not restore a removed task or a previous session.
   * @param {string} taskId
   * @returns {Observable<Task>}
   */
  restoreTaskAssignees(taskId: string): Observable<Task> {
    return defer(() => {
      const assignees = cloneDeep(this.taskQuery.getTask(taskId)?.assignees)
      const generation = this.taskStore.getResetGeneration()

      // A failed mutation can leave the server version unchanged, so normal sync cannot undo it.
      return this.fetchTask(taskId, false, true).pipe(
        tap(task => {
          const currentTask = this.taskQuery.getTask(taskId)

          if (
            generation === this.taskStore.getResetGeneration() &&
            currentTask &&
            currentTask.resourceVersion <= task.resourceVersion &&
            deepEqual(currentTask.assignees, assignees)
          ) {
            this.taskStore.updateTask(taskId, { assignees: task.assignees })
          }
        })
      )
    })
  }

  /**
   * Fetches a single task by its task key.
   * @param {string} taskIdentifier
   * @param {boolean} [skipStoreSync=false]
   * @returns {Observable<Task>}
   */
  fetchTaskByKey(taskIdentifier: string, skipStoreSync = false): Observable<Task> {
    return this.apiClient.get<Task>(`${this.url}/key/${encodeURIComponent(taskIdentifier)}`).pipe(
      map(task => {
        task = Task.mapTask(task)

        if (!skipStoreSync) {
          this.taskStore.syncEntity(task)
        }

        return task
      })
    )
  }

  /**
   * Fetches all tasks based on the query params
   * @param {QueryParams} queryParams
   * @returns {Observable<Task[]>}
   */
  fetchAllTasks(queryParams: QueryParams): Observable<Task[]> {
    const params: HttpParams = ApiClient.getQueryParams(queryParams)

    return this.apiClient.get<Task[]>(`${apiEndpoint}/me/allavailabletasks`, { params })
  }

  /**
   * Resolves a task Id by its taskIdentifier
   * @param {string} taskIdentifier
   * @param {boolean} [getExternal=false]
   * @returns {Observable<string | null>}
   */
  fetchTaskIdByIdentifier(taskIdentifier: string, getExternal = false): Observable<string | null> {
    const queryParams: QueryParams = {
      filterBy: `taskIdentifier eq '${taskIdentifier.toUpperCase()}'`,
      select: 'Id,createdOn'
    }

    return this.fetchAllTasks(queryParams).pipe(
      map(tasks => this.getTaskByExternalFlagOrFallback(tasks, getExternal)?.id ?? null)
    )
  }

  /**
   * Fetches a task by its taskIdentifier
   * @param {string} taskIdentifier
   * @param {boolean} [getExternal=false]
   * @returns {Observable<Task | null>}
   */
  fetchTaskByIdentifier(taskIdentifier: string, getExternal = false): Observable<Task | null> {
    return this.fetchAllTasksByIdentifier(taskIdentifier).pipe(
      map(tasks => this.getTaskByExternalFlagOrFallback(tasks, getExternal))
    )
  }

  /**
   * Returns the task matching the requested external flag.
   * Falls back to an external task when an internal one is requested but unavailable.
   * This is needed for key-based task URLs opened outside an explicit /external route.
   * @param {Task[] | null | undefined} tasks
   * @param {boolean} getExternal
   * @returns {Task | null}
   */
  private getTaskByExternalFlagOrFallback(tasks: Task[] | null | undefined, getExternal: boolean): Task | null {
    if (!tasks?.length) {
      return null
    }

    const taskWithRequestedExternalFlag = tasks.find(task => task.isExternal === getExternal)

    if (taskWithRequestedExternalFlag) {
      return taskWithRequestedExternalFlag
    }

    if (!getExternal) {
      return tasks.find(task => task.isExternal) ?? null
    }

    return null
  }

  /**
   * Fetches all tasks with the given taskIdentifier
   * Used to detect duplicate identifiers from connected projects
   * @param {string} taskIdentifier
   * @returns {Observable<Task[]>}
   */
  fetchAllTasksByIdentifier(taskIdentifier: string): Observable<Task[]> {
    const queryParams: QueryParams = {
      filterBy: `taskIdentifier eq '${taskIdentifier.toUpperCase()}'`
    }

    return this.fetchAllTasks(queryParams).pipe(map(tasks => tasks.map(task => Task.mapTask(task))))
  }

  /**
   * Fetches all subtasks of task by parentId
   * @param {string} parentId
   * @returns {Observable<Task[]>}
   */
  fetchTasksByParentId(parentId: string): Observable<Task[]> {
    const queryParams = {
      filterBy: `parentId eq guid'${parentId}'`,
      pageSize: 5000
    }

    return this.fetchAllTasks(queryParams).pipe(
      map(tasks => {
        tasks = tasks.map(task => Task.mapTask(task))

        const existingTasks = this.taskQuery.queryByParentId(parentId)

        this.taskStore.sync(tasks, existingTasks())

        return tasks
      })
    )
  }

  /**
   * Fetch my private tasks and set the store
   * @param {boolean} [syncStore=true]
   * @returns {Observable<Task[]>}
   */
  fetchMyPrivateTasks(syncStore = true): Observable<Task[]> {
    const params = ApiClient.getQueryParams({ pageSize: 5000 })

    return this.apiClient.getAll<Task[]>(this.privateTaskUrl, { params }).pipe(
      map(fetchedTasks => fetchedTasks.map(task => Task.mapTask(task))),
      switchMap(tasks => {
        const taskIds = new Set(tasks.map(task => task.id))
        const orphanSubtasks = tasks.filter(task => task.parentId && !taskIds.has(task.parentId))

        return this.fetchParentTasks(orphanSubtasks).pipe(
          map(parentTasks => uniqueBy([...tasks, ...parentTasks], 'id'))
        )
      }),
      switchMap(tasks => this.fetchMissingSubtasks(tasks, false)),
      tap(tasks => {
        if (syncStore) {
          const privateTasks = this.taskQuery.queryPrivateTasks()

          this.taskStore.sync(tasks, privateTasks())
        }
      })
    )
  }

  /**
   * Fetches all tasks that have been asisgned to current use during a period of time
   * @returns {Observable<Task[]>}
   */
  fetchMyAssignedTasksByDateRange(): Observable<Task[]> {
    // TODO: change implementation once is ready in the backend
    return this.apiClient.get<Task[]>(this.privateTaskUrl).pipe(
      map(tasks => {
        const mappedTasks = tasks.map(task => Task.mapTask(task))

        const privateTasks = this.taskQuery.queryPrivateTasks()

        this.taskStore.sync(tasks, privateTasks())

        return mappedTasks
      })
    )
  }

  /**
   * Fetches tasks by ids
   * @param {string[]} taskIds
   * @returns {Observable<Task[]>}
   */
  fetchTasksById(taskIds: string[]): Observable<Task[]> {
    const queryParamsFn = (taskIds: string[]) => ({ filterBy: taskIds.map(id => `id eq guid'${id}'`).join(' or ') })
    const callFn = (queryParams: QueryParams) => this.fetchAllTasks(queryParams)

    return this.apiClient.getBatch(taskIds, queryParamsFn, callFn).pipe(
      map(tasks => {
        tasks = tasks.map(task => Task.mapTask(task))

        const existingTasks = this.taskQuery.queryMany(taskIds)

        this.taskStore.sync(tasks, existingTasks())

        return tasks
      })
    )
  }

  /**
   * Fetches project tasks that have attachments through the project task endpoint so project task permissions are applied.
   * @param {string} projectId
   * @returns {Observable<Task[]>}
   */
  fetchProjectTasksWithAttachments(projectId: string): Observable<Task[]> {
    return this.fetchProjectTasksWithFilter(projectId, 'hasAttachment eq true')
  }

  /**
   * Makes an API call to request all assigned tasks and private tasks assigned to someone else
   * @param {boolean} [getAssignedToOthers=true]
   * @param {boolean} [filterOld=true] True to filter closed tasks older than 30 days
   * @param {QueryParams} queryParams
   * @param {boolean} syncToStore
   * @param {boolean} [includeSubtasks=false]
   * @returns {Observable<Task[]>}
   */
  fetchAssignedTasks(
    getAssignedToOthers = true,
    filterOld = true,
    queryParams?: QueryParams,
    syncToStore?: boolean,
    includeSubtasks = false
  ): Observable<Task[]> {
    let syncStore = false

    // force flag, to sync with store
    if (syncToStore) {
      syncStore = true
    }

    if (!queryParams) {
      queryParams = {}

      // Sync store only if the query params are not set
      // Ex.: If a filter is set, the response cannot be used to sync the store
      syncStore = true
    }

    if (!queryParams.pageSize && filterOld) {
      queryParams.pageSize = 5000
    }

    if (!queryParams.filterBy) {
      // We don't want all done tasks of the past, only of the last 30 days
      const date = formatDateForFilter(addDays(new Date(), -30))

      queryParams.filterBy = `taskStatus/type ne 'done' or closedOn ge datetime'${date}'`
    }

    let params: HttpParams = ApiClient.getQueryParams(queryParams)

    const isPaidPlan = this.subscriptionQuery.isPaidPlan()

    // Get tasks from active projects (in progress) if current plan is paid, otherwise from all projects
    if (isPaidPlan) {
      params = params.append('inProgress', 'true')
    }

    return forkJoin([
      this.apiClient.getAll<Task[]>(`${apiEndpoint}/me/assignedtasks`, { params }),
      getAssignedToOthers ? this.apiClient.getAll<Task[]>(`${apiEndpoint}/me/otherprivatetasks`, { params }) : of([])
    ]).pipe(
      map(responses => {
        const tasks: Task[] = [...responses[0], ...responses[1]].map(task => Task.mapTask(task))

        if (syncStore) {
          const assignedTasks = this.taskQuery.queryAssignedTasks({
            assignedToOthers: getAssignedToOthers,
            activeProjects: isPaidPlan,
            doneTasksSinceYesterday: false,
            includeOldDoneTasks: !filterOld
          })

          this.taskStore.sync(tasks, assignedTasks(), {
            resetFunction: () => this.taskStore.resetCheckedTasks(this.taskQuery.getCheckedTasks())
          })
        }

        return tasks
      }),
      switchMap(tasks => {
        if (includeSubtasks) {
          return this.fetchMissingSubtasks(tasks)
        }

        return of(tasks)
      })
    )
  }

  /**
   * Fetched the tasks that belong to a project and a list
   * @param {string} projectId
   * @param listId
   * @returns {Observable<Task[]>}
   */
  fetchProjectTasksByList(projectId: string, listId): Observable<Task[]> {
    const queryParams = {
      filterBy: `lists/any(l: l/id eq guid'${listId}')`
    }

    return this.fetchProjectTasks(projectId, false, queryParams, false)
  }

  /**
   * Makes an API call to request all tasks of a project
   * @param {string} projectId
   * @param {boolean} [filterOldDoneTasks=true] True to filter closed tasks older tan 30 days
   * @param {QueryParams} queryParams
   * @param {boolean} [forceStore=false]
   * @returns {Observable<Task[]>}
   */
  fetchProjectTasks(
    projectId: string,
    filterOldDoneTasks = true,
    queryParams?: QueryParams,
    forceStore = false
  ): Observable<Task[]> {
    let syncStore = false

    if (!queryParams) {
      queryParams = {}
      syncStore = true
    }

    if (forceStore && !syncStore) {
      syncStore = true
    }

    if (!queryParams.pageSize) {
      queryParams.pageSize = 5000
    }

    if (!queryParams.filterBy && !queryParams.count && filterOldDoneTasks) {
      // We don't want all done tasks of the past, only of the last 30 days,
      // can be dangerous because probably a filter for updated on would be safer..
      const date = formatDateForFilter(addDays(new Date(), -30))

      queryParams.filterBy = `taskStatus/type ne 'done' or closedOn ge datetime'${date}'`
    }

    const params: HttpParams = ApiClient.getQueryParams(queryParams)

    return this.apiClient
      .getAll<HttpResponse<Task[]>>(this.getTaskUrl('projectTasks', projectId), {
        params,
        observe: 'response'
      })
      .pipe(
        map(response => {
          const tasks: Task[] = response.body.map(task => Task.mapTask(task))

          // Set totalItems only if count=true, otherwise the number can be wrong (filters in place)
          if (queryParams.count) {
            this.totalItems = Number(response.headers.get('aw-totalitems'))
          }

          if (syncStore) {
            // Set the project's taskLoaded flag to true
            this.projectStore.update(projectId, { tasksLoaded: true })

            const syncQuerySignal = this.taskQuery.queryByProject(projectId, {
              filterOldDoneTasks,
              includeExternal: true
            })

            this.taskStore.sync(tasks, syncQuerySignal(), {
              resetFunction: () => this.taskStore.resetCheckedTasks(this.taskQuery.getCheckedTasks())
            })
          }

          return tasks
        })
      )
  }

  /**
   * Makes an API call to request all tasks of a project
   * We need the projectId because we depend on the projecttasks endpoint for now
   * @param {string} projectId
   * @param {string} filterBy
   * @returns {Observable<Task[]>}
   */
  fetchProjectTasksWithFilter(projectId: string, filterBy: string): Observable<Task[]> {
    const queryParams: QueryParams = {
      pageSize: 5000,
      filterBy
    }

    const params: HttpParams = ApiClient.getQueryParams(queryParams)

    return this.apiClient
      .getAll<HttpResponse<Task[]>>(`${apiEndpoint}/projects/${projectId}/projecttasks`, {
        params,
        observe: 'response'
      })
      .pipe(
        map(response => {
          const tasks: Task[] = response.body.map(task => Task.mapTask(task))

          this.projectStore.update(projectId, { tasksLoaded: true })

          const storedTasks = this.taskQuery.queryMany(tasks.map(task => task.id))

          this.taskStore.sync(tasks, storedTasks())

          return tasks
        })
      )
  }

  /**
   * Fetches the project tasks in a date range with filters
   * @param {{ projectId: string; dateRange: DateRange; includeExternal?: boolean; showDoneTasks?: boolean; showTasksOfDoneProjects?: boolean; showRecurringTasks?: boolean; }} options
   * @returns {Observable<Task[]>}
   * @param {string} options.projectId
   * @param {DateRange} options.dateRange
   * @param {boolean} options.includeExternal
   * @param {boolean} options.showDoneTasks
   * @param {boolean} options.showTasksOfDoneProjects
   * @param {boolean} options.showRecurringTasks
   */
  fetchProjectTasksInDateRange(options: {
    projectId: string
    dateRange: DateRange
    includeExternal?: boolean
    showDoneTasks?: boolean
    showTasksOfDoneProjects?: boolean
    showRecurringTasks?: boolean
  }): Observable<Task[]> {
    const {
      projectId,
      dateRange,
      showDoneTasks = true,
      showTasksOfDoneProjects = true,
      showRecurringTasks = true
    } = options

    const startOnFilter = `startOn le datetime'${formatDateForFilter(dateRange.endDate)}'`
    const endOnFilter = `dueOn ge datetime'${formatDateForFilter(dateRange.startDate)}'`
    const showDoneFilter = showDoneTasks ? '' : "taskStatus/type ne 'done'"
    const showTasksOfDoneProjectsFilter = showTasksOfDoneProjects ? '' : "project/projectStatus/type ne 'closed'"
    const showRecurringTasksFilter = showRecurringTasks ? '' : 'isRecurring eq false'

    const filterBy = [
      startOnFilter,
      endOnFilter,
      showDoneFilter,
      showTasksOfDoneProjectsFilter,
      showRecurringTasksFilter
    ]
      .filter(filter => !!filter)
      .join(' and ')

    const params = ApiClient.getQueryParams({ filterBy })

    return this.apiClient.getAll<Task[]>(this.getTaskUrl('projectTasks', projectId), { params }).pipe(
      map(tasks => {
        tasks = tasks.map(task => Task.mapTask(task))

        const existingTasks = this.taskQuery.queryProjectTasksInDateRange(options)

        this.taskStore.sync(tasks, existingTasks())

        return tasks
      })
    )
  }

  /**
   * Fetches the parent tasks of the given tasks
   * @param {Task[]} tasks
   * @returns {Observable<Task[]>}
   */
  fetchParentTasks(tasks: Task[]): Observable<Task[]> {
    const uniqueParentTaskIds = Array.from(
      new Set(tasks.map(task => task.parentId).filter((parentId): parentId is string => !!parentId))
    )

    if (!uniqueParentTaskIds.length) {
      return of([])
    }

    const queryParamsFn = (taskIds: string[]) => {
      const filterBy = taskIds.map(taskId => `id eq guid'${taskId}'`).join(' or ')
      return { filterBy }
    }

    const callFn = (queryParam: QueryParams) => this.fetchAvailableTasks(queryParam, false)

    return this.apiClient.getBatch(uniqueParentTaskIds, queryParamsFn, callFn, 'id')
  }

  /**
   * Makes an API call to request all the tasks that the user can see
   * @param {QueryParams} queryParams
   * @param {boolean} [syncStore=true]
   * @returns {Observable<Task[]>}
   */
  fetchAvailableTasks(queryParams?: QueryParams, syncStore = true): Observable<Task[]> {
    if (!queryParams) {
      queryParams = {}
    }

    if (!queryParams.pageSize) {
      queryParams.pageSize = 5000
    }

    const params: HttpParams = ApiClient.getQueryParams(queryParams)

    return this.apiClient
      .getAll<HttpResponse<Task[]>>(`${apiEndpoint}/me/allavailabletasks`, {
        params,
        observe: 'response' // To get the full response and get access to the headers (pagination info)
      })
      .pipe(
        map(response => {
          const tasks: Task[] = response.body.map(task => Task.mapTask(task))

          this.totalItems = Number(response.headers.get('aw-totalitems'))

          if (syncStore) {
            this.taskStore.add(tasks)
          }

          return tasks
        })
      )
  }

  /**
   * Fetches the users' tasks within the given date range
   * @param {{ userIds: string[]; startDate: Date; endDate: Date; showRecurringTasks?: boolean; showDoneTasks?: boolean; showTasksOfDoneProjects?: boolean; }} args - Selected users, date range, and task visibility filters.
   * @param args.userIds
   * @param args.startDate
   * @param args.endDate
   * @param args.showRecurringTasks
   * @param args.showDoneTasks
   * @param args.showTasksOfDoneProjects
   * @returns {Observable<Task[]>} Fetched tasks after reconciliation with the planner store.
   */
  fetchTasksInDateRange(args: {
    userIds: string[]
    startDate: Date
    endDate: Date
    showRecurringTasks?: boolean
    showDoneTasks?: boolean
    showTasksOfDoneProjects?: boolean
  }): Observable<Task[]> {
    const {
      userIds,
      startDate,
      endDate,
      showDoneTasks = false,
      showRecurringTasks = true,
      showTasksOfDoneProjects = true
    } = args
    const queryParamsFn = (ids: string[]) => {
      const userIdsFilter = ids
        ? '(' + ids.map(userId => `assignees/any(a: a/id eq guid'${userId}')`).join(' or ') + ')'
        : null

      const startOnFilter = startDate ? `startOn le datetime'${formatDateForFilter(endDate)}'` : null
      const endOnFilter = endDate ? `dueOn ge datetime'${formatDateForFilter(startDate)}'` : null
      const showDoneFilter = showDoneTasks ? '' : "taskStatus/type ne 'done'"
      const showTasksOfDoneProjectsFilter = showTasksOfDoneProjects ? '' : "project/projectStatus/type ne 'closed'"
      const showRecurringTasksFilter = showRecurringTasks ? '' : 'isRecurring eq false'

      const queryParams: QueryParams = {
        filterBy: [
          userIdsFilter,
          startOnFilter,
          endOnFilter,
          showDoneFilter,
          showTasksOfDoneProjectsFilter,
          showRecurringTasksFilter
        ]
          .filter(filter => !!filter)
          .join(' and ')
      }

      return queryParams
    }

    const callFn = (queryParam: QueryParams) => this.fetchAvailableTasks(queryParam, false)

    const call$ = userIds?.length
      ? this.apiClient.getBatch(userIds, queryParamsFn, callFn, 'id')
      : callFn(queryParamsFn(null))

    return defer(() => {
      const fetchId = ++this.plannerTaskFetches.sequence

      this.plannerTaskFetches.activeCount++

      const tasksAtRequest = this.taskStore.entityMap()

      return call$.pipe(
        tap(tasks => {
          if (!Array.isArray(tasks)) {
            return
          }

          const plannerTasks = this.taskQuery.queryTasksByUserForPlannerOverview({
            userIds,
            startDate,
            endDate,
            showDoneTasks,
            showRecurringTasks,
            showTasksOfDoneProjects
          })
          const currentTasks = plannerTasks()
          const currentById = new Map(currentTasks.map(task => [task.id, task]))
          const fetchedById = new Map(tasks.map(task => [task.id, task]))
          const mergedById = new Map(fetchedById)
          const latestTasks = this.taskStore.entityMap()
          const affectedIds = new Set([...fetchedById.keys(), ...currentById.keys()])
          const acceptedReadIds = new Set(affectedIds)

          for (const taskId of affectedIds) {
            const latest = latestTasks[taskId]
            const fetched = fetchedById.get(taskId)
            const appliedRead = this.plannerTaskFetches.entities.get(taskId)
            const changedByRead = appliedRead !== undefined && appliedRead.task === latest
            const changedLocally = tasksAtRequest[taskId] !== latest && !changedByRead
            const newerReadChangedMembership = changedByRead && appliedRead.fetchId > fetchId && (!latest || !fetched)
            const newerStoredVersion = latest && fetched && latest.resourceVersion > fetched.resourceVersion

            if (changedLocally || newerReadChangedMembership || newerStoredVersion) {
              acceptedReadIds.delete(taskId)

              const current = currentById.get(taskId)

              if (current) {
                mergedById.set(taskId, current)
              } else {
                mergedById.delete(taskId)
              }
            }
          }

          for (const [taskId, change] of this.localPlannerTaskChanges) {
            if (Date.now() - change.changedAt > PLANNER_TASK_CHANGE_TTL_MS) {
              this.localPlannerTaskChanges.delete(taskId)
              continue
            }

            const current = latestTasks[taskId]
            const fetched = fetchedById.get(taskId)

            if (current && fetched && this.plannerTaskFieldsMatch(current, fetched)) {
              this.clearLocalPlannerTaskChange(taskId, change.version)
            } else if (currentById.has(taskId)) {
              acceptedReadIds.delete(taskId)
              mergedById.set(taskId, currentById.get(taskId))
            } else {
              acceptedReadIds.delete(taskId)
              mergedById.delete(taskId)
            }
          }

          this.taskStore.sync([...mergedById.values()], currentTasks)

          const acceptedTasks = this.taskStore.entityMap()

          // Confirmed membership also protects an unchanged task from an older empty response.
          acceptedReadIds.forEach(taskId => {
            const latestFetchId = Math.max(fetchId, this.plannerTaskFetches.entities.get(taskId)?.fetchId ?? fetchId)
            this.plannerTaskFetches.entities.set(taskId, { fetchId: latestFetchId, task: acceptedTasks[taskId] })
          })
        }),
        finalize(() => {
          this.plannerTaskFetches.activeCount--

          if (!this.plannerTaskFetches.activeCount) {
            this.plannerTaskFetches.entities.clear()
          }
        })
      )
    })
  }

  /**
   * Makes an API call to request all the private tasks assigned to others
   * @param {QueryParams} queryParams
   * @returns {Observable<Task[]>}
   */
  fetchAssignedToOthersTasks(queryParams?: QueryParams): Observable<Task[]> {
    const params: HttpParams = ApiClient.getQueryParams(queryParams)

    return this.apiClient
      .get<HttpResponse<Task[]>>(`${apiEndpoint}/me/otherprivatetasks`, {
        params,
        observe: 'response' // To get the full response and get access to the headers (pagination info)
      })
      .pipe(
        map(response => {
          const tasks = response.body.map(task => Task.mapTask(task))

          const assignedToOthers = this.taskQuery.queryPrivateTasksAssignedToOthers()

          this.taskStore.sync(tasks, assignedToOthers(), {
            resetFunction: () => this.taskStore.resetCheckedTasks(this.taskQuery.getCheckedTasks())
          })

          return tasks
        })
      )
  }

  /**
   * Makes an API call to return all the project tasks which the user can see
   * @param {QueryParams} queryParams
   * @param {boolean} [includeSubtasks=false] true to force the inclusion of any subtasks which is not part of the response
   * @returns {Observable<Task[]>} The task information
   */
  fetchMyProjectTasks(queryParams?: QueryParams, includeSubtasks = false): Observable<Task[]> {
    const params: HttpParams = ApiClient.getQueryParams(queryParams)
    const url = `${apiEndpoint}/me/projecttasks`

    return this.apiClient
      .get<HttpResponse<Task[]>>(url, {
        params,
        observe: 'response' // To get the full response and get access to the headers (pagination info)
      })
      .pipe(
        map(response => {
          const tasks: Task[] = response.body.map(task => Task.mapTask(task))

          this.totalItems = Number(response.headers.get('aw-totalitems'))

          // TODO: define sync policy
          this.taskStore.add(tasks)
          this.taskStore.setLoading(false)

          return tasks
        }),
        switchMap(tasks => {
          if (includeSubtasks) {
            return this.fetchMissingSubtasks(tasks)
          }

          return of(tasks)
        })
      )
  }

  /**
   * Makes an API call to return all the tasks which are assigned to the user
   * @param {QueryParamsForAssigned} queryParams
   * @param {boolean} [includeSubtasks=false] true to force the inclusion of any subtasks which are not part of the response
   * @returns {Observable<Task[]>} The task information
   */
  fetchMyAssignedTasks(queryParams?: QueryParamsForAssigned, includeSubtasks = false): Observable<Task[]> {
    queryParams = { ...queryParams }

    if (!queryParams.pageSize) {
      queryParams.pageSize = 5000
    }

    let params: HttpParams = ApiClient.getQueryParams(queryParams)

    if (queryParams.assignedOnFrom) {
      params = params.append('assignedOnFrom', queryParams.assignedOnFrom.toString())
    }

    if (queryParams.assignedOnTo) {
      params = params.append('assignedOnTo', queryParams.assignedOnTo.toString())
    }

    const url = `${apiEndpoint}/me/assignedtasks`

    return this.apiClient.getAll<Task[]>(url, { params }).pipe(
      map(fetchedTasks => {
        const tasks: Task[] = fetchedTasks.map(task => Task.mapTask(task))

        this.totalItems = tasks.length

        const cachedTasks = this.taskQuery.queryMany(tasks.map(task => task.id))

        this.taskStore.sync(tasks, cachedTasks())

        return tasks
      }),
      switchMap(tasks => {
        if (includeSubtasks) {
          return this.fetchMissingSubtasks(tasks)
        }

        return of(tasks)
      })
    )
  }

  /**
   * Makes an API call to return all the tasks which are assigned to the user
   * @param {string} userId
   * @param {QueryParams} queryParams
   * @param {Signal<Task[]>} syncQuery
   * @returns {Observable<Task[]>} The task information
   */
  fetchTasksForUser(userId: string, queryParams?: QueryParams, syncQuery?: Signal<Task[]>): Observable<Task[]> {
    const params: HttpParams = ApiClient.getQueryParams(queryParams)
    const url = `${apiEndpoint}/users/${userId}/assignedtasks`

    return this.apiClient
      .getAll<HttpResponse<Task[]>>(url, {
        params,
        observe: 'response' // To get the full response and get access to the headers (pagination info)
      })
      .pipe(
        map(response => {
          const tasks: Task[] = response.body.map(task => Task.mapTask(task))

          this.totalItems = Number(response.headers.get('aw-totalitems'))

          // eslint-disable-next-line @angular-eslint/no-uncalled-signals
          if (syncQuery) {
            this.taskStore.sync(tasks, syncQuery(), {
              resetFunction: () => this.taskStore.resetCheckedTasks(this.taskQuery.getCheckedTasks())
            })
          }

          this.taskStore.setLoading(false)

          return tasks
        })
      )
  }

  /**
   * Makes an API call to create a task.
   * Uses a queue to create one task after the other.
   * @param {Task} task
   * @param {boolean} [regenerateLists=true]
   * @param {{ id: string; order: number; }[]} [lists=null]
   * @returns {Observable<Task>}
   */
  sendTaskInQueue(task: Task, regenerateLists = true, lists: { id: string; order: number }[] = null): Observable<Task> {
    const taskCreated = new Subject<Task>()

    this.taskQueue.next(
      this.sendTask(task, regenerateLists, lists).pipe(
        tap(task => taskCreated.next(task)),
        catchError(error => {
          taskCreated.error(error)
          return of()
        })
      )
    )

    return taskCreated.asObservable().pipe(take(1))
  }

  /**
   * Makes an API call to create a task
   * @param {Task} task
   * @param {boolean} [regenerateLists=true]
   * @param {{ id: string; order: number; }[]} [lists=null]
   * @returns {Observable<Task>}
   */
  sendTask(task: Task, regenerateLists = true, lists: { id: string; order: number }[] = null): Observable<Task> {
    const taskPost = {
      typeOfWorkId: task.typeOfWorkId,
      taskStatusId: task.taskStatusId,
      name: task.name,
      description: trimRichText(task.description),
      isPrio: task.isPrio,
      plannedDuration: task.plannedDuration,
      startOn: task.startOn,
      dueOn: task.dueOn,
      entityId: task.entityId,
      baseType: task.baseType,
      laneOrder: task.laneOrder,
      order: task.order,
      lists,
      parentId: task.parentId,
      subtaskOrder: task.parentId ? task.subtaskOrder : null,
      primaryTaskListId: task.primaryTaskListId
    }

    const backupAssignees = task.assignees || []

    // save the last used work type
    this.userSettingsService.setLastUsedWorkTypeSetting(task.typeOfWorkId, 'task')

    let optimisticId: string
    let operationId: string

    task.isSaving = true

    // If task already has an id, is a random one for optimistic saving purposes
    if (task.id) {
      optimisticId = task.id
      task.trackById = optimisticId
      task.resourceVersion = 0
      task.storeVersion = 0
      operationId = this.taskStore.history.startOperation({ type: 'add', entities: [task] })
      this.taskStore.add(task)
    }

    const assigneesBackup = task.assignees
    const url: string = this.getTaskCreateUrl()

    return this.apiClient.post<Task>(url, taskPost, { generateTraceId: true }).pipe(
      map(createdTask => {
        createdTask.assignees = assigneesBackup
        Object.assign(task, createdTask)
        task = Task.mapTask(task)
        task.assignees = backupAssignees
        this.taskCreated.next({ task, regenerateLists })

        // If taskList was added optimistically, remove it and add the saved one
        task.isSaving = false

        if (optimisticId) {
          this.taskStore.remove(optimisticId)

          task.trackById = optimisticId
        }

        task.storeVersion = 1
        this.taskStore.add(task)

        if (task.parentId) {
          this.updateNumberOfSubtasksForParents([task], 1)
        }

        this.totalItems++

        if (operationId) {
          this.taskStore.history.endOperation(operationId)
        }

        return task
      }),
      catchError(error => {
        this.logService.logMessage(Level.error, 'Error in Task Creation', { error })

        if (operationId) {
          this.taskStore.history.undo(operationId)
        }

        return throwError(error)
      })
    )
  }

  /**
   * Makes an API call to create a task
   * @param {Task} task The task to be created
   * @param {boolean} [regenerateLists=true]
   * @returns {Observable<Task>} The task information
   */
  copyTask(task: Task, regenerateLists = true): Observable<Task> {
    // save the last used work type
    this.userSettingsService.setLastUsedWorkTypeSetting(task.typeOfWorkId, 'task')

    const url: string = this.getTaskCopyUrl(task.id)

    return this.apiClient.post<Task>(url, {}).pipe(
      map(createdTask => {
        const lists = cloneDeep(task.lists).map(list => {
          list.orderOfTask = 9999
          return list
        })

        const { id, startOn, dueOn, order } = createdTask

        Object.assign(createdTask, task)

        createdTask.id = id
        createdTask.startOn = startOn
        createdTask.dueOn = dueOn
        createdTask.order = order
        createdTask.lists = lists
        createdTask.trackedDuration = 0

        createdTask = Task.mapTask(createdTask)

        this.taskCreated.next({ task: createdTask, regenerateLists })
        this.taskStore.add(createdTask)
        this.totalItems++

        return createdTask
      })
    )
  }

  /**
   * Sets the newest list id
   * we use this to avoid regeneration of the list component when the new list has been saved
   * On creation of a new list we set the random id
   * When the list is blur we reset this value to null
   * @param {string} optimisticId
   * @param {string} taskListId
   */
  setNewListIdTracker(optimisticId?: string, taskListId?: string): void {
    if (optimisticId) {
      this.newListIdTracker.optimisticId = optimisticId
    }

    if (taskListId) {
      this.newListIdTracker.taskListId = taskListId
    }
  }

  /**
   * Resets the ListId tracker
   */
  resetNewListIdTracker(): void {
    this.newListIdTracker = {
      optimisticId: null,
      taskListId: null
    }
  }

  /**
   * Returns the object which contains the new list id optimistic id and id, to avoid
   * unnecessary component rerender
   * @returns {{ optimisticId: string; taskListId: string; }}
   */
  getNewListIdTracker(): { optimisticId: string; taskListId: string } {
    return this.newListIdTracker
  }

  /**
   * Update task in timeline and optionally its successors
   * As this uses batchMoveTasksDates it will also handle the move of subtasks when the parent task has deriveDatesFromSubtasks enabled
   * @param {Task} task
   * @param {number} daysDelta
   * @param {boolean} moveSuccessors
   * @returns {Observable<void>}
   */
  updateTaskWithDaysDelta(task: Task, daysDelta: number, moveSuccessors: boolean): Observable<void> {
    if (!daysDelta) {
      return of(null)
    }

    return this.batchMoveTasksDates([task?.id], daysDelta, moveSuccessors)
  }

  /**
   * Update task in timeline and optionally its successors
   * @param {Task} task
   * @param {number} daysDelta
   * @param {boolean} moveSuccessors
   * @param {string[]} [ignoreSuccessorsIds=[]]
   * @param timezone Timezone whose wall-clock time is preserved when moving successors.
   * @returns {Observable<Task[]>}
   * @deprecated - Use UpdateTaskWithDaysDelta instead
   */
  updateMultipleTasks(
    task: Task,
    daysDelta: number,
    moveSuccessors: boolean,
    ignoreSuccessorsIds: string[] = [],
    timezone = getAccountTimezone()
  ): Observable<Task[]> {
    const originalTask = this.taskQuery.getTask(task.id)
    let operationId: string
    let originalAssigneeIds: string[] = []

    if (originalTask) {
      operationId = this.taskStore.history.startOperation({ type: 'update', entities: [originalTask] })
      originalAssigneeIds = originalTask.assignees?.map(assignee => assignee.id)
    }

    this.taskStore.updateTask(task.id, task)

    return forkJoin([this.updateTask(task), this.manageAssignees(task, originalAssigneeIds)]).pipe(
      tap(() => {
        this.taskStore.updateTask(task.id, task)

        if (moveSuccessors && task.successors?.length && daysDelta) {
          this.updateSuccessorTasks(task, daysDelta, ignoreSuccessorsIds, timezone)
        }

        if (operationId) {
          this.taskStore.history.endOperation(operationId)
        }
      }),
      catchError(error => {
        if (operationId) {
          this.taskStore.history.undo(operationId)
        }

        return throwError(error)
      })
    )
  }

  /**
   * Updates the task's successors recursively moving them the specified number of days
   * @param {Task} task
   * @param {number} deltaDays
   * @param {string[]} updatedTaskIds
   * @param timezone Timezone whose wall-clock time is preserved.
   */
  updateSuccessorTasks(task: Task, deltaDays: number, updatedTaskIds: string[], timezone = getAccountTimezone()): void {
    updatedTaskIds.push(task.id)

    if (!task.successors?.length) {
      return
    }

    const successorTasks = this.taskQuery.getTasks(task.successors.map(successors => successors.taskId))

    return successorTasks.forEach(successor => {
      if (updatedTaskIds.includes(successor.id)) {
        return
      }

      if (successor.startOn) {
        successor.startOn = convertToUTC(addDays(convertToTimezone(successor.startOn, timezone), deltaDays), timezone)
      }

      if (successor.dueOn) {
        successor.dueOn = convertToUTC(addDays(convertToTimezone(successor.dueOn, timezone), deltaDays), timezone)
      }

      this.updateTask(successor).subscribe()

      this.updateSuccessorTasks(successor, deltaDays, updatedTaskIds, timezone)
    })
  }

  /**
   * Fetches a preview of the derived dates of the parent task
   * @param {string} taskId
   * @returns {Observable<ParentTaskDerivedDatesPreview>}
   */
  fetchParentTaskDerivedDatesPreview(taskId: string): Observable<ParentTaskDerivedDatesPreview> {
    return this.apiClient.get<ParentTaskDerivedDatesPreview>(`${this.url}/${taskId}/subtasks/dates`).pipe(
      map(response => ({
        latestDueOn: response.latestDueOn ? new Date(response.latestDueOn) : null,
        earliestStartOn: response.earliestStartOn ? new Date(response.earliestStartOn) : null
      }))
    )
  }

  /**
   * Makes an API call to update the task
   * @param {Task} task Task to be updated
   * @param {Record<string, string>} [queryParams={}]
   * @returns {Observable<Task>}
   */
  updateTask(task: Task, queryParams: Record<string, string> = {}): Observable<Task> {
    const taskPut = {
      name: task.name,
      description: trimRichText(task.description),
      isPrio: task.isPrio,
      plannedDuration: task.plannedDuration,
      dueOn: task.dueOn,
      startOn: task.startOn,
      laneOrder: task.laneOrder,
      primaryTaskListId: task.primaryTaskListId,
      deriveDatesFromSubtasks: task.deriveDatesFromSubtasks
    }

    // optimistic update
    const originalTask = this.taskQuery.getTask(task.id)

    this.taskStore.updateTask(task.id, task)

    const url: string = this.getTaskUpdateUrl(task.id)

    return this.apiClient.put<Task>(url, taskPut, { params: queryParams }).pipe(
      map(updatedTask => {
        // To prevent overwrite taskStatus in task modal
        delete updatedTask.taskStatus
        delete updatedTask.taskStatusId

        const completeTask = Task.mapTask(
          new Task({
            ...task,
            ...updatedTask,
            ...{
              tags: task.tags,
              taskLists: task.lists,
              assignees: task.assignees,
              taskStatus: task.taskStatus,
              dueOn: updatedTask.dueOn ?? task.dueOn,
              startOn: updatedTask.startOn ?? task.startOn
            }
          })
        )

        this.taskUpdated.next(completeTask)

        this.taskStore.updateTask(completeTask.id, completeTask)

        return completeTask
      }),
      catchError(error => {
        if (originalTask) {
          // Update storeVersion to make sure it's bumped on update
          const latestStoreVersion = this.taskQuery.getTask(task.id)?.storeVersion

          originalTask.storeVersion = latestStoreVersion || 0

          this.taskStore.updateTask(task.id, originalTask)
        }

        return throwError(error)
      })
    )
  }

  /**
   * Makes an API call to update the order of the task
   * @param {Task} task The task whose order is to be updated
   * @returns {Observable<EntityReorderResponse[]>}
   */
  updateTaskOrder(task: Task): Observable<EntityReorderResponse[]> {
    const url: string = this.getTaskUpdateUrl(task.id) + `/updateorder`

    const originalTask = this.taskQuery.getTask(task.id)
    const operationId = this.taskStore.history.startOperation({ type: 'update', entities: [originalTask] })

    this.taskStore.updateTask(task.id, task)

    return this.apiClient
      .post<EntityReorderResponse[]>(url, {
        order: task.order
      })
      .pipe(
        tap(tasksOrder => {
          tasksOrder.forEach(taskOrder => {
            try {
              this.taskStore.updateTask(taskOrder.id, { order: taskOrder.order })
            } catch (_) {}
          })

          this.taskStore.history.endOperation(operationId)
        }),
        catchError(error => {
          this.taskStore.history.undo(operationId)
          return throwError(error)
        })
      )
  }

  /**
   * Updates the assignees planned efforts for a task.
   * If the assignee has a distributed planned effort, sends the value as null
   * so the BE can handle it and convert it to distributed
   * @param {Task} task - Task to update.
   * @param {TaskAssignee[]} assignees - Assignees and their planned effort.
   * @returns {Observable<void>} Completion after the update, or immediately when denied.
   */
  updateTaskPlannedEfforts(task: Task, assignees: TaskAssignee[]): Observable<void> {
    if (!this.permissionsService.canReadProjectBudget(task.projectId)) {
      return of(undefined)
    }

    const isPEHierarchySettingEnabled = this.taskSettingsQuery.getTaskSettings().enablePlannedEffortHierarchy
    const updateTaskPlannedDuration = !(this.isPEHierarchyFFEnabled() && isPEHierarchySettingEnabled)

    const url: string =
      this.getTaskUpdateUrl(task.id) + `/setplannedefforts?updateTaskPlannedDuration=${updateTaskPlannedDuration}`

    const efforts = assignees.map(assignee => ({
      userId: assignee.id,
      plannedEffort: !assignee.isDistributedPlannedEffort ? assignee.plannedEffort : null
    }))

    this.plannedEffortUpdateLoadingService.startLoading()

    return this.apiClient.post(url, efforts).pipe(
      map(_ => {
        try {
          this.taskStore.updateTask(task.id, { assignees })
        } catch (_) {}
      }),
      catchError(error => {
        return throwError(error)
      }),
      finalize(() => {
        this.plannedEffortUpdateLoadingService.stopLoading()
      })
    )
  }

  /**
   * Updates tasks only in the store (used for some optimistic order updates)
   * @param {Task[]} [tasks=[]]
   */
  updateTasksInStore(tasks: Task[] = []): void {
    tasks.forEach(task => {
      this.taskStore.updateTask(task.id, task)
    })
  }

  /**
   * Makes an API call to delete the tasks
   * @param {Task[]} tasks The tasks to be deleted
   * @param {IDeleteTaskBody} body
   * @returns {Observable<void>}
   */
  deleteTask(tasks: Task[], body: IDeleteTaskBody): Observable<void> {
    const tasksById = new Map(tasks.map(task => [task.id, task]))

    const independentTaskIds = this.getIndependentTaskIds([...new Set(body.taskIds)], BatchOperation.deleteTask)

    return from(independentTaskIds).pipe(
      bufferCount(MAX_BATCH_OPERATION_ITEMS),
      concatMap(taskIds =>
        this.deleteTaskBatch(
          taskIds.map(id => tasksById.get(id)).filter(task => !!task),
          { ...body, taskIds }
        )
      ),
      toArray(),
      map(() => void 0),
      shareReplay({ bufferSize: 1, refCount: false })
    )
  }

  /**
   * Deletes one batch and rolls back only that batch on failure.
   * @param {Task[]} tasks The tasks to delete.
   * @param {IDeleteTaskBody} body The bounded request.
   * @returns {Observable<void>}
   */
  private deleteTaskBatch(tasks: Task[], body: IDeleteTaskBody): Observable<void> {
    const taskIds = tasks.map(task => task.id) || []
    const subtasks = tasks
      ?.filter(task => !task.parentId)
      .reduce((subIds, task) => {
        const ids = this.taskQuery.getByParentId(task.id) || []

        return [...ids, ...subIds]
      }, [])
    const subtasksIds = subtasks?.map(task => task.id) || []

    // Remove tasks optimistically
    this.taskStore.remove([...taskIds, ...subtasksIds])
    // Remove all subtasks for given parent

    this.totalItems = this.totalItems - tasks.length

    return this.apiClient.post<HttpResponse<void>>(`${this.url}/delete`, body, { observe: 'response' }).pipe(
      map(response => {
        if (response.status === 207) {
          throw new HttpErrorResponse({ status: 207, statusText: 'MultiStatus', error: response.body })
        }

        tasks.forEach(task => {
          this.taskDeleted.next(task.id)
          this.updateTaskTimeTrackings(task.id, body)
        })

        const subtasks = tasks.filter(t => t.parentId)

        this.updateNumberOfSubtasksForParents(subtasks, -1)
      }),
      catchError(error => {
        // Rollback in case of error
        this.taskStore.add([...tasks, ...subtasks])
        this.totalItems = this.totalItems + tasks.length

        return throwError(error)
      })
    )
  }

  /**
   * Updates the numberOfSubtasks for all parent tasks of the subtasks
   * @param {Task[]} subtasks
   * @param {number} increment
   */
  private updateNumberOfSubtasksForParents(subtasks: Task[], increment: number): void {
    subtasks.forEach(subtask => {
      this.taskStore.update(subtask.parentId, task => {
        task.numberOfSubtasks += increment
        return task
      })
    })
  }

  /**
   * Update the task's time trackings
   * @param {string} taskId
   * @param {IDeleteTaskBody} body
   */
  private updateTaskTimeTrackings(taskId: string, body: IDeleteTaskBody): void {
    const timeTrackingOperation = body.deleteTimeTrackings ? 'delete' : 'unlink'
    this.timeTrackingStore.updateEntityTimeTrackings('task', taskId, timeTrackingOperation)
  }

  // ########### TASK PROPERTIES ##############

  /**
   * Makes an API call to update the task's project
   * @param {Task} task Task to be updated
   * @param {string} projectId Current task's project id
   * @returns {Observable<Task>}
   */
  updateTaskProject(task: Task, projectId: string): Observable<Task> {
    const originalTask = this.taskQuery.getTask(task.id)
    const operationId = this.taskStore.history.startOperation({ type: 'update', entities: [originalTask] })

    task.entityId = task.project.id
    this.taskStore.updateTask(task.id, task)

    return this.apiClient
      .post<void>(`${apiEndpoint}/projects/${projectId}/projecttasks/${task.id}/changeproject`, {
        projectId: task.project.id,
        taskStatusId: task.taskStatusId
      })
      .pipe(
        map(() => {
          // we need to reassign here again the entityId,
          // otherwise we can have race conditions with parallel put calls.
          task.entityId = task.project.id
          this.taskStore.updateTask(task.id, task)
          this.taskStore.history.endOperation(operationId)
          this.taskUpdated.next(task)

          return task
        }),
        catchError(error => {
          this.taskStore.history.undo(operationId)
          return throwError(error)
        })
      )
  }

  /**
   * Makes an API call to change the base type of a task
   * @param {ValidTaskBaseTypes} baseType The base type the tasks should be migrated to
   * @param {ValidTaskEntityTypes} entity The entity the tasks should be migrated to
   * @param {Task[]} tasks The tasks to be migrated
   * @returns {Observable<void>}
   */
  updateBaseType(baseType: ValidTaskBaseTypes, entity: ValidTaskEntityTypes, tasks: Task[]): Observable<void> {
    const url: string = this.url + '/changebasetypes'
    const taskIds = tasks.map(task => task.id)
    const body = {
      baseType,
      entityId: entity.id,
      taskIds
    }

    return this.apiClient.post(url, body).pipe(
      map(() => {
        tasks.forEach(task => {
          task.entity = entity
          task.entityId = entity.id
          task.baseType = baseType
          this.taskUpdated.next(task)

          this.taskStore.updateTask(task.id, task)
        })
      })
    )
  }

  /**
   * Makes an API call to set the priority of the task
   * @param {Task} task The task to be prioritized
   * @returns {Observable<void>}
   */
  setTaskPriority(task: Task): Observable<void> {
    return this.apiClient
      .post(`${this.url}/settaskpriority`, {
        taskId: task.id,
        isPrio: task.isPrio
      })
      .pipe(
        map(() => {
          this.taskUpdated.next(task)

          this.taskStore.updateTask(task.id, task)
        })
      )
  }

  /**
   * Makes an API call to assign an user to a task by email
   * @param {Task} task The task to be updated
   * @param {string} email
   * @param {string} name
   * @param {string} roleId
   * @param {string} gender
   * @returns {Observable<Task>} The task information
   */
  assignUserByEmail(task: Task, email: string, name: string, roleId: string, gender: string): Observable<Task> {
    let firstName: string, lastName: string

    if (name) {
      if (name.includes(' ')) {
        firstName = name.substr(0, name.indexOf(' '))
        lastName = name.substr(name.indexOf(' ') + 1)
      } else {
        firstName = name
      }
    }

    const url = `${this.url}/assignuserbyemail`
    const body = {
      taskId: task.id,
      email,
      firstName,
      lastName,
      roleId,
      gender
    }

    return this.apiClient.post<string>(url, body).pipe(
      map(() => {
        this.taskUpdated.next(task)

        if (task.baseType === 'projecttask') {
          this.projectMembersToBeChecked.next(task)
        }

        return task
      }),
      catchError(error => {
        return throwError(error)
      })
    )
  }

  /**
   * Sends new version of task assignees to API
   * @param {Task} task
   * @param {string[]} [originalAssigneeIds=null] only needed to activley prevent API call if there is no change
   * @returns {Observable<Task>}
   */
  manageAssignees(task: Task, originalAssigneeIds: string[] = null): Observable<Task> {
    const newAssigneeIds = task.assignees?.map(assignee => assignee.id) || []
    const originalTask = this.taskQuery.getTask(task.id)

    // Check if assignees have actually changed using the shared areArraysEqual utility
    if (originalAssigneeIds) {
      const hasAssigneesChanged = !areArraysEqual(newAssigneeIds, originalAssigneeIds, true)

      // If assignees haven't changed, return the task without making an API call
      if (!hasAssigneesChanged) {
        return of(task)
      }
    }

    const url = `${this.url}/${task.id}/setassignees`

    const operationId = this.taskStore.history.startOperation({ type: 'update', entities: [originalTask] })

    this.taskStore.updateTask(task.id, { assignees: task.assignees })

    return this.apiClient.post<HttpResponse<void | ErrorResponse[]>>(url, newAssigneeIds, { observe: 'response' }).pipe(
      switchMap(response => {
        // Handle guest access restriction error case
        if (response.status === 207) {
          const errorResponses = response.body as ErrorResponse[]

          if (
            errorResponses &&
            errorResponses.some(errorResponse => errorResponse.code === 'guest-access-restriction')
          ) {
            this.taskStore.history.undo(operationId)

            return throwError(
              new HttpErrorResponse({
                error: errorResponses[0],
                status: 400
              })
            )
          }
        }

        this.taskUpdated.next(task)

        if (task.baseType === 'projecttask') {
          this.projectMembersToBeChecked.next(task)
        }

        this.taskStore.history.endOperation(operationId)

        return of(task)
      }),
      catchError(error => {
        this.taskStore.history.undo(operationId)
        return throwError(error)
      })
    )
  }

  /**
   * Sends new version of task assignees to API
   * @param {TaskTemplate} task
   * @param {TaskBundle} taskBundle
   * @returns {Observable<void>}
   */
  manageTaskTemplateAssignees(task: TaskTemplate, taskBundle: TaskBundle): Observable<void> {
    const newAssigneeIds = task.assigneeIds || []

    const url = `${this.bundleUrl}/${taskBundle.id}/tasktemplates/${task.id}/setassignees`

    return this.apiClient.post<void>(url, newAssigneeIds, { observe: 'response' })
  }

  /**
   * Fetches the tags sets shown in the popup for task templates
   * @returns {Observable<Tag[]>}
   */
  fetchTagsForTaskTemplates(): Observable<Tag[]> {
    return this.apiClient.get<Tag[]>(`${apiEndpoint}/tasktemplates/tags`)
  }

  /**
   * Makes API calls to add/delete tags to task template
   * @param {TagOwner} owner The task template to be updated
   * @param {Tag[]} tags The task's tags
   * @param {Tag[]} addedTags The tags to be added to the task
   * @param {Tag[]} removedTags The tags to be deleted from the task
   * @returns {Observable<Tag[]>}
   */
  manageTaskTemplateTags(owner: TagOwner, tags: Tag[], addedTags: Tag[], removedTags: Tag[]): Observable<Tag[]> {
    const calls$: Observable<void>[] = []
    const taskTemplate = owner as TaskTemplate

    if (addedTags.length) {
      calls$.push(this.addTaskTemplateTags(taskTemplate.id, addedTags))
    }

    if (removedTags.length) {
      calls$.push(this.deleteTaskTemplateTags(taskTemplate.id, removedTags))
    }

    return forkJoin(calls$).pipe(
      map(() => {
        this.taskStore.update(taskTemplate.id, { tags })
        return tags
      })
    )
  }

  /**
   * Makes an API call to add tags to the task template
   * @param {string} taskTemplateId The id of the entity
   * @param {Tag[]} tags The tags to be added to the task
   * @returns {Observable<void>}
   */
  private addTaskTemplateTags(taskTemplateId: string, tags: Tag[]): Observable<void> {
    const url = `${apiEndpoint}/tasktemplates/${taskTemplateId}`
    return this.apiClient.post<void>(`${url}/addtags`, tags)
  }

  /**
   * Makes an API call to delete tags from the task template
   * @param {string} taskTemplateId The id of the entity
   * @param {Tag[]} tags The tags to be removed to the task
   * @returns {Observable<void>}
   */
  private deleteTaskTemplateTags(taskTemplateId: string, tags: Tag[]): Observable<void> {
    const url = `${apiEndpoint}/tasktemplates/${taskTemplateId}`
    return this.apiClient.post<void>(`${url}/deletetags`, tags)
  }

  /**
   * Assigns the current user and sets the status to in progress of a task.
   * @param {Task} task The task to be updated
   * @param {User} currentUser The current user
   */
  setInProgressProperties(task: Task, currentUser: User): void {
    if (task.taskStatus.type === 'todo') {
      const previousStatus: TaskStatus = cloneShallow(task.taskStatus)

      if (
        task.projectId &&
        (task.isUserAssigned(currentUser.id) ||
          this.permissionsService.isAllowed(Features.ProjectPlanning, AccessLevels.Manage, false, task.projectId))
      ) {
        const projectTaskStatuses = this.taskStatusQuery.queryProjectStatuses(task.projectId)

        const progressStatus = projectTaskStatuses().find(status => status.type === 'progress')

        if (!progressStatus) {
          return
        }

        task.taskStatus = progressStatus
        task.taskStatusId = progressStatus.id
      } else if (task.baseType === 'private') {
        const privateStatuses = this.taskStatusQuery.queryPrivateStatuses()

        const progressStatus = privateStatuses().find(status => status.type === 'progress')

        if (!progressStatus) {
          return
        }

        task.taskStatus = progressStatus
        task.taskStatusId = progressStatus.id
      }

      // If the task is set to "progress" and no user is assigned, assign the current user
      this.autoAssignCurrentUser(task, currentUser)
      // Update task status
      this.updateTasksStatuses([task], previousStatus).subscribe()
    }
  }

  /**
   * Assigns the current user to a task, but just if there is no current assignee and the task status is in progress.
   * @param {Task} task The task to be updated
   * @param {User} currentUser The current user
   */
  autoAssignCurrentUser(task: Task, currentUser: User): void {
    if (task.baseType === 'projecttask' && !task.hasAssignees && currentUser && task.taskStatus.type === 'progress') {
      task.assignUserOnProgress(currentUser)

      this.manageAssignees(task).subscribe(
        () => {
          this.taskUpdated.next(task)

          this.taskStore.updateTask(task.id, task)
        },
        () => {
          task.assignees = []
        }
      )
    }
  }

  /**
   * Makes an API call to update the statuses of the task
   * @param {Task[]} tasks Tasks to be updated including the new status
   * @param {TaskStatus} previousStatus The previous status of the tasks
   * @param {boolean} [suppressNotificationChecks=false]
   * @param {boolean} [ignoreTaskOrder=true]
   *   If set, the current task order within a status is ignored and a null value passed to the api and the api calculates
   *   the actual order
   * @param {boolean} [trackClosedEvent=true]
   * @returns {Observable<void>}
   */
  updateTasksStatuses(
    tasks: Task[],
    previousStatus: TaskStatus,
    suppressNotificationChecks = false,
    ignoreTaskOrder = true,
    trackClosedEvent = true
  ): Observable<void> {
    const changeStatuses = []
    const temporaryTaskStatusSignalExclusions: Omit<ExcludedSignal, 'expiresAt'>[] = tasks.flatMap(task =>
      task.id
        ? [
            { entityName: 'task', entityId: task.id, propertyName: 'taskStatusId' },
            { entityName: 'task', entityId: task.id, propertyName: 'taskStatus' }
          ]
        : []
    )

    const originalTasks = this.taskQuery.getTasks(tasks.map(task => task.id))
    const operationId = this.taskStore.history.startOperation({ type: 'update', entities: originalTasks })

    this.signalService.temporarilyExcludeSignals(temporaryTaskStatusSignalExclusions, TASK_STATUS_SIGNAL_EXCLUSION_MS)

    tasks.forEach(task => {
      const taskInStatus = this.taskQuery.getByTaskStatus(task.taskStatus.id)

      // private tasks use the order like the kanban board, so the order needs to stay after status change
      if (ignoreTaskOrder && task.baseType !== 'private') {
        task.order = taskInStatus.length
      }

      this.taskStore.updateTask(task.id, task)

      changeStatuses.push({
        taskId: task.id,
        statusId: task.taskStatus.id,
        order: ignoreTaskOrder && task.baseType !== 'private' ? null : task.order
      })
    })

    // Track Event
    if (tasks[0] && tasks[0].taskStatus && tasks[0].taskStatus.type === 'done' && trackClosedEvent) {
      this.trackingService.trackEvent(TrackingEvent.taskClosed, {})

      if (!suppressNotificationChecks) {
        // Check if the task is a project task
        if (tasks[0].baseType === 'projecttask') {
          this.taskClosed.next(tasks[0])
        }
      }
    }

    const taskIds = tasks.filter(task => !!task.id).map(task => task.id)

    // Timeout to wait until the done animation finish
    setTimeout(
      () => {
        this.taskStore.updateTask(taskIds, {
          taskStatus: tasks[0].taskStatus,
          taskStatusId: tasks[0].taskStatus.id,
          justCheckedOff: tasks[0].justCheckedOff
        })
      },
      tasks[0].taskStatus.type === 'done' ? 1000 : 0
    )

    return this.apiClient.post<string>(`${this.url}/changestatuses`, changeStatuses).pipe(
      map(() => {
        tasks.forEach(task => {
          this.taskUpdated.next(task)
          this.taskStatusUpdated.next({ task, previousStatus })
        })

        this.taskStore.history.endOperation(operationId)
      }),
      catchError(error => {
        this.signalService.removeTemporaryExcludedSignals(temporaryTaskStatusSignalExclusions)
        this.taskStore.history.undo(operationId)

        return throwError(error)
      })
    )
  }

  /**
   * Makes an API call to update the types of the tasks
   * @param {Task[]} tasks Tasks to be updated
   * @param {string} workTypeId The id of the work type
   * @param {boolean} [moveTimeTrackings=true] True to also move the time trackings
   * @returns {Observable<void>}
   */
  updateTasksTypeOfWork(tasks: Task[], workTypeId: string, moveTimeTrackings = true): Observable<void> {
    const taskIds = tasks.map(task => task.id)

    const originalTasks = this.taskQuery.getTasks(taskIds)
    const operationId = this.taskStore.history.startOperation({ type: 'update', entities: originalTasks })

    tasks.forEach(task => {
      task.typeOfWorkId = workTypeId
      this.taskStore.updateTask(task.id, task)
    })

    return this.apiClient
      .post<string>(`${this.url}/changetypeofwork`, {
        typeOfWorkId: workTypeId,
        taskIds,
        moveTimeTrackings
      })
      .pipe(
        map(() => {
          tasks.forEach((task, index) => {
            this.taskUpdated.next(task)

            if (tasks.length - 1 === index) {
              // Save the type to the last used entity type setting
              if (task.typeOfWorkId) {
                this.userSettingsService.setLastUsedWorkTypeSetting(task.typeOfWorkId, 'task')
              }
            }
          })

          this.taskStore.history.endOperation(operationId)
        }),
        catchError(error => {
          this.taskStore.history.undo(operationId)

          return throwError(error)
        })
      )
  }

  // ########### TASK LISTS ##############

  /**
   * Makes an API call to request all the task lists
   * @param {Injector} _injector
   * @param {string} projectId The id of the project
   * @param {boolean} [showArchived=false]
   * @returns {Observable<TaskList[]>} The taskLists information
   */
  fetchTaskLists(_injector: Injector, projectId: string, showArchived = false): Observable<TaskList[]> {
    return this.apiClient
      .get<TaskList[]>(`${apiEndpoint}/projects/${projectId}/tasklists?showArchived=${showArchived}`)
      .pipe(
        map(lists => {
          const taskLists =
            lists?.map(list => {
              list.projectId = projectId
              return new TaskList(list)
            }) || []

          this.taskListStore.sync(taskLists, this.taskListQuery.getByProject(projectId))
          this.taskListStore.setLoading(false)

          return this.taskListQuery.getByProject(projectId, showArchived)
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            this.taskListStore.setLoading(false)
            this.taskListStore.sync([], this.taskListQuery.getByProject(projectId))
          }

          return throwError(() => error)
        })
      )
  }

  /**
   * Makes an API call to request the task list information
   * @param {ValidEntityTypes} entityType The type of the entity
   * @param {string} entityId The id of the entity
   * @param {string} taskListId The id of the task list
   * @returns {Observable<TaskList>} The task list information
   */
  fetchTaskList(entityType: ValidEntityTypes, entityId: string, taskListId: string): Observable<TaskList> {
    return this.apiClient.get<TaskList>(`${this.getTaskListUrl(entityType, entityId)}/${taskListId}`).pipe(
      map(list => {
        entityType === 'users' ? (list.userId = entityId) : (list.projectId = entityId)
        list = new TaskList(list)

        this.taskListStore.add(list)
        this.taskListStore.setLoading(false)

        return list
      })
    )
  }

  /**
   * Makes an API call to create a task list
   * @param {ValidEntityTypes} entityType The type of the entity
   * @param {string} entityId The id of the entity
   * @param {TaskList} taskList The task list to be created
   * @param {boolean} [updateOptimistically=true]
   * @returns {Observable<TaskList>} The task list information
   */
  sendTaskList(
    entityType: ValidEntityTypes,
    entityId: string,
    taskList: TaskList,
    updateOptimistically = true
  ): Observable<TaskList> {
    const taskListPost = {
      name: taskList.name,
      order: taskList.order
    }

    let optimisticId: string

    // If taskList already has an id, is a random one for optimistic saving purposes
    if (updateOptimistically && taskList.id) {
      optimisticId = taskList.id
      this.taskListStore.add(taskList)
    }

    return this.apiClient.post<TaskList>(this.getTaskListUrl(entityType, entityId), taskListPost).pipe(
      map(list => {
        Object.assign(taskList, list)
        taskList.projectId = entityId
        taskList.isSaving = false

        this.setNewListIdTracker(null, list.id)

        // If taskList was added optimistically, remove it and add the saved one
        if (optimisticId) {
          this.taskListStore.remove(optimisticId)
        }

        this.taskListStore.add(taskList)

        return list
      }),
      catchError(error => {
        if (optimisticId) {
          this.taskListStore.remove(optimisticId)
        }

        return throwError(error)
      })
    )
  }

  /**
   * Makes an API call to update the task list
   * @param {ValidEntityTypes} entityType The type of the entity
   * @param {string} entityId The id of the entity
   * @param {TaskList} taskList Task list to be updated
   * @returns {Observable<TaskList>} The task list information
   */
  updateTaskList(entityType: ValidEntityTypes, entityId: string, taskList: TaskList): Observable<TaskList> {
    const taskListPutForm: TaskListPutForm = {
      name: taskList.name,
      plannedDuration: taskList.plannedDuration
    }

    if (entityType === 'projects') {
      const project = this.projectQuery.queryEntity(entityId)

      // avoid external users to change the visibility of the task list
      if (!project().isExternal) {
        taskListPutForm.isHiddenForConnectUsers = taskList.isHiddenForConnectUsers
      }
    }

    this.plannedEffortUpdateLoadingService.startLoading()

    return this.apiClient
      .put<TaskList>(`${this.getTaskListUrl(entityType, entityId)}/${taskList.id}`, taskListPutForm)
      .pipe(
        map(() => {
          this.taskListStore.update(taskList.id, { name: taskList.name, isArchived: taskList.isArchived })

          return taskList
        }),
        finalize(() => {
          this.plannedEffortUpdateLoadingService.stopLoading()
        })
      )
  }

  /**
   * Makes an API call to duplicate a task list with its tasks and subtaks
   * @param {string} projectId
   * @param {string} taskListId
   * @returns {Observable<void>}
   */
  copyTaskList(projectId: string, taskListId: string): Observable<void> {
    const copyTaskListUrl = `${apiEndpoint}/projects/${projectId}/tasklists/${taskListId}/copy`

    return this.apiClient.post<TaskList>(copyTaskListUrl, {}).pipe(
      map(taskList => {
        this.taskListStore.add(taskList)
      })
    )
  }

  /**
   * Set task list to archived or not
   * @param {ValidEntityTypes} entityType The type of the entity
   * @param {string} entityId The id of the entity
   * @param {TaskList} taskList Task list to be updated
   * @param {TaskListMigrationOperation} taskOperation
   * @param {TaskList} moveToSelectedTaskList
   * @param {Task[]} undoneTasks
   * @param {TaskStatus} previousStatus
   * @param {boolean} deleteTimeTrackings
   * @returns {Observable<TaskList>} The task list information
   */
  setArchivedTaskList(
    entityType: ValidEntityTypes,
    entityId: string,
    taskList: TaskList,
    taskOperation?: TaskListMigrationOperation,
    moveToSelectedTaskList?: TaskList,
    undoneTasks?: Task[],
    previousStatus?: TaskStatus,
    deleteTimeTrackings?: boolean
  ): Observable<TaskList> {
    let taskStoreOperationId: string

    if (undoneTasks?.length) {
      // Remove open tasks from list to be without list
      if (taskOperation === 'task-remove') {
        taskStoreOperationId = this.taskStore.history.startOperation({ type: 'update', entities: undoneTasks })

        undoneTasks.forEach(task => {
          task.lists = task.lists.filter(list => list.id !== taskList.id)
          this.taskStore.update(task.id, task)
        })
      }
      // Deleting open tasks
      else if (taskOperation === 'task-delete') {
        taskStoreOperationId = this.taskStore.history.startOperation({ type: 'remove', entities: undoneTasks })

        const taskIds = undoneTasks.map(undoneTask => undoneTask.id)

        this.taskStore.remove(taskIds)
      }
      // Move task to another list
      else if (taskOperation === 'task-move') {
        taskStoreOperationId = this.taskStore.history.startOperation({ type: 'update', entities: undoneTasks })

        const newTasks = this.taskQuery.queryByTaskList(moveToSelectedTaskList.id)

        if (newTasks()) {
          const newListTasks = newTasks()

          undoneTasks.forEach(task => {
            const tasksOrder = newListTasks.map(newTask => newTask.getOrderForTaskList(moveToSelectedTaskList))
            const lastOrderNumber = Math.max.apply(null, tasksOrder)

            task.setOrderForTaskList(moveToSelectedTaskList, lastOrderNumber + 1)
            newListTasks.push(task)

            // If the task list is not already in the task's list array, push it
            if (!task.lists.some(list => list.id === moveToSelectedTaskList.id)) {
              task.lists = task.lists.filter(list => list.id !== taskList.id)
              task.lists.push(moveToSelectedTaskList)
            }

            this.taskStore.updateTask(task.id, task)
          })
        }
        // open tasks to be done
      } else if (taskOperation === 'task-to-done') {
        taskStoreOperationId = this.taskStore.history.startOperation({ type: 'update', entities: undoneTasks })

        undoneTasks.forEach(task => this.taskStore.updateTask(task.id, task))
      }
    }

    const taskListStoreOperationId = this.taskListStore.history.startOperation({ type: 'update', entities: [taskList] })

    this.taskListStore.update(taskList.id, { isArchived: taskList.isArchived, order: taskList.order })

    return this.apiClient
      .post<TaskList>(`${this.getTaskListUrl(entityType, entityId)}/${taskList.id}/setarchived`, {
        taskOperation,
        taskListId: moveToSelectedTaskList ? moveToSelectedTaskList.id : null,
        isArchived: taskList.isArchived,
        deleteTimeTrackings
      })
      .pipe(
        tap(() => {
          this.taskStore.history.endOperation(taskStoreOperationId)
          this.taskListStore.history.endOperation(taskListStoreOperationId)
        }),
        map(() => taskList),
        catchError(error => {
          if (undoneTasks?.length) {
            this.taskStore.history.undo(taskStoreOperationId)
          }

          this.taskListStore.history.undo(taskListStoreOperationId)

          return throwError(error)
        })
      )
  }

  /**
   * Makes an API call to delete the task list
   * @param {ValidEntityTypes} entityType The type of the entity
   * @param {string} entityId The id of the entity
   * @param {TaskList} taskList The task list to be deleted
   * @param {Task[]} tasks The task list's tasks to update their lists array
   * @param {boolean} [deleteTasks=false] Delete the tasks from the list too
   * @param {boolean} [deleteTimes=false]
   * @returns {Observable<void>}
   */
  deleteTaskList(
    entityType: ValidEntityTypes,
    entityId: string,
    taskList: TaskList,
    tasks: Task[],
    deleteTasks = false,
    deleteTimes = false
  ): Observable<void> {
    if (!taskList?.id) {
      return of(null)
    }

    const taskListStoreOperationId = this.taskListStore.history.startOperation({ type: 'remove', entities: [taskList] })

    this.taskListStore.remove(taskList.id)

    if (deleteTasks && tasks?.length) {
      this.taskStore.remove(tasks.map(t => t.id))
    }

    return this.apiClient
      .post<void>(`${this.getTaskListUrl(entityType, entityId)}/${taskList.id}` + `/delete`, {
        deleteTasks,
        deleteTimes
      })
      .pipe(
        map(() => {
          if (!deleteTasks) {
            tasks.forEach(task => {
              task.lists = task.lists.filter(list => list.id !== taskList.id)
              this.taskStore.updateTask(task.id, task)
            })

            this.taskListStore.history.endOperation(taskListStoreOperationId)
          }
        }),
        catchError(error => {
          this.taskListStore.history.undo(taskListStoreOperationId)
          return throwError(error)
        })
      )
  }

  /**
   * Makes an API call to update the orderBy of the task list
   * @param {TaskList} taskList The task list whose order is to be updated
   * @param {number} order The new order of the task list
   * @returns {Observable<EntityReorderResponse[]>}
   */
  updateTaskListsOrder(taskList: TaskList, order: number): Observable<EntityReorderResponse[]> {
    const type: ValidEntityTypes = taskList.userId ? 'users' : 'projects'
    const entityId: string = taskList.userId ? taskList.userId : taskList.projectId

    const url: string = this.getTaskListUrl(type, entityId) + `/${taskList.id}/updateorder`

    this.taskListStore.update(taskList.id, taskList)

    return this.apiClient.post<EntityReorderResponse[]>(url, {
      order
    })
  }

  /**
   * Updates tasks list only in the store (used for some optimistic order updates)
   * @param {TaskList[]} [taskLists=[]]
   */
  updateTaskListsInStore(taskLists: TaskList[] = []): void {
    taskLists.forEach(taskList => {
      this.taskListStore.update(taskList.id, taskList)
    })
  }

  /**
   * Makes an api call to set the task lists and the order of multiple tasks.. Updates the store with the api results.
   * @param {Task[]} tasks The tasks whose lists/order is to be updated
   * @returns {Observable<void>}
   */
  updateListsForTasks(tasks: Task[]): Observable<void> {
    const url = `${this.url}/changeLists`
    const taskListChanges: ChangeTaskLists[] = []

    const filteredTasks = tasks.filter(task => !!task.project)
    const operationId = this.taskStore.history.startOperation({ type: 'update', entities: filteredTasks })

    filteredTasks.forEach(task => {
      // filter out duplicates
      task.lists = task.lists.filter((a, i) => task.lists.findIndex(s => a.id === s.id) === i)

      const lists = task.lists.map(list => {
        return {
          id: list.id,
          order: list.orderOfTask ?? Number.MAX_SAFE_INTEGER,
          isPrimary: list.id === task.primaryTaskListId
        }
      })

      this.taskStore.update(task.id, task)

      taskListChanges.push({ taskId: task.id, taskLists: lists })
    })

    if (!taskListChanges.length) {
      this.taskStore.history.endOperation(operationId)

      return of(null)
    }

    return this.apiClient.post<ChangeTaskLists[]>(url, taskListChanges).pipe(
      map(
        (response: ChangeTaskLists[]) => {
          response.forEach(changeListResult => {
            const originalTask = tasks.find(task => task.id === changeListResult.taskId)

            changeListResult.taskLists.forEach(list => {
              const originalList = originalTask.lists.find(originalList => originalList.id === list.id)
              originalList.orderOfTask = list.order
            })

            this.taskStore.update(originalTask.id, originalTask)
          })

          this.taskStore.history.endOperation(operationId)
        },
        catchError(error => {
          this.taskStore.history.undo(operationId)
          return throwError(error)
        })
      )
    )
  }

  /**
   * Makes an API call to move a task from a list to another
   * @param {TaskList} fromTaskList The task list from where to move
   * @param {TaskList} toTaskList The task list to where to move
   * @param {Task} task The type of the entity
   * @param {number} order The id of the entity
   * @returns {Observable<Task>}
   */
  moveTaskToList(fromTaskList: TaskList, toTaskList: TaskList, task: Task, order: number): Observable<Task> {
    if (fromTaskList) {
      task.lists = task.lists.filter(list => list.id !== fromTaskList.id)
    }

    if (toTaskList) {
      toTaskList.order = order
      task.lists.push(toTaskList)
    }

    this.taskStore.updateTask(task.id, task)

    return this.updateListsForTasks([task]).pipe(
      map(() => {
        return task
      })
    )
  }

  /**
   * Makes an API call to update the orderBy of the task in a task list
   * @param {TaskList} taskList The task list which contains the task
   * @param {Task} task The task whose order is to be updated
   * @param {number} newOrder The new order
   */
  updateTaskOrderInList(taskList: TaskList, task: Task, newOrder: number): void {
    const url: string =
      this.getTaskListUrl(task.entityType, task.entityId) + `/${taskList.id}/tasks/${task.id}/updateorder`

    const originalTask = this.taskQuery.getTask(task.id)
    const operationId = this.taskStore.history.startOperation({ type: 'update', entities: [originalTask] })

    this.taskStore.updateTask(task.id, task)

    const call$ = this.apiClient
      .post<EntityReorderResponse[]>(url, {
        order: newOrder
      })
      .pipe(
        tap(tasksOrder => {
          tasksOrder.forEach(taskOrder => {
            try {
              // For each affected task, update the order within the list according to the response
              this.taskStore.update(taskOrder.id, storedTask => {
                const lists = storedTask.lists.map(storedList => {
                  if (storedList.id === taskList.id) {
                    storedList.orderOfTask = taskOrder.order
                  }

                  return storedList
                })

                return { lists }
              })
            } catch (_) {}
          })

          this.taskStore.history.endOperation(operationId)
        }),
        catchError(error => {
          this.taskStore.history.undo(operationId)
          return throwError(error)
        })
      )

    // Add the call to the queue
    this.taskOrderQueue.next(call$)
  }

  /**
   * Moves a task list to another project
   * @param {{ taskListId: string; oldProjectId: string; newProjectId: string; taskStatusMapping: { oldStatusId: string; newStatusId: string; }[]; }} params
   *   The parameters to move the task list to another project
   * @returns {Observable<void>}
   * @param {string} params.taskListId - The id of the task list to move
   * @param {string} params.oldProjectId - The id of the project from where to move
   * @param {string} params.newProjectId - The id of the project to where to move
   * @param {{ oldStatusId: string; newStatusId: string }[]} params.taskStatusMapping - The mapping of the old projects statuses to the new projects statuses
   */
  moveTaskListToProject(params: {
    taskListId: string
    oldProjectId: string
    newProjectId: string
    taskStatusMapping: { oldStatusId: string; newStatusId: string }[]
  }): Observable<void> {
    const url = `${apiEndpoint}/projects/${params.oldProjectId}/tasklists/${params.taskListId}/changeproject`
    return this.apiClient.post<void>(url, {
      projectId: params.newProjectId,
      taskStatusMapping: params.taskStatusMapping
    })
  }

  // ########### TASK STATUS ##############

  /**
   * Fetches the task statuses of all projects
   * @returns {Observable<TaskStatus[]>}
   */
  fetchProjectsTaskStatuses(): Observable<TaskStatus[]> {
    // Set page size to 1500 (no pagination used)
    const params: HttpParams = ApiClient.getQueryParams({ pageSize: 5000 })

    return this.apiClient.get<TaskStatus[]>(`${apiEndpoint}/projects/taskstatuses`, { params }).pipe(
      map(taskStatuses => {
        taskStatuses = taskStatuses.map(taskStatus => new TaskStatus(taskStatus))

        this.taskStatusStore.sync(taskStatuses, this.taskStatusQuery.getProjectsStatuses())

        return taskStatuses
      })
    )
  }

  /**
   * Makes an API call to request the task statuses (global)
   * @param {QueryParams} queryParams
   * @param {string} projectId If set, fetches the task statuses of that project
   * @returns {Observable<TaskStatus[]>} The task statuses
   */
  fetchTaskStatuses(queryParams?: QueryParams, projectId?: string): Observable<TaskStatus[]> {
    const params: HttpParams = ApiClient.getQueryParams(queryParams)
    const apiCache: ApiCacheOptions<TaskStatus> = {
      entity: TaskStatus,
      property: 'projectId',
      value: projectId ?? null
    }
    const options = { params, apiCache }

    let call$: Observable<TaskStatus[]>

    if (projectId) {
      call$ = this.apiClient.get<TaskStatus[]>(`${apiEndpoint}/projects/${projectId}/taskstatuses`, options)
    } else {
      call$ = this.apiClient.get<TaskStatus[]>(`${apiEndpoint}/me/privatetasks/taskstatuses`, options)
    }

    return call$.pipe(
      map(taskStatuses => {
        taskStatuses = (taskStatuses ?? []).map(taskStatus => {
          taskStatus = new TaskStatus(taskStatus)

          taskStatus.projectId = projectId ?? null

          return taskStatus
        })

        this.taskStatusStore.sync(
          taskStatuses,
          projectId ? this.taskStatusQuery.getProjectStatuses(projectId) : this.taskStatusQuery.getPrivateStatuses()
        )

        return taskStatuses
      })
    )
  }

  /**
   * Gets cache invalidation options for project, project-template, or private task status scopes.
   * @param {TaskStatus} taskStatus
   * @returns {ApiCacheInvalidationOptions<TaskStatus>}
   */
  private getTaskStatusCacheInvalidation(taskStatus: TaskStatus): ApiCacheInvalidationOptions<TaskStatus> {
    if (taskStatus.workflowId) {
      return { entity: TaskStatus, property: 'workflowId', value: taskStatus.workflowId }
    }

    if (taskStatus.projectTemplateId) {
      return { entity: TaskStatus, property: 'projectTemplateId', value: taskStatus.projectTemplateId }
    }

    if (taskStatus.projectId) {
      return { entity: TaskStatus, property: 'projectId', value: taskStatus.projectId }
    }

    return { entity: TaskStatus, property: 'projectId', value: null }
  }

  /**
   * Makes an API call to create a task status
   * @param {TaskStatus} taskStatus The task status to be created
   * @returns {Observable<TaskStatus>} The task status information
   */
  sendTaskStatus(taskStatus: TaskStatus): Observable<TaskStatus> {
    taskStatus.name = taskStatus.name.substring(0, 30)

    const url = taskStatus.projectTemplateId
      ? `${apiEndpoint}/projecttemplates/${taskStatus.projectTemplateId}`
      : `${apiEndpoint}/projects/${taskStatus.projectId}`

    return this.apiClient
      .post<TaskStatus>(
        `${url}/taskstatuses`,
        {
          name: taskStatus.name,
          icon: taskStatus.icon,
          type: taskStatus.type
        },
        {
          invalidateCache: this.getTaskStatusCacheInvalidation(taskStatus)
        }
      )
      .pipe(
        map(newTaskStatus => {
          newTaskStatus.projectTemplateId = taskStatus.projectTemplateId
          newTaskStatus.projectId = taskStatus.projectId

          newTaskStatus = new TaskStatus(newTaskStatus)

          this.taskStatusStore.add(newTaskStatus)

          return newTaskStatus
        })
      )
  }

  /**
   * Makes an API call to update a task status
   * @param {TaskStatus} taskStatus Task status to be updated
   * @returns {Observable<TaskStatus>} The task status information
   */
  updateTaskStatus(taskStatus: TaskStatus): Observable<TaskStatus> {
    taskStatus.name = taskStatus.name.substring(0, 30)

    const url = taskStatus.projectTemplateId
      ? `${apiEndpoint}/projecttemplates/${taskStatus.projectTemplateId}`
      : `${apiEndpoint}/projects/${taskStatus.projectId}`

    return this.apiClient
      .put<TaskStatus>(
        `${url}/taskstatuses/${taskStatus.id}`,
        {
          name: taskStatus.name,
          type: taskStatus.type,
          icon: taskStatus.icon,
          order: taskStatus.order
        },
        {
          invalidateCache: this.getTaskStatusCacheInvalidation(taskStatus)
        }
      )
      .pipe(
        map(updatedTaskStatus => {
          updatedTaskStatus = new TaskStatus(updatedTaskStatus)

          this.taskStatusStore.update(updatedTaskStatus.id, updatedTaskStatus)

          if (taskStatus.tasks) {
            taskStatus.tasks.forEach(task => {
              task.taskStatusId = updatedTaskStatus.id
              task.taskStatus = updatedTaskStatus
              this.taskStore.update(task.id, task)
            })
          }

          return updatedTaskStatus
        })
      )
  }

  /**
   * Updates the given task statuses in the store. Needed for the optimistic changes of the order values
   * on client & API in parallel without fetching everything new from the backend.
   * @param {TaskStatus[]} taskStatuses
   */
  updateTaskStatusInStore(taskStatuses: TaskStatus[]): void {
    if (taskStatuses) {
      const validTaskStatuses = taskStatuses.filter(status => !!status.name && !!status.id)
      const taskStatusesById = new Map(validTaskStatuses.map(taskStatus => [taskStatus.id, taskStatus]))

      this.taskStatusStore.update(
        validTaskStatuses.map(taskStatus => taskStatus.id),
        storedTaskStatus => taskStatusesById.get(storedTaskStatus.id) ?? storedTaskStatus
      )
    }
  }

  /**
   * Makes an API call to delete the task status
   * @param {TaskStatus} taskStatus The task status to be deleted
   * @param {IDeleteTaskStatusBody} deleteBody
   * @returns {Observable<void>}
   */
  deleteTaskStatus(taskStatus: TaskStatus, deleteBody: IDeleteTaskStatusBody): Observable<void> {
    const url = taskStatus.projectTemplateId
      ? `${apiEndpoint}/projecttemplates/${taskStatus.projectTemplateId}`
      : `${apiEndpoint}/projects/${taskStatus.projectId}`

    return this.apiClient
      .post(`${url}/taskstatuses/${taskStatus.id}/delete`, deleteBody, {
        invalidateCache: this.getTaskStatusCacheInvalidation(taskStatus)
      })
      .pipe(
        map(() => {
          this.taskStatusDeleted.next(taskStatus)
          this.taskStatusStore.remove(taskStatus.id)

          const newStatus = this.taskStatusQuery.getEntity(deleteBody.taskStatusId)

          // update all tasks with the old status in the store to have the new status
          if (newStatus) {
            const tasks = this.taskQuery.queryByProject(taskStatus.projectId)

            const tasksOfStatus = tasks().filter(task => task.taskStatusId === taskStatus.id)

            // update all tasks
            tasksOfStatus.forEach(task => {
              task.taskStatusId = newStatus.id
              task.taskStatus = newStatus
              this.taskStore.update(task.id, task)
            })
          }
        })
      )
  }

  // ########### PROJECT TYPE TASK STATUS ##############

  /**
   * Makes an API call to request the project template's task statuses
   * @param {string} projectTemplateId If set, fetches the task statuses of that project template
   * @param {QueryParams} queryParams
   * @returns {Observable<TaskStatus[]>} The task statuses
   */
  fetchProjectTemplateTaskStatuses(projectTemplateId: string, queryParams?: QueryParams): Observable<TaskStatus[]> {
    const params: HttpParams = ApiClient.getQueryParams(queryParams)
    const apiCache: ApiCacheOptions<TaskStatus> = {
      entity: TaskStatus,
      property: 'projectTemplateId',
      value: projectTemplateId
    }
    const options = { params, apiCache }

    return this.apiClient
      .get<TaskStatus[]>(`${apiEndpoint}/projecttemplates/${projectTemplateId}/taskstatuses`, options)
      .pipe(
        map(taskStatuses => {
          taskStatuses = (taskStatuses ?? []).map(taskStatus => {
            taskStatus.projectTemplateId = projectTemplateId
            taskStatus = new TaskStatus(taskStatus)

            return taskStatus
          })

          this.taskStatusStore.sync(taskStatuses, this.taskStatusQuery.getProjectTemplateStatuses(projectTemplateId))

          return taskStatuses
        })
      )
  }

  /**
   * Gets from the store and/or from the API the project projectTemplateId's task statuses
   * @param {Injector} injector
   * @param {string} projectTemplateId
   * @param {QueryParams} queryParams
   * @returns {Observable<TaskStatus[]>}
   */
  getProjectTemplateTaskStatuses(
    injector: Injector,
    projectTemplateId: string,
    queryParams?: QueryParams
  ): Observable<TaskStatus[]> {
    let fetched = false
    return this.taskStatusQuery.selectProjectTemplateStatuses(injector, projectTemplateId).pipe(
      mergeMap(taskStatuses => {
        if (taskStatuses) {
          if (!fetched) {
            fetched = true
            this.fetchProjectTemplateTaskStatuses(projectTemplateId, queryParams).subscribe()
          }
        } else if (!fetched) {
          fetched = true

          return this.fetchProjectTemplateTaskStatuses(projectTemplateId, queryParams)
        }

        return of(taskStatuses)
      })
    )
  }

  /**
   * Makes an API call to create a project type's task status
   * @param {TaskStatus} taskStatus The task status to be created
   * @param {string} projectTypeId The project type's id of which the status belong
   * @returns {Observable<TaskStatus>} The task status information
   */
  sendProjectTypeTaskStatus(taskStatus: TaskStatus, projectTypeId: string): Observable<TaskStatus> {
    taskStatus.name = taskStatus.name.substring(0, 30)

    return this.apiClient
      .post<TaskStatus>(`${apiEndpoint}/projecttypes/${projectTypeId}/taskstatuses`, {
        name: taskStatus.name,
        icon: taskStatus.icon,
        type: taskStatus.type
      })
      .pipe(
        map(newTaskStatus => {
          newTaskStatus = new TaskStatus(newTaskStatus)

          this.taskStatusStore.add(newTaskStatus)

          return newTaskStatus
        })
      )
  }

  /**
   * Makes an API call to update a project type's task status
   * @param {TaskStatus} taskStatus Task status to be updated
   * @param {string} projectTypeId The project type's id of which the status belong
   * @returns {Observable<TaskStatus>} The task status information
   */
  updateProjectTypeTaskStatus(taskStatus: TaskStatus, projectTypeId: string): Observable<TaskStatus> {
    taskStatus.name = taskStatus.name.substring(0, 30)

    return this.apiClient
      .put<TaskStatus>(`${apiEndpoint}/projecttypes/${projectTypeId}/taskstatuses/${taskStatus.id}`, {
        name: taskStatus.name,
        type: taskStatus.type,
        icon: taskStatus.icon,
        order: taskStatus.order
      })
      .pipe(
        map(updatedTaskStatus => {
          updatedTaskStatus = new TaskStatus(updatedTaskStatus)

          this.taskStatusStore.update(updatedTaskStatus.id, updatedTaskStatus)

          return updatedTaskStatus
        })
      )
  }

  /**
   * Makes an API call to delete the project template's task status
   * @param {TaskStatus} taskStatus The task status to be deleted
   * @param {string} projectTemplateId The project template's id of which the status belong
   * @returns {Observable<void>}
   */
  deleteProjectTemplateTaskStatus(taskStatus: TaskStatus, projectTemplateId: string): Observable<void> {
    return this.apiClient
      .delete<void>(`${apiEndpoint}/projecttemplates/${projectTemplateId}/taskstatuses/${taskStatus.id}`, {
        invalidateCache: this.getTaskStatusCacheInvalidation(taskStatus)
      })
      .pipe(
        map(() => {
          this.taskStatusDeleted.next(taskStatus)
          this.taskStatusStore.remove(taskStatus.id)
        })
      )
  }

  // ########### TASK BUNDLES ##############

  /**
   * Makes an API call to request all the task bundles
   * This endpoint includes the task lists and task templates that belong to each bundle
   * @param {QueryParams} queryParams
   * @returns {Observable<TaskBundle[]>} The task bundle information
   */
  fetchTaskBundles(queryParams?: QueryParams): Observable<TaskBundle[]> {
    const params: HttpParams = ApiClient.getQueryParams(queryParams)

    return this.apiClient.get<TaskBundle[]>(this.bundleUrl, { params }).pipe(
      map(taskBundles => {
        return taskBundles.map(taskBundle => new TaskBundle(taskBundle))
      })
    )
  }

  /**
   * Makes an API call to request the total count of task bundles
   * @param {QueryParams} queryParams
   * @returns {Observable<number>}
   */
  fetchTaskBundlesCount(queryParams?: QueryParams): Observable<number> {
    let params: HttpParams = ApiClient.getQueryParams(queryParams)

    params = params.append('count', 'true')

    const call$ = this.apiClient.get<HttpResponse<TaskBundle[]>>(this.bundleUrl, {
      params,
      observe: 'response' // To get the full response and get access to the headers (pagination info)
    })

    return call$.pipe(
      map((res: HttpResponse<TaskBundle[]>) => {
        return Number(res.headers.get('aw-totalitems'))
      })
    )
  }

  /**
   * Fetch all the task bundles that are not belonging to a project template.
   * @param {QueryParams} [queryParams={}]
   * @returns {Observable<TaskBundle[]>}
   */
  fetchTaskBundlesWithoutProjectTemplate(queryParams: QueryParams = {}): Observable<TaskBundle[]> {
    const projectTemplateFilter = `projectTemplateId eq null`

    if (queryParams.filterBy) {
      queryParams.filterBy = `${projectTemplateFilter} and ${queryParams.filterBy}`
    } else {
      queryParams.filterBy = projectTemplateFilter
    }

    return this.fetchTaskBundles(queryParams)
  }

  /**
   * Makes an API call to request the task bundle information
   * @param {string} taskBundleId The id of the task bundle
   * @param {QueryParams} queryParams
   * @returns {Observable<TaskBundle>} The task bundle information
   */
  fetchTaskBundle(taskBundleId: string, queryParams?: QueryParams): Observable<TaskBundle> {
    const params: HttpParams = ApiClient.getQueryParams(queryParams)

    return this.apiClient
      .get<TaskBundle>(`${this.bundleUrl}/${taskBundleId}`, { params })
      .pipe(map(taskBundle => new TaskBundle(taskBundle)))
  }

  /**
   * Makes an API call to create a task bundle
   * @param {TaskBundle} taskBundle The task bundle to be created
   * @returns {Observable<TaskBundle>} The task bundle information
   */
  sendTaskBundle(taskBundle: TaskBundle): Observable<TaskBundle> {
    const taskBundlePost = {
      name: taskBundle.name,
      description: taskBundle.description,
      icon: taskBundle.icon
    }

    return this.apiClient.post<TaskBundle>(`${this.bundleUrl}`, taskBundlePost)
  }

  /**
   * Makes an API call to update the task bundle
   * @param {TaskBundle} taskBundle The task bundle to be updated
   * @returns {Observable<TaskBundle>} The task bundle information
   */
  updateTaskBundle(taskBundle: TaskBundle): Observable<TaskBundle> {
    const taskBundlePut = {
      name: taskBundle.name,
      description: taskBundle.description,
      icon: taskBundle.icon
    }
    return this.apiClient.put<TaskBundle>(`${this.bundleUrl}/${taskBundle.id}/`, taskBundlePut)
  }

  /**
   * Makes an API call to delete the task bundle
   * @param {TaskBundle} taskBundle The task bundle to be deleted
   * @returns {Observable<string>}
   */
  deleteTaskBundle(taskBundle: TaskBundle): Observable<string> {
    return this.apiClient.delete(`${this.bundleUrl}/${taskBundle.id}`)
  }

  /**
   * Makes an API call to add (import) a task bundle to an entity (project or user)
   * @param {ValidEntityTypes} entityType The type of the entity
   * @param {string} entityId The id of the string
   * @param {TaskBundle} taskBundle The task bundle that needs to be added to the entity
   * @returns {Observable<void>}
   */
  addTaskBundleToEntity(entityType: ValidEntityTypes, entityId: string, taskBundle: TaskBundle): Observable<void> {
    const url = `${apiEndpoint}/${entityType.toLowerCase()}/${entityId}/addtaskbundle`
    return this.apiClient
      .post<void>(url, {
        taskBundleId: taskBundle.id
      })
      .pipe(
        tap(() => {
          this.bundleImported.next({
            taskBundle,
            entityType,
            entityId
          })
        })
      )
  }

  /**
   * Copy Task Bundle
   * @param {TaskBundle} taskBundle
   * @returns {Observable<TaskBundle>}
   */
  copyTaskBundle(taskBundle: TaskBundle): Observable<TaskBundle> {
    return this.apiClient.post<TaskBundle>(`${this.bundleUrl}/${taskBundle.id}/copy`, taskBundle).pipe(
      map(copiedTaskBundle => {
        copiedTaskBundle.taskLists = taskBundle.taskLists
        copiedTaskBundle.tasks = taskBundle.tasks

        return new TaskBundle(copiedTaskBundle)
      })
    )
  }

  /**
   * Imports a taskBundle into another taskBundle. For example: for project
   * templates, there's a task bundle already, so we can use this endpoint to
   * import a taskBundle into it.
   * @param {string[]} sourceTaskBundleIds
   * @param {string} targetTaskBundleId
   * @returns {Observable<void>}
   */
  importTaskBundleIntoTaskBundle(sourceTaskBundleIds: string[], targetTaskBundleId: string): Observable<void> {
    const importBody = { sourceTaskBundleIds, targetTaskBundleId }
    return this.apiClient.post<void>(`${this.bundleUrl}/import`, importBody)
  }

  /**
   * Create Task Bundle from Project
   * @param {TaskBundle} taskBundle
   * @param {string} projectId
   * @param {Date} referenceDate
   * @returns {Observable<TaskBundle>}
   */
  createTaskBundleFromProject(taskBundle: TaskBundle, projectId: string, referenceDate?: Date): Observable<TaskBundle> {
    const taskBundlePost = {
      name: taskBundle.name,
      description: taskBundle.description,
      icon: taskBundle.icon,
      referenceDate
    }

    return this.apiClient.post<TaskBundle>(`${this.bundleUrl}/fromProject/${projectId}/`, taskBundlePost)
  }

  /**
   * Create Task Bundle from TaskListId
   * @param {TaskBundle} taskBundle
   * @param {string} taskListId
   * @param {Date} referenceDate
   * @returns {Observable<TaskBundle>}
   */
  createTaskBundleFromTaskList(
    taskBundle: TaskBundle,
    taskListId: string,
    referenceDate?: Date
  ): Observable<TaskBundle> {
    const taskBundlePost = {
      name: taskBundle.name,
      description: taskBundle.description,
      icon: taskBundle.icon,
      referenceDate
    }

    return this.apiClient.post<TaskBundle>(`${this.bundleUrl}/fromTaskList/${taskListId}/`, taskBundlePost)
  }

  /**
   * Saves the recurrence settings for a task bundle template.
   * @param {string} taskTemplateId
   * @param {TaskRecurrency} recurrency
   * @returns {Observable<void>}
   */
  setTaskBundleTaskRecurrency(taskTemplateId: string, recurrency: TaskRecurrency): Observable<void> {
    return this.apiClient.post<void>(`${this.tasksTemplatesUrl}/${taskTemplateId}/setrecurrency`, recurrency)
  }

  /**
   * Removes recurrence from a task bundle template.
   * @param {string} taskTemplateId
   * @returns {Observable<void>}
   */
  deleteTaskBundleTaskRecurrency(taskTemplateId: string): Observable<void> {
    return this.apiClient.post<void>(`${this.tasksTemplatesUrl}/${taskTemplateId}/deleterecurrency`, {})
  }

  /**
   * Fetches task bundle recurrence settings and converts the start date.
   * @param {string} taskTemplateId
   * @returns {Observable<TaskRecurrency>}
   */
  getTaskBundleTaskRecurrency(taskTemplateId: string): Observable<TaskRecurrency> {
    return this.apiClient.get<TaskRecurrency>(`${this.tasksTemplatesUrl}/${taskTemplateId}/recurrency`).pipe(
      map(recurrency => {
        if (recurrency.recurrencyStartOn) {
          recurrency.recurrencyStartOn = new Date(recurrency.recurrencyStartOn)
        }

        return recurrency
      })
    )
  }

  // ########### TASK LIST TEMPLATES ##############

  /**
   * Makes an API call to request the task list templates of the task bundle
   * @param {TaskBundle} taskBundle The task bundle whose templates are to be fetched
   * @returns {Observable<TaskListTemplate[]>} The task list template information
   */
  fetchTaskListTemplates(taskBundle: TaskBundle): Observable<TaskListTemplate[]> {
    return this.apiClient
      .get<TaskListTemplate[]>(`${this.bundleUrl}/${taskBundle.id}/tasklisttemplates`)
      .pipe(map(taskListTemplates => taskListTemplates.map(taskListTemplate => new TaskListTemplate(taskListTemplate))))
  }

  /**
   * Makes an API call to request the task list template information
   * @param {TaskBundle} taskBundle The task bundle whose template is to be fetched
   * @param {string} taskListTemplateId The id of the task list template
   * @returns {Observable<TaskListTemplate>} The task list template information
   */
  fetchTaskListTemplate(taskBundle: TaskBundle, taskListTemplateId: string): Observable<TaskListTemplate> {
    return this.apiClient.get<TaskListTemplate>(
      `${this.bundleUrl}/${taskBundle.id}/tasklisttemplates/${taskListTemplateId}`
    )
  }

  /**
   * Makes an API call to create a task list template
   * @param {TaskBundle} taskBundle The task bundle that the task list template is part of
   * @param {TaskListTemplate} taskListTemplate The task list template
   * @returns {Observable<TaskListTemplate>} The task list template information
   */
  sendTaskListTemplate(taskBundle: TaskBundle, taskListTemplate: TaskListTemplate): Observable<TaskListTemplate> {
    const taskListTemplatePost = {
      name: taskListTemplate.name,
      order: taskListTemplate.order
    }

    return this.apiClient.post<TaskListTemplate>(
      `${this.bundleUrl}/${taskBundle.id}/tasklisttemplates`,
      taskListTemplatePost
    )
  }

  /**
   * Makes an API call to update the task list template
   * @param {TaskBundle} taskBundle The task bundle that the task list template is part of
   * @param {TaskListTemplate} taskListTemplate Task list template to be updated
   * @returns {Observable<TaskListTemplate>} The task list template information
   */
  updateTaskListTemplate(taskBundle: TaskBundle, taskListTemplate: TaskListTemplate): Observable<TaskListTemplate> {
    const taskListTemplatePut: TaskListTemplatePut = {
      name: taskListTemplate.name,
      order: taskListTemplate.order
    }

    taskListTemplatePut.isHiddenForConnectUsers = taskListTemplate.isHiddenForConnectUsers

    return this.apiClient.put<TaskListTemplate>(
      `${this.bundleUrl}/${taskBundle.id}/tasklisttemplates/${taskListTemplate.id}`,
      taskListTemplatePut
    )
  }

  /**
   * Makes an API call to delete the task list template
   * @param {TaskBundle} taskBundle The task bundle that the task list template is part of
   * @param {TaskListTemplate} taskListTemplate The task list template to be deleted
   * @returns {Observable<string>}
   */
  deleteTaskListTemplate(taskBundle: TaskBundle, taskListTemplate: TaskListTemplate): Observable<string> {
    return this.apiClient.delete(`${this.bundleUrl}/${taskBundle.id}/tasklisttemplates/${taskListTemplate.id}`)
  }

  // ########### TASK TEMPLATES ##############

  /**
   * Makes an API call to request the task templates of the task bundle
   * @param {TaskBundle} taskBundle The task bundle for which the task templates need to be fetched
   * @param {QueryParams} queryParams
   * @returns {Observable<TaskTemplate[]>} The task template information
   */
  fetchTaskTemplates(taskBundle: TaskBundle, queryParams?: QueryParams): Observable<TaskTemplate[]> {
    if (!queryParams) {
      queryParams = {}
    }

    // If no page size is specified, set it to 1500 (no pagination used)
    if (!queryParams.pageSize) {
      queryParams.pageSize = 5000
    }

    const params: HttpParams = ApiClient.getQueryParams(queryParams)

    return this.apiClient
      .get<TaskTemplate[]>(`${this.bundleUrl}/${taskBundle.id}/tasktemplates`, {
        params
      })
      .pipe(map(taskTemplates => taskTemplates.map(t => new TaskTemplate(t))))
  }

  /**
   * Makes an API call to request the task template information
   * @param {TaskBundle} taskBundle The task bundle for which the task templates need to be fetched
   * @param {string} taskTemplateId The id of the task template
   * @returns {Observable<TaskTemplate>} The task template information
   */
  fetchTaskTemplate(taskBundle: TaskBundle, taskTemplateId: string): Observable<TaskTemplate> {
    return this.apiClient.get<TaskTemplate>(`${this.bundleUrl}/${taskBundle.id}/tasktemplates/${taskTemplateId}`)
  }

  /**
   * Makes an API call to create a task template
   * @param {TaskBundle} taskBundle The task bundle that the new task template should be part of
   * @param {TaskTemplate} taskTemplate The new task template
   * @returns {Observable<TaskTemplate>} The task template information
   */
  sendTaskTemplate(taskBundle: TaskBundle, taskTemplate: TaskTemplate): Observable<TaskTemplate> {
    const taskTemplatePost = {
      name: taskTemplate.name,
      description: trimRichText(taskTemplate.description),
      isPrio: taskTemplate.isPrio,
      plannedDuration: taskTemplate.plannedDuration || 0,
      relativeStartOn: taskTemplate.relativeStartOn,
      relativeDueOn: taskTemplate.relativeDueOn,
      typeOfWorkId: taskTemplate.typeOfWorkId,
      assignedRoleId: taskTemplate.assignedRoleId,
      parentId: taskTemplate.parentId
    }

    // Save the type to the last used entity type setting
    if (taskTemplate.typeOfWorkId) {
      this.userSettingsService.setLastUsedWorkTypeSetting(taskTemplate.typeOfWorkId, 'task')
    }

    return this.apiClient.post<TaskTemplate>(`${this.bundleUrl}/${taskBundle.id}/tasktemplates`, taskTemplatePost)
  }

  /**
   * Copies a task template including all subentities
   * @param {TaskTemplate} taskTemplate The task template to be copied
   * @returns {Observable<TaskTemplate>} The copied task template
   */
  copyTaskTemplate(taskTemplate: TaskTemplate): Observable<TaskTemplate> {
    return this.apiClient
      .post<TaskTemplate>(`${this.tasksTemplatesUrl}/${taskTemplate.id}/copy`, {})
      .pipe(map(newTaskTemplate => new TaskTemplate(newTaskTemplate)))
  }

  /**
   * Makes an API call to update the task template
   * @param {TaskBundle} taskBundle The task bundle that the new task template should be part of
   * @param {TaskTemplate} taskTemplate Task template to be updated
   * @returns {Observable<TaskTemplate>} The task template information
   */
  updateTaskTemplate(taskBundle: TaskBundle, taskTemplate: TaskTemplate): Observable<TaskTemplate> {
    const taskTemplatePut = {
      name: taskTemplate.name,
      description: trimRichText(taskTemplate.description),
      isPrio: taskTemplate.isPrio,
      plannedDuration: taskTemplate.plannedDuration || 0,
      relativeStartOn: taskTemplate.relativeStartOn,
      relativeDueOn: taskTemplate.relativeDueOn,
      typeOfWorkId: taskTemplate.typeOfWorkId,
      assignedRoleId: taskTemplate.assignedRoleId,
      subtaskOrder: taskTemplate.subtaskOrder
    }

    return this.apiClient.put<TaskTemplate>(
      `${this.bundleUrl}/${taskBundle.id}/tasktemplates/${taskTemplate.id}`,
      taskTemplatePut
    )
  }

  /**
   * Makes an API call to delete the task template
   * @param {TaskBundle} taskBundle The task bundle that the task templates are a part of
   * @param {TaskTemplate[]} taskTemplates The task templates to be deleted
   * @returns {Observable<string[]>}
   */
  deleteTaskTemplates(taskBundle: TaskBundle, taskTemplates: TaskTemplate[]): Observable<string[]> {
    const calls$: Observable<string>[] = []

    taskTemplates.forEach(taskTemplate =>
      calls$.push(this.apiClient.delete(`${this.bundleUrl}/${taskBundle.id}/tasktemplates/${taskTemplate.id}`))
    )

    return forkJoin(calls$)
  }

  /**
   * Makes an API call to update the orderBy of the task template in a task list
   * @param {TaskBundle} taskBundle The task bundle that the task template is part of
   * @param {TaskListTemplate} taskListTemplate The task list template that the task template is part of
   * @param {TaskTemplate} taskTemplate The task template whose order is to be updated
   * @param {number} order The new order of the task template
   * @returns {Observable<string>}
   */
  updateTaskTemplateOrderInList(
    taskBundle: TaskBundle,
    taskListTemplate: TaskListTemplate,
    taskTemplate: TaskTemplate,
    order: number
  ): Observable<string> {
    return this.apiClient.post<string>(
      `${this.bundleUrl}/${taskBundle.id}/tasklisttemplates/${taskListTemplate.id}/tasktemplates/${taskTemplate.id}/updateorder`,
      {
        order
      }
    )
  }

  // ########### TASK LIST TASK TEMPLATES ##############

  /**
   * Makes an API call to request the task templates of the task list
   * @param {TaskBundle} taskBundle The task bundle that the task list template is part of
   * @param {TaskListTemplate} taskListTemplate The task list template for which the task templates need to be fetched
   * @returns {Observable<TaskTemplate[]>} The task template information
   */
  fetchTaskListTaskTemplates(taskBundle: TaskBundle, taskListTemplate: TaskListTemplate): Observable<TaskTemplate[]> {
    return this.apiClient.get<TaskTemplate[]>(
      `${this.bundleUrl}/${taskBundle.id}/tasklisttemplates/${taskListTemplate.id}/tasktemplates`
    )
  }

  /**
   * Makes an API call to request the task template of the task list
   * @param {TaskBundle} taskBundle The task bundle that the task list template is part of
   * @param {TaskListTemplate} taskListTemplate The task list template for which the task template need to be fetched
   * @param {string} taskTemplateId The id of the task template whose information needs to be fetched
   * @returns {Observable<TaskTemplate>} The task template information
   */
  fetchTaskListTaskTemplate(
    taskBundle: TaskBundle,
    taskListTemplate: TaskListTemplate,
    taskTemplateId: string
  ): Observable<TaskTemplate> {
    return this.apiClient.get<TaskTemplate>(
      `${this.bundleUrl}/${taskBundle.id}/tasklisttemplates/${taskListTemplate.id}/tasktemplates/${taskTemplateId}`
    )
  }

  /**
   * Makes an API call to add task templates to a task list template
   * @param {TaskBundle} taskBundle The task bundle that the task list template is part of
   * @param {TaskListTemplate} taskListTemplate The task list template to which the task templates need to be added
   * @param {TaskTemplate[]} taskTemplates The task templates that needs to be added
   * @returns {Observable<{ id: string; order: number; }[]>}
   */
  addTaskTemplates(
    taskBundle: TaskBundle,
    taskListTemplate: TaskListTemplate,
    taskTemplates: TaskTemplate[]
  ): Observable<{ id: string; order: number }[]> {
    const taskTemplateData: { taskTemplateId: string; order?: number }[] = taskTemplates.map(taskTemplate => {
      const taskList = taskTemplate.lists.find(list => list.id === taskListTemplate.id)
      const order = taskList ? taskList.orderOfTask : undefined

      return { taskTemplateId: taskTemplate.id, order }
    })

    return this.apiClient
      .post<{ id: string; order: number }[]>(
        `${this.bundleUrl}/${taskBundle.id}/tasklisttemplates/${taskListTemplate.id}/addtasktemplates`,
        taskTemplateData
      )
      .pipe(
        tap(addOperations => {
          taskTemplates.forEach(taskTemplate => {
            const taskList = taskTemplate.lists.find(list => list.id === taskListTemplate.id)
            const addOperation = addOperations.find(operation => operation.id === taskTemplate.id)

            // Update the task order within the list
            if (taskList && addOperation) {
              taskList.orderOfTask = addOperation.order
            }
          })
        })
      )
  }

  /**
   * Makes an API call to remove task templates to a task list template
   * @param {TaskBundle} taskBundle The task bundle that the task list template is part of
   * @param {TaskListTemplate} taskListTemplate The task list template from which the task templates are to be removed
   * @param {TaskTemplate[]} taskTemplates The task templates that needs to be removed
   * @returns {Observable<string>}
   */
  removeTaskTemplates(
    taskBundle: TaskBundle,
    taskListTemplate: TaskListTemplate,
    taskTemplates: TaskTemplate[]
  ): Observable<string> {
    const taskTemplateIds: string[] = []

    taskTemplates.forEach(taskTemplate => taskTemplateIds.push(taskTemplate.id))

    return this.apiClient.post<string>(
      `${this.bundleUrl}/${taskBundle.id}/tasklisttemplates/${taskListTemplate.id}/removetasktemplates`,
      taskTemplateIds
    )
  }

  // ########### TASK TEMPLATE DEPENDENCIES ##############

  /**
   * Makes an API call to request the task template dependencies where the task
   * template is either the successor or the predecessor of the dependency
   * @param {string} taskTemplateId The id of the task template
   * @param {'predecessor' | 'successor'} filter Filter dependencies where the template is the successor or the predecessor
   * @returns {Observable<TaskTemplateDependency[]>} The task template dependencies
   */
  fetchTaskTemplateDependencies(
    taskTemplateId: string,
    filter?: 'predecessor' | 'successor'
  ): Observable<TaskTemplateDependency[]> {
    return this.apiClient
      .get<TaskTemplateDependency[]>(`${this.tasksTemplatesUrl}/${taskTemplateId}/taskdependencytemplates`)
      .pipe(
        map(dependencies => {
          if (filter === 'successor') {
            return dependencies.filter(dependency => dependency.successorTemplateId === taskTemplateId)
          }

          if (filter === 'predecessor') {
            return dependencies.filter(dependency => dependency.predecessorTemplateId === taskTemplateId)
          }

          return dependencies
        })
      )
  }

  /**
   * Makes an API call to create a task template dependency
   * @param {string} taskBundleId The task bundle id
   * @param {string} predecessorTemplateId The id of the predecessor task template
   * @param {string} successorTemplateId The id of the successor task template
   * @returns {Observable<TaskTemplateDependency>} The task template dependency
   */
  sendTaskTemplateDependency(
    taskBundleId: string,
    predecessorTemplateId: string,
    successorTemplateId: string
  ): Observable<TaskTemplateDependency> {
    const dependencyPost = {
      predecessorTemplateId,
      successorTemplateId
    }

    return this.apiClient.post<TaskTemplateDependency>(
      `${this.bundleUrl}/${taskBundleId}/taskdependencytemplates`,
      dependencyPost
    )
  }

  /**
   * Makes an API call to create a task template dependency
   * @param {string} taskBundleId The task bundle id
   * @param {string} taskDependencyTemplateId The id of the task template dependency
   * @returns {Observable<void>}
   */
  deleteTaskTemplateDependency(taskBundleId: string, taskDependencyTemplateId: string): Observable<void> {
    return this.apiClient.delete<void>(
      `${this.bundleUrl}/${taskBundleId}/taskdependencytemplates/${taskDependencyTemplateId}`
    )
  }

  /**
   * Updates the dependencies for the task template on the API
   * @param {string} taskBundleId
   * @param {string} taskTemplateId
   * @param {TaskTemplate[]} selectedTaskTemplates
   * @param {TaskTemplateDependency[]} currentDependencies
   * @returns {Observable<(void | TaskTemplateDependency)[]>}
   */
  updateTaskTemplateDependencies(
    taskBundleId: string,
    taskTemplateId: string,
    selectedTaskTemplates: TaskTemplate[],
    currentDependencies: TaskTemplateDependency[]
  ): Observable<(void | TaskTemplateDependency)[]> {
    const calls$: Observable<void | TaskTemplateDependency>[] = []

    // Remove the dependencies that are no longer selected
    currentDependencies?.forEach(dependency => {
      const template = selectedTaskTemplates.find(({ id }) => dependency.predecessorTemplateId === id)

      if (!template) {
        calls$.push(this.deleteTaskTemplateDependency(taskBundleId, dependency.id))
      }
    })

    // Add the dependencies that are newly selected
    selectedTaskTemplates?.forEach(({ id }) => {
      const dependency = currentDependencies?.find(({ predecessorTemplateId }) => predecessorTemplateId === id)

      if (!dependency) {
        calls$.push(this.sendTaskTemplateDependency(taskBundleId, id, taskTemplateId))
      }
    })

    return calls$.length ? forkJoin(calls$) : of([])
  }

  // ########### TASK BUNDLE ASSIGNMENTS ##############

  /**
   * Makes an API call to get the assignments of a task bundle
   * @param {TaskBundle} taskBundle The task bundle for which the assignments needs to be fetched
   * @returns {Observable<TaskBundleAssignment[]>}
   */
  fetchTaskBundleAssigments(taskBundle: TaskBundle): Observable<TaskBundleAssignment[]> {
    return this.apiClient.get<TaskBundleAssignmentResponse[]>(`${this.bundleUrl}/${taskBundle.id}/projecttypes`).pipe(
      // Convert TaskBundleAssignmentResponse to TaskBundleAssignment
      map((response: TaskBundleAssignmentResponse[]) => {
        const assignments: TaskBundleAssignment[] = []

        response.forEach(resp => {
          if (resp.onCreation) {
            assignments.push(
              new TaskBundleAssignment({
                taskBundle,
                projectType: {
                  id: resp.id,
                  name: resp.name,
                  icon: resp.icon
                }
              })
            )
          }

          if (resp.projectStatuses) {
            resp.projectStatuses.forEach(status => {
              assignments.push(
                new TaskBundleAssignment({
                  taskBundle,
                  projectType: {
                    id: resp.id,
                    name: resp.name,
                    icon: resp.icon
                  },
                  projectStatus: {
                    id: status.id,
                    name: status.name,
                    type: status.type as ProjectStatusType
                  }
                })
              )
            })
          }
        })

        return assignments
      })
    )
  }

  // ########### GLOBAL TASK SMART LISTS ##############

  /**
   * Makes an API call to request the global task smart lists
   * @param {QueryParams} queryParams
   * @returns {Observable<TaskViewList[]>} The task smart lists
   */
  fetchTaskViewLists(queryParams?: QueryParams): Observable<TaskViewList[]> {
    const params: HttpParams = ApiClient.getQueryParams(queryParams)
    return this.apiClient
      .get<HttpResponse<TaskViewList[]>>(`${this.viewTaskListUrl}`, {
        params,
        observe: 'response' // To get the full response and get access to the headers (pagination info)
      })
      .pipe(
        map(response => {
          return response.body.map(smartList => new TaskViewList(smartList))
        })
      )
  }

  /**
   * Makes an API call to request a global task smart list information
   * @param {string} taskViewListId The id of the task smart list
   * @returns {Observable<TaskViewList>} The task smart list information
   */
  fetchTaskViewList(taskViewListId: string): Observable<TaskViewList> {
    return this.apiClient.get<TaskViewList>(`${this.privateTaskViewListUrl}/${taskViewListId}`).pipe(
      tap(taskView => {
        this.taskViewStore.upsert(taskViewListId, taskView)
      })
    )
  }

  /**
   * Gets the task view from the store and/or the API
   * @param {Injector} injector
   * @param {string} taskViewListId
   * @returns {Observable<TaskViewList>}
   * @deprecated
   */
  getTaskViewList(injector: Injector, taskViewListId: string): Observable<TaskViewList> {
    let fetched = false

    return this.taskViewQuery.selectTaskView(injector, taskViewListId).pipe(
      mergeMap(taskView => {
        if (taskView) {
          if (!fetched) {
            fetched = true
            this.fetchTaskViewList(taskViewListId).subscribe()
          }
        } else {
          fetched = true

          return this.fetchTaskViewList(taskViewListId)
        }

        return of(taskView)
      })
    )
  }

  /**
   * Makes an API call to subscribe the current user to a task smart list
   * @param {TaskViewList} taskViewList
   * @returns {Observable<string>}
   */
  subscribeToTaskViewList(taskViewList: TaskViewList): Observable<string> {
    const body = {
      taskViewId: taskViewList.id
    }

    this.taskViewStore.add(taskViewList)
    this.settingsStore.updateTaskViewListsSettings({ id: taskViewList.id })

    return this.apiClient.post(`${this.privateTaskViewListUrl}/subscribe`, body)
  }

  /**
   * Makes an API call to unsubscribe the current user from a task smart list
   * @param {string} taskViewListId The id of the task smart list
   * @returns {Observable<void>}
   */
  unsubscribeFromTaskViewList(taskViewListId: string): Observable<void> {
    const body = {
      taskViewId: taskViewListId
    }
    return this.apiClient
      .post(`${this.privateTaskViewListUrl}/unsubscribe`, body)
      .pipe(map(() => this.taskViewStore.remove(taskViewListId)))
  }

  /**
   * Makes an API call to delete a task smart list
   * @param {TaskViewList} taskViewList The task smart list to be deleted
   * @returns {Observable<void>}
   */
  deleteTaskViewList(taskViewList: TaskViewList): Observable<void> {
    return this.apiClient
      .delete(`${this.privateTaskViewListUrl}/${taskViewList.id}`)
      .pipe(map(() => this.taskViewStore.remove(taskViewList.id)))
  }

  // ########### PRIVATE TASK SMART LISTS ##############

  /**
   * Makes an API call to fetch the private task views and the shared ones the user is subscribed to
   * @param {boolean} [setStore=true]
   * @returns {Observable<TaskViewList[]>} The task views
   */
  fetchUserTaskViewLists(setStore = true): Observable<TaskViewList[]> {
    return this.apiClient.get<TaskViewList[]>(`${this.privateTaskViewListUrl}`).pipe(
      map(taskViewLists => {
        taskViewLists = taskViewLists.map(task => new TaskViewList(task))

        if (setStore) {
          const allTaskViews = this.taskViewQuery.queryAllTaskViews()

          this.taskViewStore.sync(taskViewLists, allTaskViews())
        }

        return taskViewLists
      })
    )
  }

  /**
   * Makes an API call to request a private task smart list information
   * @param {string} taskViewListId The id of the private task smart list
   * @returns {Observable<TaskViewList>} The private task smart list information
   */
  fetchPrivateTaskViewList(taskViewListId: string): Observable<TaskViewList> {
    return this.apiClient
      .get<TaskViewList>(`${this.privateTaskViewListUrl}/${taskViewListId}`)
      .pipe(map(list => new TaskViewList(list)))
  }

  /**
   * Makes an API call to create a private task smart list
   * @param {TaskViewList} taskViewList The private task smart list to be created
   * @returns {Observable<TaskViewList>} The newly created private task smart list
   */
  sendPrivateTaskViewList(taskViewList: TaskViewList): Observable<TaskViewList> {
    return this.apiClient
      .post<TaskViewList>(`${this.privateTaskViewListUrl}`, {
        name: taskViewList.name,
        filter: taskViewList.filter,
        filterSettings: taskViewList.filterSettings,
        isShared: taskViewList.isShared
      })
      .pipe(
        map(taskView => {
          const newTaskView = new TaskViewList(taskView)

          this.taskViewStore.add(newTaskView)

          return newTaskView
        })
      )
  }

  /**
   * Makes an API call to update a private task smart list
   * @param {TaskViewList} taskSmartList Private task smart list to be updated
   * @returns {Observable<TaskViewList>} Updated private task smart list
   */
  updatePrivateTaskViewList(taskSmartList: TaskViewList): Observable<TaskViewList> {
    return this.apiClient
      .put<TaskViewList>(`${this.privateTaskViewListUrl}/${taskSmartList.id}/`, {
        name: taskSmartList.name,
        filter: taskSmartList.filter,
        filterSettings: taskSmartList.filterSettings,
        isShared: taskSmartList.isShared
      })
      .pipe(
        map(taskView => {
          const updatedTaskView = new TaskViewList(taskView)

          this.taskViewStore.upsert(taskView.id, updatedTaskView)

          return updatedTaskView
        })
      )
  }

  // ########### TASK SMART LIST TASKS ##############

  /**
   * Makes an API call to return all the project tasks for that smart list
   * @param {string} taskViewListId The id of the task smart list
   * @param {boolean} [forSync=false] True if the fetch is only for store sync purposes (Ex.: Dashboard)
   * @param {QueryParams} queryParams Includes any query params to be sent to the API
   * @param {boolean} [includeSubtasks=false] True if all subtasks should be returns regardless of the filters
   * @returns {Observable<Task[]>}
   */
  getTasksForTaskViewList(
    taskViewListId: string,
    forSync = false,
    queryParams?: QueryParams,
    includeSubtasks = false
  ): Observable<Task[]> {
    if (!queryParams) {
      queryParams = {}
    }

    if (!queryParams.pageSize) {
      queryParams.pageSize = 5000
    }

    const params: HttpParams = ApiClient.getQueryParams(queryParams)
    const requestKey = this.getTaskViewTasksRequestKey(taskViewListId, params, includeSubtasks)
    const pendingRequest = this.pendingTaskViewTaskRequests.get(requestKey)

    if (forSync && pendingRequest) {
      return pendingRequest
    }

    const request$ = this.apiClient
      .getAll<Task[]>(`${this.privateTaskViewListUrl}/${taskViewListId}/tasks`, { params })
      .pipe(
        map(tasks => {
          tasks = tasks.map(task => Task.mapTask(task))
          this.taskViewStore.update(taskViewListId, { taskIds: tasks.map(task => task.id) })

          const allTasks = this.taskViewQuery.queryAllTasks(taskViewListId)

          this.taskStore.sync(tasks, allTasks())

          return tasks
        }),
        switchMap(tasks => {
          if (includeSubtasks) {
            return this.fetchMissingSubtasks(tasks).pipe(
              map(allTasks => {
                this.taskViewStore.update(taskViewListId, { taskIds: allTasks.map(task => task.id) })

                return allTasks
              })
            )
          }

          return of(tasks)
        }),
        finalize(() => this.pendingTaskViewTaskRequests.delete(requestKey)),
        shareReplay({ bufferSize: 1, refCount: true })
      )

    if (forSync) {
      this.pendingTaskViewTaskRequests.set(requestKey, request$)
    }

    return request$
  }

  /**
   * Creates a stable key for task view task requests so identical in-flight sync calls can share a response.
   * @param {string} taskViewListId
   * @param {HttpParams} params
   * @param {boolean} includeSubtasks
   * @returns {string}
   */
  private getTaskViewTasksRequestKey(taskViewListId: string, params: HttpParams, includeSubtasks: boolean): string {
    return JSON.stringify({
      taskViewListId,
      params: params.toString(),
      includeSubtasks
    })
  }

  /**
   * Fetches subtasks which are excluded from the initial API request as they don't comply with the filter options
   * @param {Task[]} fetchedTasks
   * @param {boolean} [syncStore=true]
   * @returns {Observable<Task[]>}
   */
  fetchMissingSubtasks(fetchedTasks: Task[], syncStore = true): Observable<Task[]> {
    const { subtasksByParentMap } = getFlattenItemsByGrouping(fetchedTasks, {
      showAllDoneTasks: true,
      hideOrphanSubtask: false
    })
    const tasksWithMissingSubtasks: Task[] = []

    subtasksByParentMap.forEach(subtasksByParent => {
      if (subtasksByParent.parent?.numberOfSubtasks > subtasksByParent.subtasks.length) {
        tasksWithMissingSubtasks.push(subtasksByParent.parent)
      }
    })

    if (!tasksWithMissingSubtasks.length) {
      return of(fetchedTasks)
    }

    const queryParamsFn = (taskIds: string[]) => ({
      filterBy: taskIds.map(taskId => `parentId eq guid'${taskId}'`).join(' or ')
    })
    const callFn = (queryParams: QueryParams) => this.fetchAllTasks(queryParams)

    return this.apiClient
      .getBatch(
        tasksWithMissingSubtasks.map(task => task.id),
        queryParamsFn,
        callFn
      )
      .pipe(
        map(subtasks => {
          subtasks = subtasks.map(task => Task.mapTask(task))

          if (syncStore) {
            const tasksWithMissingSubtasksIds = tasksWithMissingSubtasks.map(t => t.id)
            const existingSubtasks = this.taskQuery.queryByParentIds(tasksWithMissingSubtasksIds)

            this.taskStore.sync(subtasks, existingSubtasks())
          }

          return uniqueBy([...fetchedTasks, ...subtasks], 'id')
        })
      )
  }

  // ########### TASK TAGS ##############

  /**
   * Makes an API call to get the tags that the current user can see
   * @param {boolean} isProjectTask
   *   Returns tags of different types of tasks. - true: All tags of all project tasks - false: All tags from all private
   *   tasks - undefined: Tags from all assigned tasks
   * @param {boolean} [useCache=true]
   * @returns {Observable<Tag[]>}
   */
  fetchTags(isProjectTask?: boolean, useCache = true): Observable<Tag[]> {
    let url: string
    let tags: Tag[]

    if (isProjectTask === undefined) {
      url = `${this.meUrl}/tags`
    } else if (isProjectTask) {
      url = `${this.projectTaskUrl}/tags`
      tags = this.projectTasksTagsCache
    } else {
      url = `${this.privateTaskUrl}/tags`
      tags = this.privateTagsCache
    }

    if (useCache && tags) {
      return of(tags)
    }

    return this.apiClient.get<Tag[]>(url).pipe(
      // TODO: Remove mapping when migration is done
      map(tags => {
        const mappedTags = typeof tags?.[0] === 'string' ? tags.map(tag => ({ name: tag as unknown as string })) : tags

        if (isProjectTask) {
          this.projectTasksTagsCache = mappedTags
        } else if (isProjectTask === false) {
          this.privateTagsCache = mappedTags
        }

        return mappedTags
      })
    )
  }

  /**
   * Fetches tags last used for current user
   * @param {number} [numberOfResults=20]
   * @param {boolean} [useCache=true]
   * @returns {Observable<Tag[]>}
   */
  fetchLastUsedTags(numberOfResults = 20, useCache = true): Observable<Tag[]> {
    if (useCache && this.lastUsedByUserCache) {
      return of(this.lastUsedByUserCache)
    }

    return this.apiClient.get<Tag[]>(`${apiEndpoint}/me/tasktags/lastused?numberOfResults=${numberOfResults}`).pipe(
      tap(tags => {
        this.lastUsedByUserCache = tags
      })
    )
  }

  /**
   * Fetches tags used in tasks of a given project
   * @param {string} projectId
   * @param {boolean} [useCache=true]
   * @returns {Observable<Tag[]>}
   */
  fetchUsedTagsForProject(projectId: string, useCache = true): Observable<Tag[]> {
    const cacheTags = this.tagsByProjectCache.get(projectId)

    if (useCache && cacheTags) {
      return of(cacheTags)
    }

    return this.apiClient.get<Tag[]>(`${apiEndpoint}/projects/projecttasks/tags?projectId=${projectId}`).pipe(
      tap(tags => {
        this.tagsByProjectCache.set(projectId, tags)
      })
    )
  }

  /**
   * Makes an API call to get the task's tags
   * @param {ValidTaskBaseTypes} baseType The base type of the task
   * @param {string} entityId The id of the entity
   * @param {string} taskId The id of the task
   * @returns {Observable<Tag[]>}
   */
  fetchTaskTags(baseType: ValidTaskBaseTypes, entityId: string, taskId: string): Observable<Tag[]> {
    const url: string = this.getTaskUpdateUrl(taskId)
    return this.apiClient.get<Tag[]>(`${url}/tags`).pipe(
      // TODO: Remove mapping when migration is done
      map(tags => {
        if (typeof tags?.[0] === 'string') {
          return tags.map(tag => ({ name: tag as unknown as string }))
        } else {
          return tags
        }
      })
    )
  }

  /**
   * Makes API calls to add/delete tags
   * @param {TagOwner} owner The task to be updated
   * @param {Tag[]} tags The task's tags
   * @param {Tag[]} addedTags The tags to be added to the task
   * @param {Tag[]} removedTags The tags to be deleted from the task
   * @returns {Observable<Tag[]>}
   */
  manageTags(owner: TagOwner, tags: Tag[], addedTags: Tag[], removedTags: Tag[]): Observable<Tag[]> {
    const calls$: Observable<Tag | void>[] = []
    const task = owner as Task

    if (addedTags.length) {
      calls$.push(this.addTags(task.id, addedTags))
    }

    if (removedTags.length) {
      calls$.push(this.deleteTags(task.baseType, task.entityId, task.id, removedTags))
    }

    return forkJoin(calls$).pipe(
      map(() => {
        this.taskStore.update(task.id, { tags })

        this.invalidateTagCaches()

        return tags
      })
    )
  }

  /**
   * Invalidates all tag caches, this should be used when this data is not longer valid. E.g, navigating or when saving new tags
   */
  invalidateTagCaches(): void {
    this.tagsByProjectCache = new Map<string, Tag[]>()
    this.privateTagsCache = null
    this.projectTasksTagsCache = null
    this.lastUsedByUserCache = null
  }

  /**
   * Fetches the tags sets shown in the popup
   * @param {Task | AutomationTask} task
   * @param {{ lastUsedByYou: string; usedInProject: string; }} labels
   * @param labels.lastUsedByYou
   * @param labels.usedInProject
   * @returns {Observable<{ all: Tag[]; tagSets: TagsSet[]; }>}
   */
  fetchPopupTags(
    task: Task | AutomationTask,
    labels: { lastUsedByYou: string; usedInProject: string }
  ): Observable<{ all: Tag[]; tagSets: TagsSet[] }> {
    const tagSets: TagsSet[] = []
    const call$ = [this.fetchTags(!task.isPrivate)]

    const projectId = task.projectId || (task as Task).project?.id

    if (projectId) {
      // Last used tags are based on all tags used in projects by user
      call$.push(this.fetchLastUsedTags(LAST_USED_LIMIT))

      call$.push(this.fetchUsedTagsForProject(projectId))
    }

    return forkJoin(call$).pipe(
      map(([allTags, lastUsedTags, usedInProjectTags]) => {
        if (lastUsedTags?.length) {
          tagSets.push({
            label: labels.lastUsedByYou,
            tags: sortByAsc(lastUsedTags, tag => new Date(tag.lastUsedOn)),
            limit: LAST_USED_LIMIT,
            sortProp: null
          })
        }

        if (usedInProjectTags?.length) {
          tagSets.push({
            label: labels.usedInProject,
            tags: usedInProjectTags,
            limit: MOST_USED_IN_PROJECTS_LIMIT,
            sortProp: 'usageCount'
          })
        }

        return { all: allTags, tagSets }
      })
    )
  }

  /**
   * Fetches the tags sets shown in the popup
   * @param {Task} task
   * @param {{ usedInProject: string; }} labels
   * @param labels.usedInProject
   * @returns {Observable<{ all: Tag[]; tagSets: TagsSet[]; }>}
   */
  fetchPopupTagsForExternalProject(
    task: Task,
    labels: { usedInProject: string }
  ): Observable<{ all: Tag[]; tagSets: TagsSet[] }> {
    if (!task.project?.isExternal) {
      return throwError(() => new Error('This method should only be used for external projects'))
    }

    const projectId = task.projectId || task.project?.id

    return this.fetchUsedTagsForProject(projectId).pipe(
      map(usedInProjectTags => {
        const tagSets: TagsSet[] = []

        if (usedInProjectTags?.length) {
          tagSets.push({
            label: labels.usedInProject,
            tags: usedInProjectTags,
            limit: MOST_USED_IN_PROJECTS_LIMIT,
            sortProp: 'usageCount'
          })
        }

        return { all: usedInProjectTags, tagSets }
      })
    )
  }

  /**
   * Makes an API call to add tags to the task
   * @param {string} taskId The id of the task
   * @param {Tag[]} tags The tags to be added to the task
   * @returns {Observable<Tag>}
   */
  addTags(taskId: string, tags: Tag[]): Observable<Tag> {
    const url: string = this.getTaskUpdateUrl(taskId)
    return this.apiClient.post<Tag>(`${url}/addtags`, tags, { entityName: 'task', propertyName: 'tags' })
  }

  /**
   * Makes an API call to delete tags from the task
   * @param {ValidTaskBaseTypes} baseType The base type of the task
   * @param {string} entityId The id of the entity
   * @param {string} taskId The id of the task
   * @param {Tag[]} tags The tags to be removed to the task
   * @returns {Observable<void>}
   */
  private deleteTags(baseType: ValidTaskBaseTypes, entityId: string, taskId: string, tags: Tag[]): Observable<void> {
    const url: string = this.getTaskUpdateUrl(taskId)
    return this.apiClient.post<void>(`${url}/deletetags`, tags, { entityName: 'task', propertyName: 'tags' })
  }

  /**
   * Makes an API call to delete the tag for all tasks
   * @param {Tag} tag The tag to be deleted
   * @param {boolean} [isPrivateTask=false]
   * @returns {Observable<void>}
   */
  deleteTagGlobally(tag: Tag, isPrivateTask = false): Observable<void> {
    let params = new HttpParams()

    params = params.append('isPrivateTask', isPrivateTask.toString())

    return this.apiClient.post<void>(`${this.url}/deletetags`, tag, { params })
  }

  /**
   * Makes an API call to update the tag for all tasks (project or private)
   * @param {Tag} tag
   * @param {Tag} oldTag
   * @param {boolean} [isPrivateTask=false]
   * @param {boolean} shouldMerge
   * @returns {Observable<Tag>}
   */
  updateTag(tag: Tag, oldTag: Tag, isPrivateTask = false, shouldMerge?: boolean): Observable<Tag> {
    let url = this.url

    if (isPrivateTask) {
      url = this.privateTaskUrl
    }

    return this.apiClient.post<Tag>(`${url}/updatetags`, {
      oldTagName: oldTag.name,
      newTag: tag,
      shouldMerge
    })
  }

  /**
   * Makes an API call to update the taskTemplate's tag
   * @param {Tag} tag
   * @param {Tag} oldTag
   * @param {boolean} shouldMerge
   * @returns {Observable<Tag>}
   */
  updateTaskTemplateTag(tag: Tag, oldTag: Tag, shouldMerge?: boolean): Observable<Tag> {
    return this.apiClient.post<Tag>(`${this.tasksTemplatesUrl}/updatetags`, {
      oldTagName: oldTag.name,
      newTag: tag,
      shouldMerge
    })
  }

  // ########### HELPER FUNCTIONS ##############

  /**
   * Sets the type of the task based on the base type
   * @param {ValidTaskBaseTypes} taskBaseType The base type of the task
   * @returns {ValidTaskTypes}
   */
  private setTaskType(taskBaseType: ValidTaskBaseTypes): ValidTaskTypes {
    return taskBaseType === 'private' ? 'privateTasks' : 'projectTasks'
  }

  /**
   * Returns the url based on the type of the task
   * @param {ValidTaskTypes} taskType The type of the task
   * @param {string} entityId The id of the entity
   * @returns {string} The url
   */
  private getTaskUrl(taskType: ValidTaskTypes, entityId: string): string {
    let url = `${apiEndpoint}/`

    switch (taskType) {
      case 'projectTasks':
        url += `projects/${entityId}/${taskType.toLowerCase()}`
        break
      case 'privateTasks':
        url += `me/${taskType.toLowerCase()}`
        break
      case 'assignedTasks':
        url += `users/${entityId}/${taskType.toLowerCase()}`
        break
    }

    return url
  }

  /**
   * Returns the url to create a task
   * @returns {string} The url
   */
  private getTaskCreateUrl(): string {
    return `${apiEndpoint}/tasks`
  }

  /**
   * Returns the url to copy a task
   * @param {string} taskId
   * @returns {string} The url
   */
  private getTaskCopyUrl(taskId: string): string {
    return `${apiEndpoint}/tasks/${taskId}/copy`
  }

  /**
   * Returns the url to update the task
   * @param {string} taskId The id of the task
   * @returns {string} The url
   */
  private getTaskUpdateUrl(taskId: string): string {
    return `${apiEndpoint}/tasks/${taskId}`
  }

  /**
   * Returns the task list url
   * @param {ValidEntityTypes} entityType The type of the entity
   * @param {string} entityId The id of the entity
   * @returns {string} The url
   */
  private getTaskListUrl(entityType: ValidEntityTypes, entityId: string): string {
    return `${apiEndpoint}/${entityType.toLowerCase()}/${entityId}/tasklists`
  }

  // ########### TASK COUNT ##############

  /**
   * Makes an API call to request the total count of tasks for a given filter
   * @param {ValidTaskBaseTypes} baseType
   * @param {string} entityId
   * @param {QueryParams} queryParams
   * @returns {Observable<number>}
   */
  fetchTaskCount(baseType: ValidTaskBaseTypes, entityId: string, queryParams?: QueryParams): Observable<number> {
    let params: HttpParams = ApiClient.getQueryParams(queryParams)

    params = params.append('count', 'true')

    const url: string = this.getTaskUrl(this.setTaskType(baseType), entityId)

    return this.apiClient
      .get<HttpResponse<Task[]>>(url, {
        params,
        observe: 'response' // To get the full response and get access to the headers (pagination info)
      })
      .pipe(
        map((res: HttpResponse<Task[]>) => {
          return Number(res.headers.get('aw-totalitems'))
        })
      )
  }

  /**
   * Makes an API call to request the total count of tasks for a given filter which the user can access
   * @param {QueryParams} queryParams
   * @returns {Observable<number>}
   */
  fetchTotalTaskCount(queryParams?: QueryParams): Observable<number> {
    let params: HttpParams = ApiClient.getQueryParams(queryParams)

    params = params.append('count', 'true')

    const url = `${apiEndpoint}/me/allavailabletasks`

    return this.apiClient
      .get<HttpResponse<Task[]>>(url, {
        params,
        observe: 'response' // To get the full response and get access to the headers (pagination info)
      })
      .pipe(
        map((res: HttpResponse<Task[]>) => {
          return Number(res.headers.get('aw-totalitems'))
        })
      )
  }

  // ########### CHECKLISTITEMS ##############

  /**
   * Fetches the task's checklistItems
   * @param {Task} task
   * @returns {Observable<ChecklistItems>}
   */
  fetchChecklistItems(task: Task): Observable<ChecklistItems> {
    return this.apiClient.get<ChecklistItems>(`${this.url}/${task.id}/checklistitems`).pipe(
      map(checklistItems => {
        checklistItems = checklistItems.map(item => new ChecklistItem(item))

        this.checklistItemStore.sync(checklistItems, this.checklistItemQuery.getByTask(task.id))

        return checklistItems
      })
    )
  }

  /**
   * Sends the new task's checklistItem to the API
   * @param {Task} task
   * @param {ChecklistItem} item
   * @param {boolean} [updateStore=true]
   * @returns {Observable<ChecklistItem>}
   */
  sendChecklistItem(task: Task, item: ChecklistItem, updateStore = true): Observable<ChecklistItem> {
    let operationId: string

    // Add the checklistItem optimistically
    if (updateStore) {
      operationId = this.taskStore.history.startOperation({ type: 'update', entities: [task] })

      const checklistItemCount = ++task.checklistItemsCount

      this.taskStore.update(task.id, { checklistItemsCount: checklistItemCount })
      this.checklistItemStore.add(item)
    }

    return this.apiClient
      .post<ChecklistItem>(`${this.url}/${task.id}/checklistitems`, {
        name: item.name,
        isDone: item.isDone,
        order: item.order
      })
      .pipe(
        tap(() => updateStore && this.taskStore.history.endOperation(operationId)),
        map(newItem => {
          const randomId = item.id

          Object.assign(item, newItem)
          item.isNew = false

          this.checklistItemStore.remove(randomId)
          this.checklistItemStore.add(newItem)

          return item
        }),
        catchError(error => {
          if (updateStore) {
            this.taskStore.history.undo(operationId)
          }

          return throwError(error)
        })
      )
  }

  /**
   * Updates a task's checklistItem
   * @param {Task} task
   * @param {ChecklistItem} item
   * @param {ChecklistItem[]} checklistItems
   * @param {boolean} [updateStore=true]
   * @returns {Observable<ChecklistItem>}
   */
  updateChecklistItem(
    task: Task,
    item: ChecklistItem,
    checklistItems: ChecklistItem[],
    updateStore = true
  ): Observable<ChecklistItem> {
    let operationId: string | undefined

    // Update the checklistItem optimistically
    if (updateStore) {
      operationId = this.taskStore.history.startOperation({ type: 'update', entities: [task] })
      this.updateTaskInStore(task, checklistItems)
      this.checklistItemStore.update(item.id, item)
    }

    return this.apiClient
      .put<ChecklistItem>(`${this.url}/${task.id}/checklistitems/${item.id}`, {
        name: item.name,
        isDone: item.isDone,
        order: item.order
      })
      .pipe(
        tap(() => {
          if (operationId) {
            this.taskStore.history.endOperation(operationId)
            operationId = undefined
          }
        }),
        map(updatedItem => {
          Object.assign(item, updatedItem)
          return item
        }),
        catchError(error => {
          if (operationId) {
            this.taskStore.history.undo(operationId)
            operationId = undefined
          }

          return throwError(error)
        }),
        finalize(() => {
          if (operationId) {
            this.taskStore.history.endOperation(operationId)
          }
        })
      )
  }

  /**
   * Updates the checklistItems order
   * @param {Task} task
   * @param {ChecklistItem} reorderedItem
   * @param {ChecklistItems} allChecklistItems
   */
  reorderChecklistItems(task: Task, reorderedItem: ChecklistItem, allChecklistItems: ChecklistItems): void {
    // Update the checklistItems order optimistically
    allChecklistItems.forEach(item => {
      this.checklistItemStore.update(item.id, { order: item.order })
    })

    // Add the call to the queue
    this.checklistItemOrderQueue.next(
      this.updateChecklistItem(task, reorderedItem, allChecklistItems).pipe(
        map(_ => void 0),
        catchError(error => throwError(error))
      )
    )
  }

  /**
   * Deletes a task's checklistItem
   * @param {Task} task
   * @param {ChecklistItem} item
   * @param {ChecklistItems} allChecklistItems
   * @param {boolean} [updateStore=true]
   * @returns {Observable<void>}
   */
  deleteChecklistItem(
    task: Task,
    item: ChecklistItem,
    allChecklistItems: ChecklistItems,
    updateStore = true
  ): Observable<void> {
    let operationId: string

    // Updates the checklistItem optimistically
    if (updateStore) {
      const updatedArray = arrayRemove(allChecklistItems, item.id)

      try {
        operationId = this.taskStore.history.startOperation({ type: 'update', entities: [task] })
        this.updateTaskInStore(task, updatedArray)
        this.checklistItemStore.remove(item.id)
      } catch (error) {
        this.logService.logMessage(Level.error, 'Error in arrayRemove in function: deleteChecklistItem')

        return throwError(error)
      }
    }

    return this.apiClient.delete<void>(`${this.url}/${task.id}/checklistitems/${item.id}`).pipe(
      tap(() => operationId && this.taskStore.history.endOperation(operationId)),
      catchError(error => {
        if (operationId) {
          this.taskStore.history.undo(operationId)
        }

        return throwError(error)
      })
    )
  }

  /**
   * Updates a batch of checklistItems
   * @param {Task} task
   * @param {ChecklistItems} checklistItemsToUpdate
   * @param {ChecklistItems} allChecklistItems
   * @returns {Observable<ChecklistItems>}
   */
  updateChecklistItems(
    task: Task,
    checklistItemsToUpdate: ChecklistItems,
    allChecklistItems: ChecklistItems
  ): Observable<ChecklistItems> {
    // Update the checklistItems optimistically
    const operationId = this.taskStore.history.startOperation({ type: 'update', entities: [task] })

    this.updateTaskInStore(task, allChecklistItems)

    checklistItemsToUpdate.forEach(checklistItem => {
      this.checklistItemStore.update(checklistItem.id, checklistItem)
    })

    const calls$ = checklistItemsToUpdate.map(checklistItem =>
      this.updateChecklistItem(task, checklistItem, allChecklistItems, false)
    )

    return forkJoin(calls$).pipe(
      tap(() => this.taskStore.history.endOperation(operationId)),
      map(updatedChecklistItems => {
        return updatedChecklistItems.map(checklistItem => new ChecklistItem(checklistItem))
      }),
      catchError(error => {
        this.taskStore.history.undo(operationId)
        return throwError(error)
      })
    )
  }

  /**
   * Deletes a batch of checklistItems
   * @param {Task} task
   * @param {ChecklistItems} checklistItemsToDelete
   * @param {ChecklistItems} allChecklistItems
   * @returns {Observable<ChecklistItems>}
   */
  deleteChecklistItems(
    task: Task,
    checklistItemsToDelete: ChecklistItems,
    allChecklistItems: ChecklistItems
  ): Observable<ChecklistItems> {
    const operationId = this.taskStore.history.startOperation({ type: 'update', entities: [task] })

    try {
      // Updates the checklistItem optimistically
      this.updateTaskInStore(task, allChecklistItems)

      checklistItemsToDelete.forEach(checklistItem => {
        this.checklistItemStore.remove(checklistItem.id)
      })
    } catch (error) {
      this.taskStore.history.undo(operationId)
      this.logService.logMessage(Level.error, 'Error in arrayRemove in function: deleteChecklistItem')

      return throwError(error)
    }

    const calls$ = checklistItemsToDelete.map(checklistItem =>
      this.deleteChecklistItem(task, checklistItem, allChecklistItems, false)
    )

    return forkJoin(calls$).pipe(
      tap(() => operationId && this.taskStore.history.endOperation(operationId)),
      map(() => checklistItemsToDelete),
      catchError(error => {
        this.taskStore.history.undo(operationId)
        return throwError(error)
      })
    )
  }

  /**
   * Updates task's derived properties from checklistItems
   * @param {Task} task
   * @param {ChecklistItems} checklistItems
   */
  updateTaskInStore(task: Task, checklistItems: ChecklistItems): void {
    this.taskStore.update(task.id, {
      checklistItemsCount: checklistItems.length,
      checklistItemsDoneCount: checklistItems.filter(checklistItem => checklistItem.isDone).length
    })
  }

  // ########### CHECKLISTITEM TEMPLATES ##############

  /**
   * Fetches the tasktemplates's checklistItems
   * @param {TaskTemplate} taskTemplate
   * @returns {Observable<ChecklistItemTemplates>}
   */
  fetchChecklistItemTemplates(taskTemplate: TaskTemplate): Observable<ChecklistItemTemplates> {
    return this.apiClient
      .get<ChecklistItemTemplates>(`${this.tasksTemplatesUrl}/${taskTemplate.id}/checklistitemtemplates`)
      .pipe(
        map(checklistItemTemplate => {
          return checklistItemTemplate.map(item => new ChecklistItemTemplate(item))
        })
      )
  }

  /**
   * Sends the new tasktemplate's checklistItem to the API
   * @param {TaskTemplate} taskTemplate
   * @param {ChecklistItemTemplate} itemTemplate
   * @returns {Observable<ChecklistItemTemplate>}
   */
  sendChecklistItemTemplate(
    taskTemplate: TaskTemplate,
    itemTemplate: ChecklistItemTemplate
  ): Observable<ChecklistItemTemplate> {
    return this.apiClient
      .post<ChecklistItemTemplate>(`${this.tasksTemplatesUrl}/${taskTemplate.id}/checklistitemtemplates`, {
        name: itemTemplate.name,
        order: itemTemplate.order
      })
      .pipe(
        map(newItem => {
          Object.assign(itemTemplate, newItem)
          itemTemplate.isNew = false

          return itemTemplate
        }),
        catchError(error => {
          return throwError(error)
        })
      )
  }

  /**
   * Updates a tasktemplates's checklistItem
   * @param {TaskTemplate} taskTemplate
   * @param {ChecklistItemTemplate} itemTemplate
   * @returns {Observable<ChecklistItemTemplate>}
   */
  updateChecklistItemTemplate(
    taskTemplate: TaskTemplate,
    itemTemplate: ChecklistItemTemplate
  ): Observable<ChecklistItemTemplate> {
    return this.apiClient
      .put<ChecklistItemTemplate>(
        `${this.tasksTemplatesUrl}/${taskTemplate.id}/checklistitemtemplates/${itemTemplate.id}`,
        {
          name: itemTemplate.name,
          order: itemTemplate.order
        }
      )
      .pipe(
        map(updatedItem => {
          Object.assign(itemTemplate, updatedItem)
          return itemTemplate
        }),
        catchError(error => {
          return throwError(error)
        })
      )
  }

  /**
   * Updates the checklistItemtemplates order
   * @param {TaskTemplate} taskTemplate
   * @param {ChecklistItemTemplate} reorderedItemTemplate
   */
  reorderChecklistItemsTemplate(taskTemplate: TaskTemplate, reorderedItemTemplate: ChecklistItemTemplate): void {
    // Add the call to the queue
    this.checklistItemOrderQueue.next(
      this.updateChecklistItemTemplate(taskTemplate, reorderedItemTemplate).pipe(
        map(_ => void 0),
        catchError(error => {
          return throwError(error)
        })
      )
    )
  }

  /**
   * Deletes a tasktemplate's checklistItem
   * @param {TaskTemplate} taskTemplate
   * @param {ChecklistItemTemplate} itemTemplate
   * @returns {Observable<void>}
   */
  deleteChecklistItemTemplate(taskTemplate: TaskTemplate, itemTemplate: ChecklistItemTemplate): Observable<void> {
    return this.apiClient
      .delete<void>(`${this.tasksTemplatesUrl}/${taskTemplate.id}/checklistitemtemplates/${itemTemplate.id}`)
      .pipe(
        catchError(error => {
          return throwError(error)
        })
      )
  }

  /**
   * Sets the task as in progress if is currently 'todo'
   * and assign the user to the task
   * @param {Injector} injector
   * @param {Task} task
   * @param {User} user
   */
  startTask(injector: Injector, task: Task, user: User): void {
    const canEdit = task.canEdit(
      user,
      this.permissionsService.isAllowed(Features.ProjectPlanning, AccessLevels.Manage, false, task.projectId)
    )

    // Assign the user and set the status to "in progress"
    // There fore the statuses have to be fetched to find the proper one
    if (task.taskStatus?.type === 'todo' && canEdit) {
      // fetch the task statuses from the query and/or the api
      const query = task.projectId
        ? this.taskStatusQuery.selectProjectStatuses(injector, task.projectId)
        : this.taskStatusQuery.selectPrivateStatuses(injector)

      this.fetchTaskStatuses({}, task.projectId).subscribe()

      query
        .pipe(
          filter(statuses => !!statuses.length),
          take(1)
        )
        .subscribe(statuses => {
          const statusList = statuses.map(status => new TaskStatus(status))
          const newStatus = statusList.find(status => status.type === 'progress')

          // if an "in progress" status exists, use it
          if (newStatus) {
            const previousStatus = task.taskStatus

            task.taskStatus = newStatus
            task.taskStatusId = newStatus.id
            this.updateTasksStatuses([task], previousStatus).subscribe()

            // if there is no assignee and the new status is in progress we assign the current
            // user, the checks are handled by the auto assign function.
            this.autoAssignCurrentUser(task, user)
          }
        })
    }
  }

  // ########### TASK DEPENDENCIES ##############

  /**
   * Fetches the task's dependencies (predecessors and successors)
   * @param {Task} task
   * @returns {Observable<TaskDependencyApi[]>}
   */
  fetchTaskDependencies(task: Task): Observable<TaskDependencyApi[]> {
    return this.apiClient.get<TaskDependencyApi[]>(`${this.url}/${task.id}/taskdependencies`).pipe(
      tap(taskDependencies => {
        // Set predecessor and successors of the task
        this.taskStore.update(task.id, {
          predecessors: taskDependencies
            .filter(dependency => dependency.successorId === task.id)
            .map(dependency => ({ id: dependency.id, taskId: dependency.predecessorId })),
          successors: taskDependencies
            .filter(dependency => dependency.predecessorId === task.id)
            .map(dependency => ({ id: dependency.id, taskId: dependency.successorId }))
        })
      })
    )
  }

  /**
   * Fetches all predecessors for the given tasks and also stores them in the store
   * @param {Task} task
   * @returns {Observable<Task[]>}
   */
  fetchDependedTasksForTask(task: Task): Observable<Task[]> {
    return this.fetchTaskDependencies(task).pipe(
      mergeMap(deps => {
        const predecessorIds = deps.filter(dep => dep.successorId === task.id).map(dep => dep.predecessorId)

        if (!predecessorIds.length) {
          return of([])
        } else {
          return this.fetchTasksById(predecessorIds)
        }
      })
    )
  }

  /**
   * Fetches all project tasks' dependencies (predecessors and successors)
   * @param {string} projectId
   * @returns {Observable<TaskDependencyApi[]>}
   */
  fetchProjectDependencies(projectId: string): Observable<TaskDependencyApi[]> {
    return this.apiClient.get<TaskDependencyApi[]>(`${apiEndpoint}/projects/${projectId}/taskdependencies`).pipe(
      tap(taskDependencies => {
        let taskIds: string[] = []

        taskDependencies.forEach(dependency => taskIds.push(dependency.predecessorId, dependency.successorId))
        taskIds = [...new Set(taskIds)] // Get unique task ids

        taskIds.forEach(taskId => {
          // Set predecessor and successors of the tasks
          this.taskStore.update(taskId, {
            predecessors: taskDependencies
              .filter(dependency => dependency.successorId === taskId)
              .map(dependency => ({ id: dependency.id, taskId: dependency.predecessorId })),
            successors: taskDependencies
              .filter(dependency => dependency.predecessorId === taskId)
              .map(dependency => ({ id: dependency.id, taskId: dependency.successorId }))
          })
        })
      })
    )
  }

  /**
   * Sends a task's dependency
   * @param {Task} task
   * @param {Task} successor
   * @returns {Observable<TaskDependencyApi>}
   */
  sendTaskDependency(task: Task, successor: Task): Observable<TaskDependencyApi> {
    const optimisticId = generateGUID()

    this.taskStore.update(task.id, {
      successors: arrayAdd(task.successors, { id: optimisticId, taskId: successor.id })
    })

    this.taskStore.update(successor.id, {
      predecessors: arrayAdd(successor.predecessors, { id: optimisticId, taskId: task.id })
    })

    return this.apiClient
      .post<TaskDependencyApi>(this.dependenciesUrl, {
        predecessorId: task.id,
        successorId: successor.id
      })
      .pipe(
        tap(taskDependency => {
          try {
            this.taskStore.update(task.id, {
              successors: arrayRemove(task.successors || [], optimisticId)
            })
            this.taskStore.update(task.id, {
              successors: arrayUpsert(task.successors || [], taskDependency.id, {
                id: taskDependency.id,
                taskId: taskDependency.successorId
              })
            })
            this.taskStore.update(successor.id, {
              predecessors: arrayRemove(successor.predecessors || [], optimisticId)
            })
            this.taskStore.update(successor.id, {
              predecessors: arrayUpsert(successor.predecessors || [], taskDependency.id, {
                id: taskDependency.id,
                taskId: taskDependency.predecessorId
              })
            })
          } catch (error) {
            this.logService.logMessage(Level.error, 'Error in arrayRemove in function: sendTaskDependency')
            throwError(error)
          }
        }),
        catchError(error => {
          try {
            this.taskStore.update(task.id, {
              successors: arrayRemove(task.successors || [], optimisticId)
            })
            this.taskStore.update(successor.id, {
              predecessors: arrayRemove(successor.predecessors || [], optimisticId)
            })
          } catch (error) {
            this.logService.logMessage(Level.error, 'Error in arrayRemove in function: sendTaskDependency catchError')
          }

          return throwError(error)
        })
      )
  }

  /**
   * Update dependency in store
   * @param {string} successorId
   * @param {string} predecessorId
   * @param {string} taskDependencyId
   * @param {WebSocketSignalOperation} operationType
   */
  updateDependencyInStore(
    successorId: string,
    predecessorId: string,
    taskDependencyId: string,
    operationType: WebSocketSignalOperation
  ): void {
    const successor = this.taskQuery.getTask(successorId)
    const predecessor = this.taskQuery.getTask(predecessorId)

    if (operationType === 'added') {
      if (predecessor) {
        this.taskStore.update(predecessorId, task => ({
          successors: arrayUpsert(task.successors || [], taskDependencyId, {
            id: taskDependencyId,
            taskId: successorId
          })
        }))
      }

      if (successor) {
        this.taskStore.update(successorId, task => ({
          predecessors: arrayUpsert(task.predecessors || [], taskDependencyId, {
            id: taskDependencyId,
            taskId: predecessorId
          })
        }))
      }
    } else if (operationType === 'deleted') {
      if (successor) {
        this.taskStore.update(successorId, task => ({
          predecessors: arrayRemove(task.predecessors || [], taskDependencyId)
        }))
      }

      if (predecessor) {
        this.taskStore.update(predecessorId, task => ({
          successors: arrayRemove(task.successors || [], taskDependencyId)
        }))
      }
    }

    this.taskDependenciesUpdated.next({ task: predecessor, taskDependencies: [] })
  }

  /**
   * Deletes a task's dependency
   * @param {string} dependencyId
   * @returns {Observable<void>}
   */
  // @action('Delete task dependency')
  deleteTaskDependency(dependencyId: string): Observable<void> {
    let operationId: string

    // fetch both tasks from store here and remove the deps
    const tasks = this.taskQuery.queryByDependencyId(dependencyId)

    operationId = this.taskStore.history.startOperation({ type: 'update', entities: tasks() })

    try {
      tasks().forEach(task => {
        this.taskStore.updateTask(task.id, {
          predecessors: arrayRemove(task.predecessors, dependencyId),
          successors: arrayRemove(task.successors, dependencyId)
        })
      })
    } catch (error) {
      this.taskStore.history.undo(operationId)
      operationId = null
      this.logService.logMessage(Level.error, 'Error in arrayRemove in function: deleteTaskDependency')
      throwError(error)
    }

    // make the deletion call
    return this.apiClient.delete<void>(`${this.dependenciesUrl}/${dependencyId}`).pipe(
      tap(() => operationId && this.taskStore.history.endOperation(operationId)),
      catchError(error => {
        if (operationId) {
          this.taskStore.history.undo(operationId)
        }

        return throwError(error)
      })
    )
  }

  /**
   * Makes API calls to updates the task's dependencies (add and delete)
   * @param {Task} task
   * @param {Task[]} currentTaskDependencies
   * @param {Task[]} taskDependencies
   * @returns {Observable<TaskDependencyApi[]>}
   */
  manageTaskDependency(
    task: Task,
    currentTaskDependencies: Task[],
    taskDependencies: Task[]
  ): Observable<TaskDependencyApi[]> {
    const calls$: Observable<TaskDependencyApi | void>[] = []
    const taskDependenciesApi: TaskDependencyApi[] = []

    // Get new dependencies
    taskDependencies.forEach(dependency => {
      if (!currentTaskDependencies.some(currentDependency => currentDependency.id === dependency.id)) {
        calls$.push(
          this.sendTaskDependency(dependency, task).pipe(
            map(taskDependency => {
              taskDependenciesApi.push(taskDependency)
            })
          )
        )
      }
    })

    // Get removed dependencies
    task.predecessors.forEach(predecessor => {
      if (!taskDependencies.some(dependency => dependency.id === predecessor.taskId)) {
        calls$.push(this.deleteTaskDependency(predecessor.id))
      }
    })

    if (calls$.length) {
      return forkJoin(calls$).pipe(
        map(() => {
          this.taskDependenciesUpdated.next({ task, taskDependencies })
          return taskDependenciesApi
        })
      )
    }

    return of(
      task.predecessors.map(dependency => ({
        id: dependency.id,
        predecessorId: dependency.taskId,
        successorId: task.id
      }))
    )
  }

  /**
   * Updates the task in timeline, moving it in time
   * @param {Task} task
   * @param {number} daysDelta
   * @param {boolean} [moveSuccessors=true] True to move the task's successors by the same amount of days
   * @param {boolean} [skipMovingFirst=false] To skip the moving of the first task (when just changing due on)
   * @param timezone Timezone whose wall-clock time is preserved.
   * @returns {Observable<Task[]>}
   */
  updateTimelineTask(
    task: Task,
    daysDelta: number,
    moveSuccessors = true,
    skipMovingFirst = false,
    timezone = getAccountTimezone()
  ): Observable<Task[]> {
    const calls$: Observable<Task>[] = []
    const movedTasks: string[] = []

    this.moveTimelineTask(
      task.id,
      daysDelta,
      moveSuccessors,
      calls$,
      movedTasks,
      task.laneOrder,
      skipMovingFirst ? task.dueOn : null,
      timezone
    )

    // In test environment the calls are undefined
    return forkJoin(calls$.filter(call => !!call))
  }

  /**
   * Moves the task and its successor recursively in the timeline
   * @param {string} taskId
   * @param {number} daysDelta
   * @param {boolean} moveSuccessors
   * @param {Observable<Task>[]} calls$
   * @param {string[]} movedTasks
   * @param {number} newLane
   * @param {Date} newDueOn
   * @param timezone Timezone whose wall-clock time is preserved.
   */
  private moveTimelineTask(
    taskId: string,
    daysDelta: number,
    moveSuccessors: boolean,
    calls$: Observable<Task>[],
    movedTasks: string[],
    newLane?: number,
    newDueOn?: Date,
    timezone = getAccountTimezone()
  ): void {
    if (movedTasks.includes(taskId)) {
      return
    } else {
      movedTasks.push(taskId)
    }

    const task = this.taskQuery.getTask(taskId)

    if (task) {
      if (!newDueOn) {
        if (task.startOn) {
          task.startOn = convertToUTC(addDays(convertToTimezone(task.startOn, timezone), daysDelta), timezone)
        }

        if (task.dueOn) {
          task.dueOn = convertToUTC(addDays(convertToTimezone(task.dueOn, timezone), daysDelta), timezone)
        }
      } else {
        task.dueOn = newDueOn
      }

      if (newLane !== undefined) {
        task.laneOrder = newLane
      }

      if (task.startOn || task.dueOn) {
        calls$.push(this.updateTask(task))
      }

      if (moveSuccessors) {
        task.successors.forEach(successor =>
          this.moveTimelineTask(
            successor.taskId,
            daysDelta,
            moveSuccessors,
            calls$,
            movedTasks,
            undefined,
            undefined,
            timezone
          )
        )
      }
    }
  }

  /**
   * Import Tasks
   * @param {MapTaskImport[]} importedTasks
   * @returns {Observable<void>}
   */
  importTasks(importedTasks: MapTaskImport[]): Observable<void> {
    return this.apiClient.post<void>(`${this.url}/import`, importedTasks)
  }

  /**
   * Stores a project id for deep-linking after the async import-finished notification arrives.
   * @param {string | null} projectId
   */
  setImportNavigationProjectId(projectId: string | null): void {
    this.importNavigationProjectId = projectId
  }

  /**
   * Returns the currently stored import navigation project id.
   * @returns {string | null}
   */
  getImportNavigationProjectId(): string | null {
    return this.importNavigationProjectId
  }

  /**
   * Stores a project name for deep-linking after async import when the project is newly created.
   * @param {string | null} projectName
   */
  setImportNavigationProjectName(projectName: string | null): void {
    this.importNavigationProjectName = projectName
  }

  /**
   * Returns the currently stored import navigation project name.
   * @returns {string | null}
   */
  getImportNavigationProjectName(): string | null {
    return this.importNavigationProjectName
  }

  /**
   * Stores whether import completion should navigate to the project list.
   * @param {boolean} shouldNavigate
   */
  setNavigateToProjectListAfterImport(shouldNavigate: boolean): void {
    this.navigateToProjectListAfterImport = shouldNavigate
  }

  /**
   * Returns whether import completion should navigate to the project list.
   * @returns {boolean}
   */
  getNavigateToProjectListAfterImport(): boolean {
    return this.navigateToProjectListAfterImport
  }

  // ########### TASK RECURRENCY ##############

  /**
   * Saves task recurrence settings and updates the local recurring flag.
   * @param {string} taskId
   * @param {TaskRecurrency} recurrency
   * @returns {Observable<void>}
   */
  setRecurrency(taskId: string, recurrency: TaskRecurrency): Observable<void> {
    return this.apiClient.post<void>(`${this.url}/${taskId}/setrecurrency`, recurrency).pipe(
      tap(() => {
        this.taskStore.updateTask(taskId, { isRecurring: true })
        this.recurrencyChanged.next(taskId)
      })
    )
  }

  /**
   * Fetches the next occurrence of a recurring task
   * @param {string} taskId
   * @param {TaskRecurrency} recurrency
   * @returns {Observable<{ dueOn: Date; startOn: Date; }>}
   */
  fetchNextOccurrence(taskId: string, recurrency: TaskRecurrency): Observable<{ dueOn: Date; startOn: Date }> {
    return this.apiClient.post<{ dueOn: string; startOn: string }>(`${this.url}/nextoccurrence`, recurrency).pipe(
      map(response => ({
        dueOn: response.dueOn ? new Date(response.dueOn) : null,
        startOn: response.startOn ? new Date(response.startOn) : null
      }))
    )
  }

  /**
   * Deletes the recurrency of a task
   * @param {string} taskId
   * @returns {Observable<void>}
   */
  deleteRecurrency(taskId: string): Observable<void> {
    return this.apiClient.post<void>(`${this.url}/${taskId}/deleterecurrency`, {}).pipe(
      tap(() => {
        this.taskStore.updateTask(taskId, { isRecurring: false })
        this.recurrencyChanged.next(taskId)

        // Also update tasks with createdFromTaskId
        const recurringTasksInStore = this.taskQuery.getAll({
          filterBy: task => task.createdFromTaskId === taskId
        })

        if (recurringTasksInStore.length) {
          this.taskStore.update(
            recurringTasksInStore.map(task => task.id),
            { isRecurring: false, createdFromTaskId: null }
          )
        }
      })
    )
  }

  /**
   * Fetches the recurrency rule for the given task
   * @param {string} taskId
   * @returns {Observable<TaskRecurrency>}
   */
  getRecurrencyRule(taskId: string): Observable<TaskRecurrency> {
    return this.apiClient.get<TaskRecurrency>(`${this.url}/${taskId}/recurrency`).pipe(
      map(recurrency => {
        if (recurrency.recurrencyStartOn) {
          recurrency.recurrencyStartOn = new Date(recurrency.recurrencyStartOn)
        }

        return recurrency
      })
    )
  }

  /**
   * Fetches the recurring tasks for the given taskIds and date range.
   * We split up the request to avoid the date range limit of 8 months.
   * The date range always starts from the current date as in the past the tasks are already created.
   * @param {DateRange} dateRange
   * @param {{ projectId?: string; userIds?: string[]; }} filterOptions
   * @param filterOptions.projectId
   * @param filterOptions.userIds
   * @returns {Observable<RecurringTask[]>}
   */
  getRecurringTasks(
    dateRange: DateRange,
    filterOptions: { projectId?: string; userIds?: string[] }
  ): Observable<RecurringTask[]> {
    if (!filterOptions.projectId && !filterOptions.userIds?.length) {
      return of([])
    }

    const presentDateRange: DateRange = {
      startDate: new Date(),
      endDate: dateRange.endDate
    }

    const recurringTaskCalls = getRangeSegments(presentDateRange.startDate, presentDateRange.endDate, 240).map(
      dateRange => {
        const queryParamsFn = (userIds: string[]) => {
          let filterBy = ''

          if (filterOptions.projectId) {
            filterBy = `projectId eq guid'${filterOptions.projectId}'`
          }

          if (userIds?.length) {
            filterBy += filterBy ? ' and (' : '('
            filterBy += userIds.map(userId => `assignees/any(a: a/id eq guid'${userId}')`).join(' or ') + ')'
          }

          return new HttpParams({
            fromObject: {
              startDate: this.setStartDateTimeForRecurringTasks(dateRange.startDate),
              endDate: dateToISO(dateRange.endDate, true),
              filterBy,
              ignoreRecurrenceDueDate: true
            }
          })
        }

        const callFn = (params: HttpParams) =>
          this.apiClient
            .get<RecurringTask[]>(`${this.url}/fromrecurrencies`, { params })
            .pipe(map(recurringTasks => recurringTasks.map(r => new RecurringTask(r))))

        if (filterOptions.userIds?.length) {
          // We need to make sure the recurring tasks are not duplicated for the same user
          // which can happen when users assigned to the same task are batched separately
          const compareFn = (recA: RecurringTask, recB: RecurringTask) =>
            recA.baseTaskId === recB.baseTaskId && recA.hasSameAssignees(recB.assignees)

          return this.apiClient.getBatch(filterOptions.userIds, queryParamsFn, callFn, compareFn)
        }

        return callFn(queryParamsFn(null))
      }
    )

    return forkJoin(recurringTaskCalls).pipe(
      map((recurringTasks: RecurringTask[][]) => this.mapRecurringTasks(recurringTasks.flat()))
    )
  }

  /**
   * The recurring task endpoint will return all the recurring tasks from start (datetime) to end (datetime).
   * In order to avoid fetching past recurrences, if the startDate is today we need to provide the exact date time.
   * Otherwise, return the dateToISO start date (a date only string).
   * @param {Date} startDate
   * @returns {string}
   */
  private setStartDateTimeForRecurringTasks(startDate: Date): string {
    const now = new Date()
    const isoStartDate = dateToISO(startDate, true)

    return isoStartDate === dateToISO(now, true) ? now.toISOString() : isoStartDate
  }

  /**
   * Create a recurringTask for each occurence of the response and map each to
   * one of the occurences startOn and dueOn. We needs to filter out the recurrencies
   * that have the start date before "now" as an actual task is already created for it.
   * @param {RecurringTask[]} recurrencies
   * @returns {RecurringTask[]}
   */
  private mapRecurringTasks(recurrencies: RecurringTask[]): RecurringTask[] {
    return recurrencies.reduce((recurringTasks, recurrency) => {
      if (!recurrency.occurrences?.length) {
        return recurringTasks
      }

      const occurencesFromToday = recurrency.occurrences.filter(({ startOn }) => isAfter(startOn, startOfToday()))

      occurencesFromToday.forEach(ocurrence => {
        const recurringTask = new RecurringTask({
          ...recurrency,
          id: `${recurrency.baseTaskId}_${ocurrence.startOn.getTime()}`,
          startDate: ocurrence.startOn,
          endDate: ocurrence.dueOn
        })

        recurringTasks.push(recurringTask)
      })

      return recurringTasks
    }, [])
  }

  /**
   * Fetches task statistics for the selected project.
   * @param {string} projectId
   * @returns {Observable<TaskStatistics[]>}
   */
  fetchProjectTaskStatistics(projectId: string): Observable<TaskStatistics[]> {
    return this.apiClient.get<TaskStatistics[]>(`${apiEndpoint}/projects/${projectId}/taskstatistics`)
  }

  /**
   * Fetches the billability statistics for all project tasks
   * @param {string} projectId
   * @returns {Observable<TaskStatisticsBillability[]>}
   */
  fetchProjectTaskBillabilityStatistics(projectId: string): Observable<TaskStatisticsBillability[]> {
    return this.apiClient.get<TaskStatisticsBillability[]>(
      `${apiEndpoint}/projects/${projectId}/taskstatistics/billablehours`
    )
  }

  /**
   * Sets the task scheduled property
   * @param {Task} task
   * @param {boolean} isCompletelyScheduled
   * @returns {Observable<void>}
   */
  setIsCompletelyScheduled(task: Task, isCompletelyScheduled: boolean): Observable<void> {
    task.isCompletelyScheduled = isCompletelyScheduled
    this.taskStore.updateTask(task.id, task)

    // TODO: Planner - add endpoint once is implemented
    return of()
  }

  /**
   * Updates the task's schedule properties
   * @param {TaskSchedule} taskSchedule
   * @param {'created' | 'updated' | 'deleted'} state
   * @returns {Observable<Task>}
   */
  updateTaskScheduleProperties(taskSchedule: TaskSchedule, state: 'created' | 'updated' | 'deleted'): Observable<Task> {
    const taskId = taskSchedule.taskId

    if (!taskId) {
      return of(null)
    }

    const taskToUpdate = this.taskQuery.getTask(taskId)

    if (!taskToUpdate) {
      return of(null)
    }

    switch (state) {
      case 'created': {
        const canTaskHaveMultiUsers = this.allowMultiUserAssignment && !taskToUpdate.isPrivate

        // Only add the user if multi-user assignment is allowed
        if (canTaskHaveMultiUsers && taskSchedule.user?.id && !taskToUpdate.isUserAssigned(taskSchedule.user?.id)) {
          taskToUpdate.assignees = [...taskToUpdate.assignees, taskSchedule.user]

          this.taskStore.updateTask(taskToUpdate.id, taskToUpdate)

          return this.manageAssignees(taskToUpdate)
        }

        return of(taskToUpdate)
      }
      case 'deleted':
      case 'updated':
      default:
        return of(taskToUpdate)
    }
  }

  /**
   * Fetches all tasks schedule and returns the summed duration
   * @param {Task} task
   * @returns {Observable<number>}
   */
  fetchTaskScheduleDuration(task: Task): Observable<number> {
    const params: HttpParams = ApiClient.getQueryParams({ filterBy: `taskId eq guid'${task.id}'` })

    return this.apiClient
      .get<TaskSchedule[]>(`${apiEndpoint}/taskschedules`, { params })
      .pipe(
        map(taskSchedules =>
          taskSchedules
            .map(schedule => new TaskSchedule(schedule))
            .reduce((total, schedule) => total + (schedule.duration || 0), 0)
        )
      )
  }

  /**
   * Checks if any of the assignees are guests or external and returns a list of users
   * without the guests or the externals
   * @param {User[]} assignees
   * @returns {Observable<User[]>}
   */
  filterTaskGuestAndExternalAssignees(assignees: User[]): Observable<User[]> {
    // Filter out any users that non belong to the workspace
    const workspaceUsers = assignees?.filter(assignee => !assignee.isExternal)

    if (!workspaceUsers?.length) {
      return of([])
    }

    const userRoleCalls$ = workspaceUsers.map(assignee => this.permissionsService.getRoleByUserId(assignee.id))

    return forkJoin(userRoleCalls$).pipe(
      map(userRoles => {
        if (userRoles.some(role => role.isGuestRole)) {
          const nonGuestAssignees = workspaceUsers.filter((_, index) => !userRoles[index].isGuestRole)

          return nonGuestAssignees
        }

        return workspaceUsers
      })
    )
  }

  /**
   * Generates the post object for the changesubtasks endpoint
   * @param {Task} task
   * @param {string} parentId
   * @param {number} order
   * @returns {ChangeSubtaskItem}
   */
  getChangeSubtaskItem(task: Task, parentId: string, order: number): ChangeSubtaskItem {
    const taskParentId = parentId || task.parentId
    const parentTask = this.taskQuery.getTask(taskParentId)

    return {
      taskId: task.id,
      order,
      parentId: taskParentId,
      parentTask: { id: taskParentId, name: parentTask.name }
    }
  }

  /**
   * Handles the parentId and/or order update for a single subtask
   * @param {Task} task
   * @param {string} parentId
   * @param {number} order
   * @returns {Observable<Task>}
   */
  changeSubtask(task: Task, parentId: string, order: number): Observable<Task> {
    const changeSubtaskItem = this.getChangeSubtaskItem(task, parentId, order)
    return this.changeSubtasks([changeSubtaskItem]).pipe(map(tasks => tasks[0]))
  }

  /**
   * Updates parentId and order for multiple subtasks
   * @param {ChangeSubtaskItem[]} changeSubtaskItems
   * @returns {Observable<Task[]>}
   */
  changeSubtasks(changeSubtaskItems: ChangeSubtaskItem[]): Observable<Task[]> {
    const tasksToUpdate = this.taskQuery.getTasks(changeSubtaskItems.map(item => item.taskId))
    const operationId = this.taskStore.history.startOperation({ type: 'update', entities: tasksToUpdate })

    // Update store optimistically
    changeSubtaskItems.forEach(({ taskId, parentId, order }) => {
      const parentTask = this.taskQuery.getTask(parentId)

      this.taskStore.updateTask(taskId, {
        parentId,
        parentTask: { id: parentId, name: parentTask.name },
        subtaskOrder: order,
        lists: parentTask.lists,
        correlationId: parentTask.correlationId
      })
    })

    return this.apiClient.post<void>(`${this.url}/changeSubtasks`, changeSubtaskItems).pipe(
      tap(() => this.taskStore.history.endOperation(operationId)),
      map(() => changeSubtaskItems.map(item => this.taskQuery.getTask(item.taskId))),
      catchError(error => {
        this.taskStore.history.undo(operationId)
        return throwError(error)
      })
    )
  }

  /**
   * Promotes subtasks to parent tasks
   * @param {Task[]} tasks
   * @returns {Observable<Task[]>}
   */
  changeSubtasksToParent(tasks: Task[]): Observable<Task[]> {
    const url = `${this.url}/changeSubtasksToParent`

    let subtasksToPromote: PromoteSubtaskItem[] = []

    const originalTasks = this.taskQuery.getTasks(tasks.map(t => t.id))
    const operationId = this.taskStore.history.startOperation({ type: 'update', entities: originalTasks })

    subtasksToPromote = tasks.map(task => {
      const list = task.project ? task.lists[0] : null

      this.taskStore.updateTask(task.id, {
        parentId: null,
        parentTask: null,
        subtaskOrder: null,
        lists: task.project ? task.lists : [],
        correlationId: task.id
      })

      return {
        listId: list?.id,
        order: list?.orderOfTask,
        taskId: task.id
      }
    })

    if (!subtasksToPromote.length) {
      return of(null)
    }

    return this.apiClient.post<void>(url, subtasksToPromote).pipe(
      tap(() => this.taskStore.history.endOperation(operationId)),
      map(() => tasks),
      catchError(error => {
        this.taskStore.history.undo(operationId)

        return throwError(error)
      })
    )
  }

  /**
   * Converts checklist items to subtasks
   * @param {Task} task
   * @param {ChecklistItems} checklistItems
   * @returns {Observable<Task[]>}
   */
  convertChecklistItemsToSubtasks(task: Task, checklistItems: ChecklistItems): Observable<Task[]> {
    this.checklistItemStore.remove(checklistItems.map(c => c.id))

    return this.apiClient.post<Task[]>(`${this.url}/${task.id}/checklistitems/tosubtasks`, {}).pipe(
      map(tasks => {
        const newTasks = tasks.map(task => Task.mapTask(task))

        this.taskStore.add(newTasks)

        return newTasks
      }),
      catchError(error => throwError(error))
    )
  }

  /**
   * Sends subtask templates changes order
   * @param {ChangeSubtaskTemplateItem[]} changes
   * @returns {Observable<void>}
   */
  changeSubtaskTemplates(changes: ChangeSubtaskTemplateItem[]): Observable<void> {
    return this.apiClient.post<void>(`${this.tasksTemplatesUrl}/changesubtasks`, changes)
  }
}
